import React, { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useParams } from 'react-router-dom';
import { api } from '../../utils/api';
import type { FileNoteFields } from '../../utils/api';
import type { PoaPeople } from '../../utils/api/ddpoa';
import { useStore } from '../../store';
import Fab from '../Fab';
import Pagination, { getPageSize } from '../Pagination';
import { RC } from '../../utils/fonts';
import { fmtDateTime, fmtNumber, currencyCode, todayISO } from '../../utils/region';
import { brandOr } from '../../utils/brand';
import { normalisePersonNamePart } from '../../utils/personName';
import { COUNTRIES } from '../../utils/countries';
import { THAI_BANKS, thaiBankOf, thaiBankLabel } from '../../utils/thaiBanks';   // the Lease section's Bank: a choice from the country's banks, the list the agreement prints from
import { accountNameInFull } from '../../utils/accountName';   // the Lease section's Account name as its box is left: never in capitals, its legal form in full
import { landAreaUnits, landAreaM2, landAreaHint, setCountryFeaturesOverride } from '../../utils/countryFeatures';   // the land's units = the country pack's, as Due Diligence's
import { propCard, PROP_CARD_BG, PROP_CARD_HOVER, PROP_LIST_WIDTH } from '../../utils/propertyCard';   // the console's green-and-white bubble + the list column's width, shared with Due Diligence / POA / Legal Matters / Last Will
import { rollDates, staffChange } from './defaultDates';   // the default Agreement and POA dates (report 90208a98)
import type { DateRule } from './defaultDates';
import { POA_TH_ADDRESS, poaThaiBoxes, poaThaiFilled, poaThaiIsAi, poaThaiNow, poaThaiTyped, poaThaiWanted } from './poaThai';   // a buyer's Thai on the POA (report b4f708fa)
import type { PoaThaiFill } from './poaThai';

// ── Client Property Form — SLF Property's sixth tab (report 1949e35a) ──────────────────────────
// One form per property (PROP-00001): the questions (POA / Last Will / agreements, and for staff alone
// Due Diligence), up to three buyers, up to three sellers, the property and — only when agreements are
// being prepared — the agreement. Staff fill it in here; the client fills it in through the EDIT link or reads it through
// the VIEW link (PropertyFormPublicPage below, served by routes/propertyFormPublic.ts) — the same
// form, the same autosave, the same document readings.
//
// A RECORD, plus ONE document: the Agreement section prints the firm's Sale & Purchase Agreement
// (report 4ba8eca0, docs/spa-blueprint.md) in the variant the sellers' Property type picks — land,
// building, land and building, or condominium — or, when different buyers take the land and the
// building, the land's and the building's own (report b16d1d09), as it does for the same buyers once
// 'Separate agreements for land and building' is ticked, each part then with its own purchase price
// and payment stages (report 0d7e130c). It starts one thing: staff moving a form to Pending or Confirmed
// with the Due Diligence question answered Yes make one report, once, filled from the form and left at
// New (report 0c21500b, server/src/services/propertyFormDd.ts). Nothing else: the form copies into no
// POA or Last Will, and emails nobody.
//
// A document given to a block (an ID card, a passport or an address document on a buyer or individual
// seller, the company documents on a company seller, the property address's document, the title deed, the
// house book or the Reservation Agreement, an agreement, a lease) is kept with the form and read by AI; what
// it says fills that block's BLANKS only — an answer somebody typed is never overwritten by a file. A second agreement is added to the
// first one's figures only when the operator says Yes to combining the totals. Removing the document takes
// those boxes back: each box that kind of document fills goes blank again, ready for the right one; boxes no
// document carries (an email, a buyer's parents) keep their answers. While the pointer is on an upload, the
// boxes its reading fills are filled light red (report fefad31c; the fill, report 46a11e5a). Every upload
// stands in the page's left margin beside the first row its reading fills (MarginDocs; reports 46a11e5a, 8347170a).
//
// Every box is a box of its kind (report 2999c435, the reporter's standing rule for forms): names are
// name boxes, prices money boxes with the firm's currency code inside, numbers number boxes, emails
// email boxes, addresses Line 1 / Line 2 / City / Post Code / Country. Every box is mandatory unless
// it says it is optional (Address Line 2, a person's Middle name — report c39980c2 — and the House book
// number, report 6ae27545): a red * on its label, a red outline while it is blank, and the form cannot be
// moved to Pending or Confirmed until each box it shows is answered.

// position: relative — an upload beside one of the card's own rows (the property address, an agreement's date, a
// lease's term) is placed from the card's edges when it stands in the page margin (MarginDocs).
const card: React.CSSProperties = { position: 'relative', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 };
const lbl: React.CSSProperties = { fontSize: 12.5, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' };
const inp: React.CSSProperties = { width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 7, fontSize: 13.5, background: 'var(--bg-app)', color: 'var(--text-primary)', boxSizing: 'border-box' };
const roInp: React.CSSProperties = { background: 'var(--c-gray-200)', color: 'var(--text-secondary)', cursor: 'default' };
const btnG: React.CSSProperties = { padding: '8px 16px', fontSize: 13.5, fontWeight: 700, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-surface)', color: 'var(--text-secondary)', cursor: 'pointer' };
const hdr: React.CSSProperties = { fontSize: 14, fontWeight: 700, color: 'var(--accent)', marginTop: 4 };
// position: relative — a block's uploads are placed from its edges when they stand in the page margin (MarginDocs).
const rowBox: React.CSSProperties = { position: 'relative', border: '1px solid var(--border)', borderRadius: 8, padding: 12, display: 'grid', gap: 10, background: 'var(--bg-app)' };
const errBox: React.CSSProperties = { fontSize: 12.5, color: '#B42318', background: '#FEF3F2', border: '1px solid #FECDCA', borderRadius: 6, padding: '6px 10px' };
const noteBox: React.CSSProperties = { fontSize: 12.5, color: 'var(--text-secondary)', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 10px' };
// A required box: Due Diligence's red * on the label, and a red outline while it is blank. The outline
// replaces the whole `border`, never just its colour — taking a lone borderColor away again would
// leave the box with no border colour at all.
const star: React.CSSProperties = { color: '#D0021B', marginLeft: 3 };
const badInp: React.CSSProperties = { border: '1px solid #D0021B' };
const badHint: React.CSSProperties = { fontSize: 12, color: '#B42318', marginTop: 3 };
// An optional box (a person's Middle name, report c39980c2): no *, no red outline, and a grey
// "(optional)" after its label.
const optMark: React.CSSProperties = { fontWeight: 400, color: 'var(--text-tertiary)', marginLeft: 4 };
// A box a document's reading filled with an answer the AI may have worked out rather than copied (the
// Post Code): an amber outline and an amber line asking for it to be checked. Amber, not red — it is answered.
const checkInp: React.CSSProperties = { border: '1px solid #D97706' };
const checkHint: React.CSSProperties = { fontSize: 12, color: '#B45309' };
// A box the upload under the pointer fills (report fefad31c): a light red fill inside its border — the reporter's
// "highlight the background instead, inside the red line rather than outside" (report 46a11e5a), where it was an
// accent ring round the box — so a red or amber outline still shows round it.
const hiInp: React.CSSProperties = { background: '#FEE2E2' };
// The currency code inside a price box's right end.
const curTag: React.CSSProperties = { position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 12.5, fontWeight: 700, color: 'var(--text-tertiary)', pointerEvents: 'none' };
const STATUSES = ['', 'new', 'pending', 'confirmed'];
const MAX_PEOPLE = 3;   // a second and a third buyer / seller (the reporter's "add a second / third … button")
const MAX_STAGES = 9;   // payment stages, the reporter's "up to 9"
// A payment stage's row — Stage | Percentage | Amount — and the totals line under the stages, column for column.
const STAGE_COLS = 'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1.5fr)';

// Every dropdown's answers. The stored value is the English answer (what the report a form starts is
// filled from, server/src/services/propertyFormDd.ts); the label shown is the operator's language.
const TITLES = ['Mr.', 'Mrs.', 'Ms.'];
const SELLER_KINDS: [string, string][] = [['Individual', 'pf_individual'], ['Company', 'pf_company']];
const PROP_TYPES: [string, string][] = [['Land', 'pf_pt_land'], ['Building', 'pf_pt_building'], ['Land and Building', 'pf_pt_land_building'], ['Condominium', 'pf_pt_condo'], ['Apartment', 'pf_pt_apartment']];
const LAND_TYPES = ['Land', 'Land and Building'];
const BUILDING_TYPES = ['Building', 'Land and Building', 'Condominium', 'Apartment'];
const DEED_TYPES = ['Chanote', 'Nor Sor 3 Gor', 'Phor Tor Bor 5'];
// The building's two warranties, the Sale & Purchase Agreement's clauses 8.3 and 8.4 (report f87182e0): None, or
// 1 to this many years.
const WARRANTY_YEARS = 10;
// Who signs the agreement for the seller, and how many particular conditions it carries (up to five,
// and an agreement may have none) — the agreement's own answers, report 4ba8eca0.
const SIGN_AS: [string, string][] = [['Owner', 'pf_signs_owner'], ['Authorized Representative', 'pf_signs_rep']];
const MAX_CONDS = 5;
// The longest wording one condition's 'Write a better clause' takes (report c96a33b3) — the server's MAX_CLAUSE.
const MAX_CLAUSE = 3000;
const COND_COUNTS: [string, string][] = [['0', 'pf_cond_none'], ['1', 'pf_cond_one'], ['2', 'pf_cond_two'],
  ['3', 'pf_cond_three'], ['4', 'pf_cond_four'], ['5', 'pf_cond_five']];
const STAGES: [string, string][] = [
  ['Reservation Fee', 'pf_stage_reservation'], ['Deposit', 'pf_stage_deposit'], ['Upon Signing Contracts', 'pf_stage_signing'],
  ['Mobilization On-Site', 'pf_stage_mobilization'], ['Commencement On-Site', 'pf_stage_commencement'], ['Structure Complete', 'pf_stage_structure'],
  ['Watertight Building', 'pf_stage_watertight'], ['Lock-Up', 'pf_stage_lockup'], ['Land Transfer', 'pf_stage_land_transfer'],
  ['Building Transfer', 'pf_stage_building_transfer'], ['Land and Building Transfer', 'pf_stage_lb_transfer'],
  ['Land Lease Registration', 'pf_stage_land_lease'], ['Building Lease Registration', 'pf_stage_building_lease'],
  ['Land and Building Lease Registration', 'pf_stage_lb_lease'], ['DBD Transfer', 'pf_stage_dbd'],
  ['Shareholder Change', 'pf_stage_shareholder_change'], ['Director Change', 'pf_stage_director_change'], ['Handover', 'pf_stage_handover'],
];
const FEES_BY: [string, string][] = [['Buyer and seller share', 'pf_fees_share'], ['Seller pays all', 'pf_fees_seller'], ['Buyer pays all', 'pf_fees_buyer']];
// When each part transfers (report 8aa83b74) — the Sale & Purchase Agreement's clauses 5 and 6: at 50% or 100% of the
// purchase price paid, or Other, once one of the block's own payment stages is paid, picked in the Progress payment
// box (report bb4ea111: "let the user choose the progress payment for the land table (if different or separate from
// the building table)") — the agreement prints the share of the price paid through that stage. Kept on the card as
// landTransfer / buildingTransfer and the picked landTransferStage / buildingTransferStage (the stage's position, 1 =
// the first); a Percentage paid typed before the pick (landTransferPct / buildingTransferPct) still prints until a
// stage is picked. Blank reads as the template's own 50 (land) / 100 (building), which the box shows. Not in
// emptyAgreement on purpose: removing the card's agreement upload keeps them (they are no reading's), the section's
// Clear drops them with the rest.
const TRANSFER_AT: [string, string][] = [['50', 'pf_transfer_50'], ['100', 'pf_transfer_100'], ['Other', 'pf_transfer_other']];
const TRANSFER_DEFAULT: Record<'land' | 'building', string> = { land: '50', building: '100' };
// What a buyer or lessee is acquiring (report 21148c83): a tick box for each, kept on the block as `roles`
// in this order. One person can hold more than one (the reporter: a foreigner can lease the land and buy the
// building), but never Buyer and Lessee of the same thing — ROLE_OTHER is the tick a tick takes away.
const ROLES: [string, string][] = [
  ['Buyer of the land', 'pf_role_buy_land'], ['Buyer of the building', 'pf_role_buy_building'],
  ['Lessee of the land', 'pf_role_lease_land'], ['Lessee of the building', 'pf_role_lease_building'],
];
const ROLE_NAMES = ROLES.map(([v]) => v);
// The Lease section (report eab2c428) shows while a Lease stage is chosen or a buyer is ticked Lessee —
// the same test the server's leaseVariant makes (services/leaseDocument.ts).
const LEASE_STAGE_NAMES = ['Land Lease Registration', 'Building Lease Registration', 'Land and Building Lease Registration'];
const LEASE_EXTENT: [string, string][] = [['Whole', 'pf_lease_whole'], ['Part', 'pf_lease_part']];
const LEASE_PERIODS: [string, string][] = [['Monthly', 'pf_lease_monthly'], ['Quarterly', 'pf_lease_quarterly'],
  ['Yearly', 'pf_lease_yearly'], ['Every 3 years', 'pf_lease_every3'], ['Every 5 years', 'pf_lease_every5'],
  ['Whole term in advance', 'pf_lease_whole_term']];
// Fixed choices where the Lease section once had free text (report eab2c428's markup, docs/lease-blueprint.md §6 (e)): the
// agreement prints each with its own English and Thai (services/leaseDocument.ts USES / FEES).
const LEASE_FEES: [string, string][] = [['Lessee pays', 'pf_lease_fees_lessee'], ['Lessor pays', 'pf_lease_fees_lessor'], ['Both pay', 'pf_lease_fees_both']];
const LEASE_USES: [string, string][] = [['Living', 'pf_lease_use_living'], ['Living and renting', 'pf_lease_use_renting'],
  ['Living and working', 'pf_lease_use_working'], ['Living and subleasing', 'pf_lease_use_living_sublease'],
  ['Living, working and renting', 'pf_lease_use_working_renting'], ['Subleasing', 'pf_lease_use_sublease']];
// Assignment, subletting and succession (report 6eebd989, blueprint §6 (g)): clause 11 prints each Yes in its own English
// and Thai (services/leaseDocument.ts ASSIGNMENT), and No the template's clause. Not one of LEASE_KEYS: a right this
// important is chosen by a person, never filled from an uploaded document.
const LEASE_ASSIGN: [string, string][] = [['No', 'pf_lease_assign_no'], ['Registered', 'pf_lease_assign_registered'], ['By agreement', 'pf_lease_assign_agreement']];
const leaseApplies = (d: any): boolean => {
  const a = d?.agreement || {};
  const n = Math.max(0, Math.min(MAX_STAGES, parseInt(a.stageCount, 10) || 0));
  const stages: any[] = Array.isArray(a.stages) ? a.stages.slice(0, n) : [];
  return stages.some((s: any) => LEASE_STAGE_NAMES.indexOf(String(s?.stage || '')) >= 0)
    || (Array.isArray(d?.buyers) ? d.buyers : []).some((b: any) => (Array.isArray(b?.roles) ? b.roles : []).some((r: any) => String(r).indexOf('Lessee') === 0));
};
/** The form shows the Agreement date: the Agreement card's own box, or the Lease card's copy of it (report 90208a98, defaultDates.ts). */
const agreementShows = (d: any): boolean => d?.doAgreements === 'Yes' || leaseApplies(d);
const ROLE_OTHER: Record<string, string> = {
  'Buyer of the land': 'Lessee of the land', 'Lessee of the land': 'Buyer of the land',
  'Buyer of the building': 'Lessee of the building', 'Lessee of the building': 'Buyer of the building',
};

// ── What a box keeps (report 2999c435) ──────────────────────────────────────────────────────────
// A Thai ID card number is 13 digits, written 1-4-5-2-1 ("1-2345-67890-12-3"). Once the ID / passport
// box holds exactly 13 digits — typed in one go, or the AI's spaced copy of the card — it gets those
// dashes; anything else (a passport number, a number half typed) stays as it is.
const fmtIdNo = (v: any): string => {
  const s = String(v ?? '');
  const d = s.replace(/[\s-]/g, '');
  return /^\d{13}$/.test(d) ? `${d[0]}-${d.slice(1, 5)}-${d.slice(5, 10)}-${d.slice(10, 12)}-${d[12]}` : s;
};
// The number as the hidden-note lookup compares it (report 73f2d29a): spaces and dashes off, capitals.
const idNoKey = (v: any): string => String(v ?? '').replace(/[\s-]/g, '').toUpperCase();
// A number box keeps digits only, and one decimal point unless it counts whole things (bedrooms).
const numText = (v: string, whole?: boolean): string => {
  const s = String(v ?? '').replace(whole ? /[^0-9]/g : /[^0-9.]/g, '');
  const i = s.indexOf('.');
  return i < 0 ? s : `${s.slice(0, i + 1)}${s.slice(i + 1).replace(/\./g, '')}`;
};
// A money box keeps the plain number ("12500000.00"): digits, one point, at most two decimals. The
// thousands separators are only how it is shown, so nothing that reads it later has commas to strip.
// These three are exported for the File Library's Hidden note Salary box (report 14486e36), built like MoneyBox.
export const moneyText = (v: string): string => {
  const s = numText(v);
  const i = s.indexOf('.');
  return i < 0 ? s : s.slice(0, i + 3);
};
// Two decimals once the box is left: "12500000" → "12500000.00". Padding only — never rounded.
export const twoDp = (s: string): string => {
  const m = /^(\d*)(?:\.(\d{0,2}))?$/.exec(s);
  return m && /\d/.test(s) ? `${m[1].replace(/^0+(?=\d)/, '') || '0'}.${(m[2] || '').padEnd(2, '0')}` : s;
};
// How a money box shows its number: the whole part grouped in thousands (the region's way, as Due
// Diligence's amount boxes do), the decimals exactly as typed so a half-typed "1500." keeps its point.
export const grouped = (s: string): string => {
  const m = /^(\d*)(\.\d*)?$/.exec(String(s ?? ''));
  return m ? `${m[1] ? fmtNumber(m[1]) : ''}${m[2] ?? ''}` : String(s ?? '');
};
// The number in what a document's reading gave ("120.5 m²" → "120.5", "3 bedrooms" → "3"); '' = none.
const leadNum = (v: string, whole?: boolean): string => {
  const m = (whole ? /\d+/ : /\d+(?:\.\d+)?/).exec(String(v ?? '').replace(/,/g, ''));
  return m ? m[0] : '';
};
// An answer saved before these boxes were typed: commas and spaces go when what is left is a plain
// number (money also gets its two decimals); anything else stays as typed, for a person to correct.
const numLoad = (v: any): string => { const s = String(v ?? '').replace(/[,\s]/g, ''); return /^\d*\.?\d*$/.test(s) ? s : String(v ?? ''); };
const moneyLoad = (v: any): string => { const s = numLoad(v); return /^\d*\.?\d*$/.test(s) ? twoDp(s) : s; };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isEmail = (v: any): boolean => EMAIL_RE.test(String(v ?? '').trim());
const blank = (v: any): boolean => !String(v ?? '').trim();
// A date box's answer is a real day, yyyy-mm-dd as the date picker gives it (the Lease's dates, report eab2c428).
const isDay = (v: any): boolean => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v ?? '').trim());
  if (!m) return false;
  const day = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return day.getUTCFullYear() === Number(m[1]) && day.getUTCMonth() === Number(m[2]) - 1 && day.getUTCDate() === Number(m[3]);
};
// The lease's Expiry date: the day before the term's anniversary of the Registration date — the rule the
// agreement prints (services/leaseDocument.ts expiryOf; keep the two in step). '' until both are answered.
const leaseExpiry = (iso: any, term: any): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso ?? '').trim());
  const n = parseInt(String(term ?? '').trim(), 10);
  const years = n >= 1 && n <= 30 ? n : 0;
  if (!m || !years) return '';
  return new Date(Date.UTC(Number(m[1]) + years, Number(m[2]) - 1, Number(m[3]) - 1)).toISOString().slice(0, 10);
};

// ── Payment stages: Stage · Percentage · Amount (report e897c965) ─────────────────────────────────
// A money or % box's number as whole hundredths (cents / hundredths of a per cent), so a sum or a
// comparison is exact; null = blank or not a number.
const hundredths = (v: any): number | null => {
  const s = String(v ?? '').replace(/[,\s]/g, '');
  return /^(\d+\.?\d*|\.\d+)$/.test(s) ? Math.round(Number(s) * 100) : null;
};
const fromHundredths = (n: number): string => `${Math.floor(n / 100)}.${String(n % 100).padStart(2, '0')}`;
/** A typed % → its amount: price × % / 100, to the cent. '' while there is no price to take it of. */
const stageAmount = (price: any, pct: any): string => {
  const p = hundredths(price); const q = hundredths(pct);
  return p && q != null ? fromHundredths(Math.round(p * q / 10000)) : '';
};
/** A typed amount → its %: amount / price × 100, to two decimals. '' while there is no price. */
const stagePct = (price: any, amount: any): string => {
  const p = hundredths(price); const a = hundredths(amount);
  return p && a != null ? fromHundredths(Math.round(a * 10000 / p)) : '';
};
/** What the stages' amounts add up to, in cents. */
const stageSum = (stages: any[]): number => stages.reduce((n: number, s: any) => n + (hundredths(s?.amount) || 0), 0);
// A stage saved before the % box holds a stage and an amount only: its % is worked out from the amount
// and the price, as typing that amount would have done. Opening a form still saves nothing.
const stageLoad = (s: any, price: string): any => {
  const amount = moneyLoad(s.amount);
  const pct = moneyLoad(s.pct);
  return { ...s, pct: blank(pct) ? stagePct(price, amount) : pct, amount };
};

// ── The lease's Rent per period, worked out from its Total rent (report 805f7320) ─────────────────
// The reporter's answers on the report: the Total rent is the box typed in and covers the Term only (a Renewal
// is left out); a step-up adds its % of the FIRST band's rent once for each band (simple, never compounded); and
// a Step-up period must divide the Term into whole bands. Mirror of leaseStepChoices (services/propertyForm.ts)
// and leaseRents (services/leaseDocument.ts, which the agreement's clause 4 prints from) — keep the three in step.
/** The Step-up periods a Term allows: each whole number of years shorter than the Term that divides it into
 *  whole bands (a 7-year term every year only, a 10-year term every 1, 2 or 5 years). With a rent paid every 3
 *  or 5 years, only multiples of that period; with the whole term paid in advance, none. [] until the Term is a
 *  whole 1 to 30 years. */
const leaseStepChoices = (term: any, period: any): string[] => {
  const n = parseInt(String(term ?? '').trim(), 10);
  const years = n >= 1 && n <= 30 ? n : 0;
  const p = String(period ?? '').trim();
  const each = p === 'Every 3 years' ? 3 : p === 'Every 5 years' ? 5 : p === 'Whole term in advance' ? years : 1;
  const out: string[] = [];
  for (let s = 1; s < years; s++) if (years % s === 0 && s % each === 0) out.push(String(s));
  return out;
};
// One rent period's rent as a share of a year's, [times, divided by]: a twelfth Monthly, a quarter Quarterly,
// three or five years' rent every 3 or 5 years. Whole term in advance is the Total rent itself.
const RENT_OF_YEAR: Record<string, [number, number]> = {
  'Monthly': [1, 12], 'Quarterly': [1, 4], 'Yearly': [1, 1], 'Every 3 years': [3, 1], 'Every 5 years': [5, 1],
};
/** The Rent per period of each step-up band, `from`–`to` its years: B = Term ÷ S bands of S years (S = the
 *  Step-up period, or the Term itself with no step-up) and p = Step-up % ÷ 100. The first band's yearly rent is
 *  Total rent ÷ (S × (B + p·B(B−1)/2)) and band k's (k = 0, 1 …) that × (1 + k·p), so the bands come to the
 *  Total rent; a period's rent is its share of that year's (RENT_OF_YEAR). To the cent. [] while the Term is not
 *  a whole 1 to 30 years or the Step-up period is not one it allows (leaseStepChoices); each band's rent is ''
 *  while the Total rent or the Rent period is blank, or a Step-up period has no Step-up % above 0. */
const leaseRents = (lease: any): { from: number; to: number; rent: string }[] => {
  const n = parseInt(String(lease?.term ?? '').trim(), 10);
  const years = n >= 1 && n <= 30 ? n : 0;
  const period = String(lease?.period ?? '').trim();
  const step = String(lease?.stepEvery ?? '').trim();
  const stepped = !blank(step) && step !== 'None';
  if (!years || (stepped && leaseStepChoices(years, period).indexOf(step) < 0)) return [];
  const S = stepped ? Number(step) : years;
  const B = years / S;
  const total = hundredths(lease?.totalRent) || 0;          // in cents
  const q = stepped ? hundredths(lease?.stepPct) || 0 : 0;  // the step-up in hundredths of a per cent: 10% = 1000
  const share = RENT_OF_YEAR[period];
  const known = !!total && (!!share || period === 'Whole term in advance') && (!stepped || q > 0);
  // Band k in cents, the formula above with p = q / 10000, divided once at the end so that only the cent is rounded.
  const rentOf = (k: number): string => (!known ? '' : !share ? fromHundredths(total)
    : fromHundredths(Math.round(total * (10000 + k * q) * share[0] / (S * (B * 10000 + q * B * (B - 1) / 2) * share[1]))));
  return Array.from({ length: B }, (_, k) => ({ from: k * S + 1, to: (k + 1) * S, rent: rentOf(k) }));
};

// ── The lease's Total area against its Portion area (report 90a529be) ─────────────────────────────
// The Total area is the whole of the land or property, so beside Part it is larger than the Portion area leased.
// One that is not — the portion's own m² copied into it, or the property's m² when that is only the portion —
// counts as unknown: a reading never fills it, the box never shows the property's m² as it, the agreement prints
// the dotted gap and it is asked for. Mirror of leaseTotalTooSmall (services/propertyForm.ts) — keep them in step.
/** A box's m² as a number; null = blank or not a number. */
const sqmOf = (v: any): number | null => {
  const s = String(v ?? '').replace(/[,\s]/g, '');
  return /^(\d+\.?\d*|\.\d+)$/.test(s) ? Number(s) : null;
};
/** `total` is not larger than `portion` (false while either is blank or not a number). */
const notLarger = (total: any, portion: any): boolean => {
  const a = sqmOf(total);
  const b = sqmOf(portion);
  return a != null && b != null && a <= b;
};
/** Whole or part is Part and `total` is not larger than the lease's Portion area. */
const leaseTotalTooSmall = (lease: any, total: any): boolean => String(lease?.extent ?? '') === 'Part' && notLarger(total, lease?.portionArea);

// An address in parts, the shape of Due Diligence's address box plus a second line. A form saved
// before the parts holds the address on one line: that line becomes Address Line 1, so nothing is lost.
const emptyAddress = () => ({ line1: '', line2: '', city: '', postal: '', country: '' });
const normAddress = (v: any): any => (v && typeof v === 'object' && !Array.isArray(v)
  ? { ...emptyAddress(), ...v }
  : { ...emptyAddress(), line1: String(v ?? '').trim() });
// The parts that must be answered — all but Line 2 — with their labels, for the list of missing boxes.
const ADDRESS_PARTS: [string, string][] = [['line1', 'pf_addr_line1'], ['city', 'pf_addr_city'], ['postal', 'pf_addr_postal'], ['country', 'pf_addr_country']];

// Each buyer / seller block carries a `uid`: the key its documents are filed under ('person:<uid>',
// 'passport:<uid>', 'personAddr:<uid>' — PERSON_DOCS), so removing buyer 2 never hands buyer 3's passport
// to somebody else. A buyer's parents are asked for
// only while a POA is being done; they stay on the buyer if the answer goes back to No. A buyer's `birthDate` is
// never shown: the ID card's or the passport's reading keeps it, for the POA's Age (report 06eee624, poaAges).
const uid = (): string => Math.random().toString(36).slice(2, 12);
const emptyBuyer = () => ({ uid: uid(), roles: [] as string[], title: '', first: '', middle: '', last: '', email: '', idNo: '', nationality: '', address: emptyAddress(), fatherFirst: '', fatherLast: '', motherFirst: '', motherLast: '', birthDate: '' });
// A buyer's ticks in the form's order, only the four it offers; a form saved before them has none ticked.
const rolesOf = (v: any): string[] => (Array.isArray(v) ? ROLE_NAMES.filter(r => v.indexOf(r) >= 0) : []);
const emptySeller = () => ({ uid: uid(), kind: 'Individual', propType: '', title: '', first: '', middle: '', last: '', email: '', idNo: '', nationality: '', address: emptyAddress(), companyName: '', taxId: '' });
// The Land block (report f7b09e0d): the deed's Land number, a different number from the deed's own, and the land's
// size in the country's own units (its pack, utils/countryFeatures.ts), one box each, the m² worked out from them.
const landUnitKeys = (): string[] => landAreaUnits().map(u => u.key);
// The property's own address and — when a seller is selling one condominium unit — the unit's own title:
// its Or.Chor.2 number, which building and floor it is on, and its share of the common property (report
// 4ba8eca0; the agreement reads them, docs/spa-blueprint.md §3). The building's Levels are asked no more
// (report 6ae27545); a form saved with them keeps the answer, which nothing reads. The building's Structural and
// Architectural warranty (report f87182e0) left blank — a form saved before them too — are the template's own 5 and 1 years.
const emptyProperty = () => ({ name: '', address: emptyAddress(), deedType: '', deedNo: '', landNo: '', ...Object.fromEntries(landUnitKeys().map(k => [k, ''])), landSqm: '', unitNo: '', houseBookNo: '', buildingSqm: '', bedrooms: '', bathrooms: '', structuralWarranty: '', architecturalWarranty: '', orChor2No: '', condoBuilding: '', condoFloor: '', condoShare: '' });
// One payment block: the purchase price, the number of stages and the stages, then who pays the transfer fees and the
// Special conditions. The agreement holds one — or, when the sale is split into the land's and the building's own
// agreement (saleSplits), the Land block's and the Building block's, `parts.land` / `parts.building` (report 0d7e130c;
// their own fees and conditions too since report 7dc394ad, as each part's agreement prints them). `separate` is the
// card's 'Separate agreements for land and building' tick. `dateDefault` is the date's marker (report 90208a98,
// defaultDates.ts): undefined, so a form loads with its own, and blanking the card takes it with the date, which a New form
// then fills again.
const emptyFigures = () => ({ price: '', stageCount: '', stages: [] as any[], feesPaidBy: '', condCount: '', conds: [] as string[] });
const emptyAgreement = () => ({ date: '', dateDefault: undefined, signAs: '', ownerName: '', price: '', stageCount: '', stages: [] as any[], feesPaidBy: '', condCount: '', conds: [] as string[],
  separate: false, parts: { land: emptyFigures(), building: emptyFigures() } });
// The POA section (report 2ce6d3b5): only what the POA needs and the form does not ask elsewhere — for each buyer block
// (`grantors[uid]`) how it grants the POA, its age, and its own and its parents' names and its address in Thai (poaThai.ts);
// each seller's Thai name (`sellersTh[uid]`); the attorney; the two witnesses; where it is made and its date; the
// property's place names in Thai; and a condominium's registration number. Every box is optional: a blank Thai box prints the English, a blank attorney,
// witness or place prints the firm's New-POA defaults (server/src/services/propertyPoa.ts), and a blank date the dotted gap;
// `dateDefault` is that date's marker (report 90208a98, defaultDates.ts). The attorney's `birthDate` and
// `birthFile` are never shown: what their ID card or passport gave, and which upload gave it (report beb72eec, doneeRead).
const emptyPoa = () => ({ grantors: {} as Record<string, any>, sellersTh: {} as Record<string, string>,
  donee: { name: '', nameTh: '', age: '', idNo: '', addr: '', addrTh: '', birthDate: '', birthFile: '' },
  witness1: '', witness1Th: '', witness2: '', witness2Th: '', madeAt: '', madeAtTh: '', date: '',
  placeTh: { subdistrict: '', district: '', province: '' }, condoRegNo: '' });
/** How a buyer block grants its POA while the POA section has no answer: Lessee when it is ticked Lessee and not Buyer,
 *  else Buyer (grantsAs in server/src/services/propertyPoa.ts — keep the two in step). */
const poaAsOf = (b: any): string => {
  const r = rolesOf(b?.roles);
  return r.some(x => x.indexOf('Lessee') === 0) && !r.some(x => x.indexOf('Buyer') === 0) ? 'Lessee' : 'Buyer';
};
/** 'Grants the POA as' — the answers POA_AS in server/src/services/propertyPoa.ts takes. */
const POA_AS: [string, string][] = [['Buyer', 'pf_poa_as_buyer'], ['Lessee', 'pf_poa_as_lessee'], ['Sublessee', 'pf_poa_as_sublessee']];
// ── The POA card's Attorney and Witness dropdowns (report dc39d5a0) ──────────────────────────────
// The staff screen hands FormBody the firm's lists (`poaPeople`). A client's link never gets them, as they hold ID
// numbers and home addresses, and keeps the plain boxes; so does a firm with no list. A pick fills the boxes the POA
// prints from (emptyPoa). While they are blank the dropdown shows the firm's default person, the one who prints
// (server/src/services/propertyPoa.ts). 'Other' shows the typed boxes, as does any answer that is no listed person's.
const POA_OTHER = 'other';
// The kept date of birth counts with the boxes: a block holding only that prints the age it gives (report beb72eec).
const DONEE_BOXES = ['name', 'nameTh', 'age', 'idNo', 'addr', 'addrTh', 'birthDate'];
const poaKey = (v: any): string => String(v ?? '').replace(/[\s-]/g, '').toLowerCase();
/** The same person: the same English or the same Thai name, spacing, dashes and capitals aside. Mirror of sameName in
 *  server/src/services/ddPoaContent.ts — keep the two in step. */
const samePerson = (a: any, b: any): boolean => (!blank(a?.en) && poaKey(a.en) === poaKey(b?.en)) || (!blank(a?.th) && poaKey(a.th) === poaKey(b?.th));
const poaLabel = (p: any): string => p.name.en || p.name.th;
/** What a dropdown offers: the firm's list, plus any default person it leaves out, so the dropdown can always show who prints. */
const poaOffer = (list: any[], defaults: any[]): any[] => (list.length
  ? list.concat(defaults.filter(p => !blank(p?.name?.en || p?.name?.th) && !list.some(x => samePerson(x.name, p.name))))
  : []);
/** The attorney boxes hold exactly this person. An age worked out from a birth date moves with the day, so it is no test. */
const holdsAttorney = (d: any, a: any): boolean => poaKey(d.name) === poaKey(a.name.en) && poaKey(d.nameTh) === poaKey(a.name.th)
  && poaKey(d.idNo) === poaKey(a.idNo) && poaKey(d.addr) === poaKey(a.addr.en) && poaKey(d.addrTh) === poaKey(a.addr.th)
  && (!blank(a.birthDate) || !blank(d.birthDate) || poaKey(d.age) === poaKey(a.age));
/** A dropdown's value: 'Other' once chosen; while the boxes are blank, the default person; else whoever they hold, or 'Other'. */
const poaPick = (list: any[], other: boolean, isBlank: boolean, def: any, holds: (p: any) => boolean): string => {
  if (other) return POA_OTHER;
  const i = list.findIndex(p => (isBlank ? samePerson(p.name, def) : holds(p)));
  return i >= 0 ? String(i) : isBlank ? '' : POA_OTHER;
};
// How long the buyer's boxes must rest before the POA card's empty Thai boxes are asked for (report b4f708fa, FormBody).
const POA_THAI_PAUSE = 2500;
// The Made at dropdowns' first choice (report 404df7d2): the firm's wording for that language, poa_defaults' `madeAt`.
const POA_MADE_FIRM = 'firm';
/** A Made at dropdown's value: 'Other' once chosen; a blank box, or one holding the firm's wording, the first choice (''
 *  while the firm has none); else 'Other'. Capitals and dashes count here, unlike in a name: what shows is what prints. */
const madePick = (other: boolean, box: any, firm: any): string => {
  const tidy = (v: any): string => String(v ?? '').replace(/\s+/g, ' ').trim();
  if (other) return POA_OTHER;
  if (blank(box) || tidy(box) === tidy(firm)) return blank(firm) ? '' : POA_MADE_FIRM;
  return POA_OTHER;
};
// ── A buyer's age for the POA (report 06eee624) ──────────────────────────────────────────────────
// The POA section's Age, worked out from the date of birth the buyer's ID card's or passport's reading gives, which the
// block keeps as `birthDate`. A blank Age is filled with it as that reading comes in, or once the POA question is
// answered Yes; one left blank shows it greyed, and the POA prints it (server/src/services/propertyPoa.ts). Mirrors of
// ageOn and ageDay in server/src/services/propertyForm.ts — keep the two in step.
/** A person's age in whole years on a day, from their date of birth: one less until that year's birthday (one born on
 *  29 February has it on 1 March in a year without one). '' unless both are real days, the birth is not after the day
 *  and the age is under 130 — older is a misread year. */
const ageOn = (birth: any, on: any): string => {
  if (!isDay(birth) || !isDay(on)) return '';
  const b = String(birth).trim();
  const o = String(on).trim();
  const n = Number(o.slice(0, 4)) - Number(b.slice(0, 4)) - (o.slice(5) < b.slice(5) ? 1 : 0);
  return n >= 0 && n < 130 ? String(n) : '';
};
/** The day a buyer's age is measured on: the Agreement date while it is a real day, else today in the tenant's zone. That
 *  date's default (report 90208a98) counts like a typed one, and moves on before a change works an Age out (staffChange). */
const ageDay = (d: any): string => (isDay(d?.agreement?.date) ? String(d.agreement.date).trim() : todayISO());
/** The POA section's blank Ages filled, each from its buyer's date of birth — only while a POA is being done, and only
 *  the buyer `only` names when it is given. The form comes back as it was when none is filled; `n` = how many were. */
const poaAges = (d: any, only?: string): { next: any; n: number } => {
  if (d?.doPoa !== 'Yes') return { next: d, n: 0 };
  const on = ageDay(d);
  const gs = { ...((d.poa && d.poa.grantors) || {}) };
  const buyers: any[] = Array.isArray(d.buyers) ? d.buyers : [];
  let n = 0;
  for (const b of buyers) {
    if (!b || !b.uid || (only && b.uid !== only) || !blank((gs[b.uid] || {}).age)) continue;
    const age = ageOn(b.birthDate, on);
    if (age) { gs[b.uid] = { ...(gs[b.uid] || {}), age }; n++; }
  }
  return n ? { next: { ...d, poa: { ...(d.poa || {}), grantors: gs } }, n } : { next: d, n: 0 };
};
// ── The attorney's age for the POA (report beb72eec) ─────────────────────────────────────────────
// Their ID card or passport, the Attorney block's own upload (ATTORNEY_DOC, staff only), gives the date of birth. It is kept on
// the block as `birthDate` and, for a listed attorney, on their entry in the firm's list while that is blank, so the
// card is needed only once for each of them. Mirror of ATTORNEY_DOC in server/src/services/propertyForm.ts — keep the two in step.
const ATTORNEY_DOC = 'attorneyDoc';
/** The boxes a pick from the Attorney dropdown fills: the person's own, the age worked out from their date of birth on the
 *  Agreement date, or their stored age until one exists. */
const attorneyBoxes = (a: any, d: any): any => ({ name: a.name.en, nameTh: a.name.th, age: ageOn(a.birthDate, ageDay(d)) || a.age, idNo: fmtIdNo(a.idNo), addr: a.addr.en, addrTh: a.addr.th, birthDate: a.birthDate || '', birthFile: '' });
/** The Attorney block with the date of birth upload `file` gave, and the Age worked out from it unless somebody typed that
 *  Age: one a pick or an earlier card filled was not typed. `a` = the listed attorney the dropdown shows, whose boxes are
 *  filled as a pick fills them. */
const doneeRead = (d: any, birth: string, file: string, a?: any): { next: any; typed: boolean } => {
  const was = (d.poa && d.poa.donee) || {};
  const on = ageDay(d);
  const typed = !blank(was.age) && poaKey(was.age) !== poaKey(ageOn(was.birthDate, on)) && !(a && poaKey(was.age) === poaKey(attorneyBoxes(a, d).age));
  const donee = { ...was, ...(a ? attorneyBoxes(a, d) : {}), birthDate: birth, birthFile: file, age: typed ? was.age : ageOn(birth, on) };
  return { next: { ...d, poa: { ...(d.poa || {}), donee } }, typed };
};
/** A payment block as it was saved → as the form shows it: the price as money, each stage saved before the % box with its
 *  % worked out, and the particular conditions, MAX_CONDS at most (a block saved before them has none). */
const figuresLoad = (f: any): any => {
  const price = moneyLoad(f?.price);
  return { ...f, price, stages: Array.isArray(f?.stages) ? f.stages.map((s: any) => (s && typeof s === 'object' ? stageLoad(s, price) : s)) : [],
    conds: (Array.isArray(f?.conds) ? f.conds : []).slice(0, MAX_CONDS).map((c: any) => String(c ?? '')) };
};
const people = (v: any, make: () => any): any[] => {
  const l = Array.isArray(v) ? v.filter((x: any) => x && typeof x === 'object').slice(0, MAX_PEOPLE) : [];
  return (l.length ? l : [{}]).map((x: any) => {
    const p = { ...make(), ...x };
    return { ...p, idNo: fmtIdNo(p.idNo), address: normAddress(p.address) };
  });
};
const normForm = (d: any): any => {
  const prop = { ...emptyProperty(), ...(d?.property || {}) };
  const agr = { ...emptyAgreement(), ...(d?.agreement || {}) };
  const parts: any = agr.parts && typeof agr.parts === 'object' ? agr.parts : {};
  // A split sale's part saved before it had its own fees and conditions (report 7dc394ad) has the card's, which both
  // agreements printed until then — as the server's agreement and its list of boxes to fill read such a part.
  const was = saleSplits(d) ? { feesPaidBy: agr.feesPaidBy, condCount: agr.condCount, conds: agr.conds } : {};
  const part = (p: any): any => figuresLoad({ ...emptyFigures(), ...was, ...(p || {}) });
  return {
    doPoa: '', doWill: '', doAgreements: '', doDd: '', ...(d || {}),
    poa: { ...emptyPoa(), ...(d?.poa && typeof d.poa === 'object' ? d.poa : {}) },
    buyers: people(d?.buyers, emptyBuyer).map((b: any) => ({ ...b, roles: rolesOf(b.roles) })),
    sellers: people(d?.sellers, emptySeller),
    property: { ...prop, address: normAddress(prop.address), ...Object.fromEntries(landUnitKeys().map(k => [k, numLoad(prop[k])])), landSqm: numLoad(prop.landSqm), buildingSqm: numLoad(prop.buildingSqm), bedrooms: numLoad(prop.bedrooms), bathrooms: numLoad(prop.bathrooms) },
    agreement: {
      ...figuresLoad(agr),
      // The Land and the Building block's own figures, fees and conditions, loaded as the one block's are; a form saved
      // before them has them blank, bar a split sale's fees and conditions (`was`).
      parts: { land: part(parts.land), building: part(parts.building) },
    },
  };
};

// ── One sale, or the land's and the building's own (report b16d1d09, report 0d7e130c) ────────────
// The test agreementsOf makes (server/src/services/spaDocument.ts; services/propertyForm.ts mirrors it too —
// keep the three in step). Different buyers of the land and of the building split the sale into two
// agreements; the same buyers do once the Agreement card's 'Separate agreements for land and building' is
// ticked, the tick offered only then. A condominium unit is one title and is never split. A split sale has a
// payment block for each part (PARTS), under the part's own heading, with its own agreement upload.
type PartKey = '' | 'land' | 'building';
const PARTS: ['land' | 'building', string][] = [['land', 'pf_land'], ['building', 'pf_building']];
/** The buyer blocks ticking Buyer of the land, those ticking Buyer of the building, and whether a seller sells a condominium unit. */
const saleParts = (d: any): { land: any[]; building: any[]; condo: boolean } => {
  const buying = (role: string): any[] => (Array.isArray(d?.buyers) ? d.buyers : []).filter((b: any) => b && typeof b === 'object' && rolesOf(b.roles).indexOf(role) >= 0);
  return { land: buying('Buyer of the land'), building: buying('Buyer of the building'),
    condo: (Array.isArray(d?.sellers) ? d.sellers : []).some((s: any) => String(s?.propType || '') === 'Condominium') };
};
/** The card offers the tick: the same buyers take the land and the building, and no seller sells a condominium unit. */
const separateOffered = (d: any): boolean => {
  const p = saleParts(d);
  return !p.condo && p.land.length > 0 && p.land.length === p.building.length && p.land.every(b => p.building.indexOf(b) >= 0);
};
const saleSplits = (d: any): boolean => {
  const p = saleParts(d);
  return !p.condo && p.land.length > 0 && p.building.length > 0 && (!separateOffered(d) || d?.agreement?.separate === true);
};
/** The parts whose transfer timing the one payment block asks for (TRANSFER_AT) — those the one sale transfers, the
 *  part of agreementsOf's sale (server/src/services/spaDocument.ts; services/propertyForm.ts mirrors this — keep the
 *  three in step): none for a condominium unit; the one part bought while the other is leased; else those the sellers'
 *  Property types cover, both while none has answered. A split sale asks each part's under its own block instead. */
const transferParts = (d: any): ('land' | 'building')[] => {
  const p = saleParts(d);
  if (p.condo) return [];
  if ((p.land.length > 0) !== (p.building.length > 0)) return [p.land.length ? 'land' : 'building'];
  const types: string[] = (Array.isArray(d?.sellers) ? d.sellers : []).map((s: any) => String(s?.propType || ''));
  const hasLand = types.some(x => LAND_TYPES.indexOf(x) >= 0);
  const hasBuilding = types.some(x => BUILDING_TYPES.indexOf(x) >= 0);
  return hasLand === hasBuilding ? ['land', 'building'] : [hasLand ? 'land' : 'building'];
};
/** The Agreement card's heading names what the agreement sells (report d35074a8) — the test the server's spaVariant
 *  (server/src/services/spaDocument.ts) makes to pick the printed agreement, from the sellers' Property types: a
 *  condominium unit first (BUILDING_TYPES here counts it as a building too), then land and building, land, building.
 *  No seller has answered yet → plain 'Agreement'. A split sale's part blocks keep 'Agreement — Land/Building'. */
const agreementLabel = (d: any, t: TFunction): string => {
  const types: string[] = (Array.isArray(d?.sellers) ? d.sellers : []).map((s: any) => String(s?.propType || ''));
  if (types.indexOf('Condominium') >= 0) return t('pf_sec_agreement_condo');
  const hasLand = types.some(x => LAND_TYPES.indexOf(x) >= 0);
  const hasBuilding = types.some(x => BUILDING_TYPES.indexOf(x) >= 0);
  if (hasLand && hasBuilding) return t('pf_sec_agreement_both');
  if (hasLand) return t('pf_sec_agreement_land');
  if (hasBuilding) return t('pf_sec_agreement_building');
  return t('pf_sec_agreement');
};
/** How many payment stages a block shows: its Number of payment stages, 0 to MAX_STAGES. */
const stageCountOf = (f: any): number => Math.max(0, Math.min(MAX_STAGES, parseInt(f?.stageCount, 10) || 0));
/** Other, with no progress payment picked yet — one of the block's n stages, by position — and no whole 1–100 typed in
 *  the Percentage paid box the pick replaced: the agreement prints the dotted gap, and the pick is asked for. */
const transferPctMissing = (choice: any, stage: any, pct: any, n: number): boolean =>
  choice === 'Other' && !(/^\d+$/.test(String(stage ?? '').trim()) && Number(stage) >= 1 && Number(stage) <= n)
    && !(/^\d+$/.test(String(pct ?? '').trim()) && Number(pct) >= 1 && Number(pct) <= 100);
/** One payment block's figures — the agreement's own (''), or one part's — and the agreement with them changed. A
 *  part keeps its three figures, who pays its fees and its conditions only (report 7dc394ad). */
const figuresOf = (agr: any, k: PartKey): any => (k ? (agr?.parts?.[k] || emptyFigures()) : (agr || {}));
const withFigures = (agr: any, k: PartKey, f: any): any => (k
  ? { ...agr, parts: { ...(agr?.parts || {}), [k]: { price: f.price, stageCount: f.stageCount, stages: f.stages, feesPaidBy: f.feesPaidBy, condCount: f.condCount, conds: f.conds } } }
  : f);
/** Where a payment block's agreement upload is filed: 'agreement', or 'agreement:land' / 'agreement:building'. */
const agrPath = (k: PartKey): string => (k ? `agreement:${k}` : 'agreement');

// What each document reading may fill, and the dropdowns it may only fill with a listed answer. A
// person's and a company's reading also fill the address, part by part ('address.city' …).
const PERSON_KEYS = ['title', 'first', 'middle', 'last', 'idNo', 'nationality'];
// Both parents' names (report f6b1169a) — the boxes a buyer's block shows while a POA is being done,
// filled from a document that gives them (a house registration, a birth certificate, an ID back page).
const PARENT_KEYS = ['fatherFirst', 'fatherLast', 'motherFirst', 'motherLast'];
const COMPANY_KEYS = ['companyName', 'taxId'];
const ADDRESS_KEYS = ['line1', 'line2', 'city', 'postal', 'country'];
// The address parts as a reading names them, and as the highlight on the boxes an upload fills does.
const ADDRESS_PATHS = ADDRESS_KEYS.map(k => `address.${k}`);
// A buyer's or individual seller's three uploads (report fefad31c), each filed under its own path with the
// block's uid, and what its reading fills: the ID card the whole block — filed where the one 'ID / passport'
// upload was, so what was uploaded there is the ID card's; a passport the names, the number and the
// nationality, never an address; the house book, or another document giving the address, the residing
// address. The ID card and the address document fill the parents' names too, where the block shows them; the ID
// card and the passport give a buyer's date of birth (`birth`, report 06eee624), which the POA's Age is worked out from.
type PersonDoc = { kind: string; path: string; label: string; keys: string[]; address: boolean; parents: boolean; birth: boolean };
const PERSON_DOCS: PersonDoc[] = [
  { kind: 'idCard', path: 'person', label: 'pf_upload_id', keys: PERSON_KEYS, address: true, parents: true, birth: true },
  { kind: 'passport', path: 'passport', label: 'pf_upload_passport', keys: PERSON_KEYS, address: false, parents: false, birth: true },
  { kind: 'personAddress', path: 'personAddr', label: 'pf_upload_address', keys: [], address: true, parents: true, birth: false },
];
const personDocPath = (doc: PersonDoc, rowUid: string): string => `${doc.path}:${rowUid}`;
const BIRTH_DOCS = PERSON_DOCS.filter(doc => doc.birth);
// The title deed's: its type and number, the Land number, the land's units (the country pack's) and its m².
const deedKeys = (): string[] => ['deedType', 'deedNo', 'landNo', ...landUnitKeys(), 'landSqm'];
const BOOK_KEYS = ['unitNo', 'houseBookNo', 'buildingSqm', 'bedrooms', 'bathrooms'];
// The Building block's second upload, the Reservation Agreement under the house book's (report 6ae27545): the
// same building details, but no House book number — only a house book prints one. Kept and listed, never printed.
const RESERVATION_KEYS = BOOK_KEYS.filter(k => k !== 'houseBookNo');
// The Property address's own document (report 3904c89f) fills the address part by part, and the house book
// number while the Building block is shown. Its Post Code check is kept under PROP_ADDR: the property is one
// block, with no uid (and a uid never holds a ':').
const PROP_ADDR = 'property:address';
const ALLOWED: Record<string, string[]> = { title: TITLES, deedType: DEED_TYPES, country: COUNTRIES, nationality: COUNTRIES };
// A reading goes in the way typing would put it: the ID card number with its dashes, a name with its
// capitals, a number box only its number ('' = nothing usable, so the box stays blank).
const CLEAN: Record<string, (v: string) => string> = {
  idNo: fmtIdNo, first: v => normalisePersonNamePart(v), middle: v => normalisePersonNamePart(v), last: v => normalisePersonNamePart(v),
  fatherFirst: v => normalisePersonNamePart(v), fatherLast: v => normalisePersonNamePart(v),
  motherFirst: v => normalisePersonNamePart(v), motherLast: v => normalisePersonNamePart(v),
  landSqm: v => leadNum(v), buildingSqm: v => leadNum(v), bedrooms: v => leadNum(v, true), bathrooms: v => leadNum(v, true),
};
/** BLANKS ONLY: a box somebody already answered keeps its answer. Returns the block and how many boxes it filled. */
const fillBlanks = (obj: any, fields: Record<string, string>, keys: string[], prefix = ''): { next: any; n: number } => {
  const next = { ...(obj || {}) }; let n = 0;
  for (const k of keys) {
    const raw = String(fields?.[prefix + k] ?? '').trim();
    const v = raw && CLEAN[k] ? CLEAN[k](raw) : raw;
    if (!v || String(next[k] ?? '').trim()) continue;
    if (ALLOWED[k] && ALLOWED[k].indexOf(v) < 0) continue;
    next[k] = v; n++;
  }
  return { next, n };
};
/** A person's or a company's reading: its own boxes, then its address's blank parts. `postal` = it filled
 *  the Post Code, which the AI works out from the address when the document prints none (a Thai ID card). */
const fillWithAddress = (row: any, fields: Record<string, string>, keys: string[]): { next: any; n: number; postal: boolean } => {
  const own = fillBlanks(row, fields, keys);
  const was = normAddress(row?.address);
  const addr = fillBlanks(was, fields, ADDRESS_KEYS, 'address.');
  return { next: { ...own.next, address: addr.next }, n: own.n + addr.n, postal: addr.next.postal !== was.postal };
};
/** A document removed from its block takes back what its kind of reading fills (report 1d398933): each of
 *  those boxes goes blank, whoever answered it, so the next document's reading can fill it again. */
const clearKeys = (obj: any, keys: string[]): any => { const next = { ...(obj || {}) }; for (const k of keys) next[k] = ''; return next; };
/** A person's or a company's: its own boxes and the whole address. */
const clearWithAddress = (row: any, keys: string[]): any => ({ ...clearKeys(row, keys), address: clearKeys(normAddress(row?.address), ADDRESS_KEYS) });

// ── The land's m², worked out from its units (report f7b09e0d) ────────────────────────────────────
// A form saved before the unit boxes holds the m² only, no unit: that m² still counts — nothing is swept, as a
// stage saved before the % box keeps its amount — until a unit is filled, which works the m² out again.
const m2Only = (p: any): boolean => { const keys = landUnitKeys(); return keys.length > 0 && keys.every(k => blank(p?.[k])) && !blank(p?.landSqm); };
/** A title deed's reading → the Land block's BLANKS. A unit takes only its number, as the m² box does; once the
 *  reading fills a unit, the m² is worked out again from the units, as typing one does (DD's m2of). A country
 *  with no units has the deed's own m² read into a blank m² box instead. */
const fillDeed = (prop: any, fields: Record<string, string>): { next: any; n: number } => {
  const units = landUnitKeys();
  const read = { ...fields };
  for (const k of units) if (read[k]) read[k] = leadNum(read[k]);
  const got = fillBlanks(prop, read, deedKeys());
  if (!units.some(k => got.next[k] !== prop?.[k])) return got;
  const landSqm = landAreaM2(got.next);
  return { next: { ...got.next, landSqm }, n: got.n + Number(blank(prop?.landSqm) && !blank(landSqm)) };
};

// ── An agreement's reading (report eaf3b006) ─────────────────────────────────────────────────────
// The purchase price, the payment stages in the agreement's order — a stage's name only when it is one of
// STAGES; its amount as printed or, for a stage the agreement gives only as a percentage, that share of the
// agreement's own price — and who pays the fees. `count` is how many stages the agreement has, which can be
// more than the MAX_STAGES the form holds.
type AgrRead = { price: string; count: number; stages: { stage: string; amount: string }[]; fees: string };
const STAGE_NAMES = STAGES.map(([v]) => v);
const FEES_NAMES = FEES_BY.map(([v]) => v);
const readMoney = (v: any): string => { const s = moneyText(leadNum(v)); return s ? twoDp(s) : ''; };
const agrRead = (f: Record<string, string>): AgrRead => {
  const price = readMoney(f?.price);
  const all = Array.from({ length: MAX_STAGES }, (_, i) => {
    const k = `stages.${i + 1}.`;
    const stage = String(f?.[k + 'stage'] ?? '').trim();
    const pct = moneyText(leadNum(f?.[k + 'pct']));
    return { stage: STAGE_NAMES.indexOf(stage) >= 0 ? stage : '', amount: readMoney(f?.[k + 'amount']) || (pct ? stageAmount(price, pct) : '') };
  });
  const last = all.reduce((n, s, i) => (s.stage || s.amount ? i + 1 : n), 0);
  const count = Math.max(last, parseInt(leadNum(f?.stageCount, true), 10) || 0);
  const fees = String(f?.feesPaidBy ?? '').trim();
  return { price, count, stages: all.slice(0, Math.min(count, MAX_STAGES)), fees: FEES_NAMES.indexOf(fees) >= 0 ? fees : '' };
};
/** The stage rows the form shows for a stage count: those kept, then empty ones up to it. */
const stageRows = (agr: any, n: number): any[] => {
  const l = (Array.isArray(agr?.stages) ? agr.stages : []).slice(0, n).map((s: any) => (s && typeof s === 'object' ? s : { stage: '', pct: '', amount: '' }));
  while (l.length < n) l.push({ stage: '', pct: '', amount: '' });
  return l;
};
/** An agreement read while no other one's price is in the form → the Agreement's BLANKS, as every reading
 *  fills. A price it fills works each stage's % out again from its amount, and an amount it fills works out
 *  that stage's %, as typing them does. `over` = the agreement's stages past the MAX_STAGES its count filled in. */
const fillAgreement = (agr: any, r: AgrRead): { next: any; n: number; over: number } => {
  const a = agr || {};
  const price = blank(a.price) && r.price ? r.price : a.price;
  const stageCount = blank(a.stageCount) && r.count ? String(Math.min(r.count, MAX_STAGES)) : a.stageCount;
  const feesPaidBy = blank(a.feesPaidBy) && r.fees ? r.fees : a.feesPaidBy;
  let n = Number(price !== a.price) + Number(stageCount !== a.stageCount) + Number(feesPaidBy !== a.feesPaidBy);
  const k = Math.max(0, Math.min(MAX_STAGES, parseInt(stageCount, 10) || 0));
  const stages = stageRows(a, k).map((s: any, i: number) => {
    const got = r.stages[i];
    const row = { ...s };
    if (got?.stage && blank(row.stage)) { row.stage = got.stage; n++; }
    if (got?.amount && blank(row.amount)) { row.amount = got.amount; n++; }
    if (hundredths(price) && hundredths(row.amount) != null && (price !== a.price || row.amount !== s.amount)) row.pct = stagePct(price, row.amount);
    return row;
  });
  const over = stageCount !== a.stageCount ? Math.max(0, r.count - MAX_STAGES) : 0;
  return { next: { ...a, price, stageCount, stages: k ? stages : a.stages, feesPaidBy }, n, over };
};
/** A further agreement added to the figures another one filled, once the operator says Yes (the reporter: a
 *  land lease beside a building sale): the two prices summed to the cent, its stages after the rows already
 *  there up to MAX_STAGES (`over` = those that did not fit), and every % worked out again from its amount
 *  against the combined price, as a new price does. A blank fees box takes its answer. */
const addAgreement = (agr: any, r: AgrRead): { next: any; added: number; over: number } => {
  const a = agr || {};
  const price = fromHundredths((hundredths(a.price) || 0) + (hundredths(r.price) || 0));
  const k = Math.max(0, Math.min(MAX_STAGES, parseInt(a.stageCount, 10) || 0));
  const added = Math.min(r.stages.length, MAX_STAGES - k);
  const stages = stageRows(a, k).concat(r.stages.slice(0, added).map(s => ({ stage: s.stage, pct: '', amount: s.amount })))
    .map((s: any) => (hundredths(s.amount) != null ? { ...s, pct: stagePct(price, s.amount) } : s));
  const feesPaidBy = blank(a.feesPaidBy) && r.fees ? r.fees : a.feesPaidBy;
  return { next: { ...a, price, stageCount: k + added ? String(k + added) : a.stageCount, stages, feesPaidBy }, added, over: r.count - added };
};
/** A further agreement the operator chose not to add: the figures stay; only a blank fees box takes its answer. */
const feesOnly = (agr: any, r: AgrRead): { next: any; n: number } =>
  (blank(agr?.feesPaidBy) && r.fees ? { next: { ...agr, feesPaidBy: r.fees }, n: 1 } : { next: agr, n: 0 });

// ── A lease document's reading (report eab2c428) ─────────────────────────────────────────────────
// The Lease section's boxes a lease document fills, and its removal takes back — what the server's 'lease'
// reading asks for (services/propertyForm.ts), with the Agreement date. That date is the Agreement card's own
// box, which an agreement answers too: a reading fills it only while it is blank or still holds its default
// (report 90208a98), and a removal leaves it.
// The Step-up period comes after the Term and the Rent period, which decide the periods it may be, and before
// the Step-up %, so that a period the reading fills is there for its %. The Portion area comes after Whole or
// part, as it shows only beside Part. Rent per period is not one of them: the form works it out from the Total
// rent (report 805f7320), and a rent typed before then is left as it is.
const LEASE_KEYS = ['term', 'renewal', 'area', 'extent', 'portionArea', 'regDate', 'period', 'totalRent',
  'stepEvery', 'stepPct', 'accName', 'bank', 'accNo', 'regFees', 'use', 'witness1', 'witness2'];
/** A lease document's reading, its answers already the boxes' own (FormBody's leaseRead) → the Lease section's
 *  BLANKS and an Agreement date that is blank or still its default. A Step-up period goes in only when the Term and the Rent period allow it
 *  (leaseStepChoices: those chosen, or those this reading fills), and a Step-up % only beside such a period: the
 *  one chosen, or the one this reading fills. A Portion area likewise goes in only beside Part. A Total area goes
 *  in only when it is larger than the Portion area this reading gives and, beside Part, the form's own (report
 *  90a529be): one that is not is the portion's m² read as the whole, and the box is left as it was. */
const fillLease = (d: any, r: Record<string, string>): { next: any; n: number } => {
  const lease = { ...(d?.lease || {}) };
  let n = 0;
  for (const k of LEASE_KEYS) {
    if (!r[k] || !blank(lease[k])) continue;
    if (k === 'stepEvery' && leaseStepChoices(lease.term, lease.period).indexOf(r[k]) < 0) continue;
    if (k === 'stepPct' && leaseStepChoices(lease.term, lease.period).indexOf(String(lease.stepEvery ?? '')) < 0) continue;
    if (k === 'area' && (notLarger(r.area, r.portionArea) || leaseTotalTooSmall(lease, r.area))) continue;
    if (k === 'portionArea' && lease.extent !== 'Part') continue;
    lease[k] = r[k]; n++;
  }
  const agr = d?.agreement || {};
  const date = (blank(agr.date) || agr.date === agr.dateDefault) && r.agreementDate ? r.agreementDate : '';
  return { next: { ...d, lease, ...(date ? { agreement: { ...agr, date, dateDefault: '' } } : {}) }, n: n + Number(!!date) };
};
/** The name Contacts is asked about for a block's Email (report 33b5ec4e): a company seller's Company name,
 *  anybody else's First and Last name once both are filled in, with the Middle name between them when there
 *  is one (report c39980c2). '' = nothing to ask about yet. */
const contactName = (key: 'buyers' | 'sellers', r: any): string => (key === 'sellers' && r?.kind === 'Company'
  ? String(r.companyName ?? '').trim()
  : !blank(r?.first) && !blank(r?.last) ? [r.first, r.middle, r.last].map((x: any) => String(x ?? '').trim()).filter(Boolean).join(' ') : '');
/** A company seller's Tax ID ('' while blank), which its Email lookup carries so that the AI can guess the
 *  company's address when Contacts has none (report e2975736). undefined for a person — a buyer is always one
 *  on this form — whose lookup falls back on the email history instead (report 540019ef), never on the AI. */
const contactTax = (key: 'buyers' | 'sellers', r: any): string | undefined =>
  (key === 'sellers' && r?.kind === 'Company' ? String(r.taxId ?? '').trim() : undefined);

// ── The boxes still to answer before a form can be Pending or Confirmed ───────────────────────────
// Grouped by block, in the order the form shows them. Every box the form is SHOWING is required except
// Address Line 2 and a person's Middle name; a box it is not showing is not (a company seller's personal
// boxes, the land boxes with no land, the agreement when none is being prepared, a buyer's parents with
// no POA). Payment stages that do not add up to the purchase price are listed with them (report
// e897c965). Mirror of missingBoxes in server/src/services/propertyForm.ts, which refuses the same move —
// keep them in step.
// Each gap is keyed by the card it is under (BlockKey, report 5f753cef): the Land, Building and Condominium
// blocks are the Property card's, a buyer's or seller's row its card's, the agreement's parts the Agreement's.
type BlockKey = 'questions' | 'buyer' | 'seller' | 'property' | 'agreement' | 'lease' | 'poa';
const BLOCK_ORDER: BlockKey[] = ['questions', 'buyer', 'seller', 'property', 'agreement', 'lease', 'poa'];
type Gap = { key: BlockKey; block: string; boxes: string[] };
function missingBoxes(d: any, t: TFunction): Gap[] {
  const out: Gap[] = [];
  let l: string[] = [];
  const need = (v: any, label: string) => { if (blank(v)) l.push(label); };
  const close = (key: BlockKey, block: string) => { if (l.length) out.push({ key, block, boxes: l }); l = []; };
  const email = (v: any) => { if (!isEmail(v)) l.push(t('pf_email')); };
  const address = (v: any, label: string) => {
    const a = normAddress(v);
    const parts = ADDRESS_PARTS.filter(([k]) => blank(a[k])).map(([, key]) => t(key));
    if (parts.length) l.push(`${label} (${parts.join(', ')})`);
  };
  const person = (p: any, parents: boolean) => {
    need(p.title, t('pf_title')); need(p.first, t('pf_first')); need(p.last, t('pf_last'));
    email(p.email); need(p.idNo, t('pf_id_no')); need(p.nationality, t('pf_nationality')); address(p.address, t('pf_address'));
    if (parents) {
      need(p.fatherFirst, t('pf_father_first')); need(p.fatherLast, t('pf_father_last'));
      need(p.motherFirst, t('pf_mother_first')); need(p.motherLast, t('pf_mother_last'));
    }
  };
  need(d.doPoa, t('pf_q_poa')); need(d.doAgreements, t('pf_q_agreements'));
  // Due Diligence is staff's alone (report 0c21500b): this list is drawn on the staff screen only, never on a client's link.
  need(d.doDd, t('pf_q_dd'));
  need(d.doWill, t('pf_q_will'));
  close('questions', t('pf_sec_questions'));
  (d.buyers || []).forEach((b: any, i: number) => {
    if (!rolesOf(b.roles).length) l.push(t('pf_roles'));
    person(b, d.doPoa === 'Yes'); close('buyer', t('pf_buyer_n', { n: i + 1 }));
  });
  (d.sellers || []).forEach((s: any, i: number) => {
    need(s.kind, t('pf_seller_type')); need(s.propType, t('pf_prop_type'));
    if (s.kind === 'Company') {
      need(s.companyName, t('pf_company_name')); email(s.email); need(s.taxId, t('pf_tax_id')); address(s.address, t('pf_reg_address'));
    } else person(s, false);
    close('seller', t('pf_seller_n', { n: i + 1 }));
  });
  const p = d.property || {};
  need(p.name, t('pf_prop_name'));
  // The property's own address — the agreement prints it, and its City is the Land Office the
  // transfer is registered at (report 4ba8eca0). Asked only while agreements are being prepared.
  if (d.doAgreements === 'Yes') address(p.address, t('pf_prop_address'));
  close('property', t('pf_sec_property'));
  const types: string[] = (d.sellers || []).map((s: any) => String(s.propType || ''));
  if (types.some(x => LAND_TYPES.indexOf(x) >= 0)) {
    need(p.deedType, t('pf_deed_type')); need(p.deedNo, t('pf_deed_no')); need(p.landNo, t('poa_land_no'));
    // Each of the country's land units (report f7b09e0d), unless the form holds an m² from before them (m2Only);
    // a country with no units types the m².
    const units = landAreaUnits();
    if (!units.length) need(p.landSqm, t('pf_land_sqm'));
    else if (!m2Only(p)) units.forEach(u => need(p[u.key], u.label));
    close('property', t('pf_land'));
  }
  // The House book number is optional — the house book may not be issued yet — and Levels are gone (report 6ae27545).
  if (types.some(x => BUILDING_TYPES.indexOf(x) >= 0)) {
    need(p.unitNo, t('pf_unit_no'));
    need(p.buildingSqm, t('pf_building_sqm'));
    need(p.bedrooms, t('pf_bedrooms')); need(p.bathrooms, t('pf_bathrooms'));
    close('property', t('pf_building'));
  }
  // A condominium unit's own title: the Or.Chor.2 in place of the land deed and the house book, and
  // the unit's building, floor and share of the common property (report 4ba8eca0).
  if (types.indexOf('Condominium') >= 0) {
    need(p.orChor2No, t('pf_orchor2_no')); need(p.condoBuilding, t('pf_condo_building'));
    need(p.condoFloor, t('pf_condo_floor')); need(p.condoShare, t('pf_condo_share'));
    close('property', t('pf_condo'));
  }
  if (d.doAgreements === 'Yes') {
    const a = d.agreement || {};
    // The agreement's own date, and who signs for the seller — an owner, or somebody authorised to
    // sign for one, whose name the agreement then has to give. The conditions are optional.
    need(a.date, t('pf_agr_date')); need(a.signAs, t('pf_signs_as'));
    if (a.signAs === 'Authorized Representative') need(a.ownerName, t('pf_owner_name'));
    // One payment block — the card's own, or, when the sale is split, the Land block's and the Building
    // block's (report 0d7e130c), each listed under its own heading after the card's other boxes.
    const figures = (f: any) => {
      need(f.price, t('pf_price')); need(f.stageCount, t('pf_stage_count'));
      const n = Math.max(0, Math.min(MAX_STAGES, parseInt(f.stageCount, 10) || 0));
      const stages: any[] = (Array.isArray(f.stages) ? f.stages : []).slice(0, n);
      for (let i = 0; i < n; i++) {
        need(stages[i]?.stage, t('pf_stage_n', { n: i + 1 }));
        need(stages[i]?.pct, `${t('pf_stage_n', { n: i + 1 })} (${t('pf_pct')})`);
        need(stages[i]?.amount, `${t('pf_stage_n', { n: i + 1 })} (${t('pf_amount')})`);
      }
      // The stages must come to 100% of the purchase price, to the cent (report e897c965). A blank price is
      // listed above already; a price that is not a number, or is nothing, can never be matched.
      const price = hundredths(f.price);
      if (n && !blank(f.price) && !(price && stageSum(stages) === price)) l.push(t('pf_stages_total_bad'));
    };
    // When a part transfers (report 8aa83b74): its Progress payment, beside Other alone — one of the block's own stages
    // (report bb4ea111) — under the one block for each part the one sale transfers (transferParts), under each part's
    // own block when the sale is split. A Percentage paid typed before the pick still counts as answered.
    const transfer = (k: 'land' | 'building', f: any) => {
      if (transferPctMissing(a[`${k}Transfer`], a[`${k}TransferStage`], a[`${k}TransferPct`], stageCountOf(f))) l.push(`${t(k === 'land' ? 'pf_transfer_land' : 'pf_transfer_building')} (${t('pf_transfer_stage')})`);
    };
    // Who pays the transfer fees is the block's too (report 7dc394ad): a split sale asks it under each part's own.
    const split = saleSplits(d);
    if (!split) { figures(a); transferParts(d).forEach(k => transfer(k, a)); need(a.feesPaidBy, t('pf_fees_by')); }
    close('agreement', agreementLabel(d, t));
    if (split) PARTS.forEach(([k, key]) => { const f = figuresOf(a, k); figures(f); transfer(k, f); need(f.feesPaidBy, t('pf_fees_by')); close('agreement', t('pf_agr_part', { part: t(key) })); });
  }
  // The Lease section (report eab2c428), while it shows: every box it shows, the Term a whole 1 to 30
  // years and each date a real day. The Agreement date is the Agreement block's own box, shown here too:
  // listed here unless that block lists it already. The Total area counts while it shows the property's
  // own m², which it starts as; beside Part, one not larger than the Portion area is asked for again (report
  // 90a529be, leaseTotalTooSmall). The Portion area is asked for only beside Part, the only time it shows. A
  // Step-up period other than None must be one the Term and the Rent period allow (a saved one they no longer
  // allow is asked for again, report 805f7320) and needs its Step-up % above 0. Rent per period is worked out,
  // never asked for. Its Thai boxes are not asked for: the English prints until a Thai value exists.
  if (leaseApplies(d)) {
    const L = d.lease || {};
    const a = d.agreement || {};
    const day = (v: any, label: string) => { if (!isDay(v)) l.push(label); };
    const years = String(L.term ?? '').trim();
    if (!(/^\d+$/.test(years) && Number(years) >= 1 && Number(years) <= 30)) l.push(t('pf_lease_term'));
    need(L.renewal, t('pf_lease_renewal'));
    if (!(d.doAgreements === 'Yes' && blank(a.date))) day(a.date, t('pf_agr_date'));
    const totalArea = blank(L.area) ? (types.some(x => LAND_TYPES.indexOf(x) >= 0) ? p.landSqm : p.buildingSqm) : L.area;
    if (blank(totalArea) || leaseTotalTooSmall(L, totalArea)) l.push(t('pf_lease_area'));
    need(L.extent, t('pf_lease_extent'));
    if (L.extent === 'Part') need(L.portionArea, t('pf_lease_portion'));
    day(L.regDate, t('pf_lease_reg_date'));
    need(L.period, t('pf_lease_period'));
    need(L.totalRent, t('pf_lease_total_rent'));
    if (!blank(L.stepEvery) && L.stepEvery !== 'None' && leaseStepChoices(L.term, L.period).indexOf(String(L.stepEvery)) < 0) l.push(t('pf_lease_step_every'));
    if (!blank(L.stepEvery) && L.stepEvery !== 'None' && !(Number(L.stepPct) > 0)) l.push(t('pf_lease_step_pct'));
    need(L.accName, t('pf_lease_acc_name')); need(L.bank, t('pf_lease_bank')); need(L.accNo, t('pf_lease_acc_no'));
    need(L.regFees, t('pf_lease_reg_costs'));
    need(L.use, t('pf_lease_use'));
    need(L.assignment, t('pf_lease_assign'));
    need(L.witness1, t('pf_lease_witness1')); need(L.witness2, t('pf_lease_witness2'));
    close('lease', t('pf_sec_lease'));
  }
  return out;
}

const copyText = async (s: string): Promise<boolean> => { try { await navigator.clipboard.writeText(s); return true; } catch { return false; } };

// The view link shows the form exactly as the edit link does, every box read-only — and without the
// red marks, as nothing can be typed there.
const RO = createContext(false);

// A box's label; a required box's carries the red *, an optional box's a grey "(optional)" — neither on
// the view link, where nothing can be typed.
function Lbl({ text, req }: { text: string; req: boolean }) {
  const { t } = useTranslation();
  const ro = useContext(RO);
  return <span style={lbl}>{text}{!ro && (req ? <span style={star}>*</span> : <span style={optMark}>{t('pf_optional')}</span>)}</span>;
}
// One text box. Required unless `opt`; red while blank, or while `bad` says its answer is wrong; amber
// while `check` asks for an answer the form filled in to be checked; filled light red while `hi` (the upload
// under the pointer fills it — every box below takes `hi` the same way).
// `input` carries the box's kind (inputMode, autoComplete, autoCapitalize, onBlur …); `tag` is
// written inside the box's right end (a price box's currency code). `side` is drawn beside the box, as
// tall as it (a Special condition's AI button, report c96a33b3).
function Txt({ label, value, onChange, ph, type, opt, bad, check, hi, input, tag, side }: {
  label: string; value: string; onChange: (v: string) => void; ph?: string; type?: string; opt?: boolean; bad?: boolean; check?: boolean; hi?: boolean;
  input?: React.InputHTMLAttributes<HTMLInputElement>; tag?: string; side?: React.ReactNode;
}) {
  const ro = useContext(RO);
  const red = !ro && !opt && (bad ?? blank(value));
  const box = (
    <div style={{ position: 'relative', ...(side ? { flex: 1, minWidth: 0 } : {}) }}>
      <input {...input} style={{ ...inp, ...(tag ? { paddingRight: 50 } : {}), ...(ro ? roInp : {}), ...(red ? badInp : {}), ...(check ? checkInp : {}), ...(hi ? hiInp : {}) }} type={type || 'text'}
        placeholder={ro ? '' : ph} value={value || ''} readOnly={ro} onChange={e => onChange(e.target.value)} />
      {tag && <span style={curTag}>{tag}</span>}
    </div>
  );
  return (
    <div><Lbl text={label} req={!opt} />
      {side ? <div style={{ display: 'flex', gap: 8 }}>{box}{side}</div> : box}
    </div>
  );
}
// One Special condition box (report 4ba8eca0) with its AI button (report c96a33b3): the sparkles beside the
// box opens a small panel under it holding the box's text, which may be changed there. 'Write a better
// clause' has the AI draft that wording as one clause in formal contract English — its meaning kept, no fact
// added, no numbering — and shows it with Insert (it takes the place of the box's text, which then saves as
// typing does), Try again (another draft of the wording in the panel) and Cancel. Nothing reaches the box,
// or the saved form, before Insert. `improve` comes from the staff screen only, never on the view link:
// without it this is the plain box it always was.
function CondBox({ label, value, onChange, improve }: { label: string; value: string; onChange: (v: string) => void; improve?: (text: string) => Promise<string> }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [clause, setClause] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // Only the latest ask's answer is shown: one that comes back after Cancel, or after a newer ask, is dropped.
  const seq = useRef(0);
  const close = () => { seq.current++; setOpen(false); setBusy(false); setClause(''); setErr(''); };
  const toggle = () => {
    if (open) { close(); return; }
    setDraft(value || ''); setClause(''); setErr(''); setOpen(true);
  };
  const long = draft.length > MAX_CLAUSE;
  const can = !!improve && !busy && !blank(draft) && !long;
  const write = async () => {
    if (!can || !improve) return;
    const n = ++seq.current;
    setBusy(true); setErr('');
    try {
      const got = String(await improve(draft.trim())).trim();
      if (n !== seq.current) return;
      setClause(got);
      if (!got) setErr(t('pf_clause_none'));
    } catch (e: any) {
      if (n !== seq.current) return;
      setErr(!e?.htmlPage && e?.message ? String(e.message) : t('pf_clause_failed'));
    }
    setBusy(false);
  };
  const insert = () => { if (!clause || busy) return; onChange(clause); close(); };
  const btn = (on: boolean): React.CSSProperties => ({ ...btnG, padding: '6px 12px', fontSize: 12.5, display: 'inline-flex', alignItems: 'center', gap: 6, opacity: on ? 1 : 0.6, cursor: on ? 'pointer' : 'default' });
  const main: React.CSSProperties = { background: 'var(--accent)', border: '1px solid var(--accent)', color: '#fff' };
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <Txt label={label} opt value={value} onChange={onChange} side={improve && (
        <button type="button" onClick={toggle} title={t('pf_clause_ai')} aria-label={t('pf_clause_ai')} aria-expanded={open}
          style={{ ...btnG, padding: '0 11px', fontSize: 15, display: 'inline-flex', alignItems: 'center', ...(open ? { color: 'var(--accent)', border: '1px solid var(--accent)' } : {}) }}>
          <i className="ti ti-sparkles" aria-hidden />
        </button>
      )} />
      {improve && open && (
        <div style={{ ...noteBox, display: 'grid', gap: 8, color: 'var(--text-primary)', border: '1px solid var(--accent)', padding: 10 }}>
          <div>
            <span style={lbl}>{t('pf_clause_yours')}</span>
            <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={3} placeholder={t('pf_clause_ph')}
              style={{ ...inp, display: 'block', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.45, ...(long ? badInp : {}) }} />
            {long && <div style={badHint}>{t('pf_clause_too_long', { max: MAX_CLAUSE })}</div>}
          </div>
          {clause && (
            <div>
              <span style={lbl}>{t('pf_clause_better')}</span>
              <div style={{ ...inp, background: 'var(--bg-surface)', border: '1px solid var(--accent)', lineHeight: 1.45, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', opacity: busy ? 0.6 : 1 }}>{clause}</div>
            </div>
          )}
          {err && <div style={{ fontSize: 12.5, color: '#B42318' }}>{err}</div>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {clause ? (
              <>
                <button type="button" disabled={busy} onClick={insert} style={{ ...btn(!busy), ...main }}>
                  <i className="ti ti-check" aria-hidden />{t('pf_clause_insert')}
                </button>
                <button type="button" disabled={!can} onClick={write} style={btn(can)}>
                  <i className={`ti ${busy ? 'ti-loader' : 'ti-refresh'}`} aria-hidden />{t('pf_clause_again')}
                </button>
              </>
            ) : (
              <button type="button" disabled={!can} onClick={write} style={{ ...btn(can), ...main }}>
                <i className={`ti ${busy ? 'ti-loader' : 'ti-sparkles'}`} aria-hidden />{t('pf_clause_write')}
              </button>
            )}
            <button type="button" onClick={close} style={btn(true)}>{t('pf_clause_cancel')}</button>
          </div>
        </div>
      )}
    </div>
  );
}
// `opt` = the answer may stay blank (the agreement's Special conditions count, report 4ba8eca0): a
// grey "(optional)" instead of the red *, and no red outline while nothing is chosen. `bad` as Txt's: it says
// whether the outline is red in place of the blank test (the Progress payment pick, answered as well by a
// Percentage paid typed before it). `noDash` leaves out the '—' line, for a dropdown that always holds one of its
// `options` (Made at, whose first choice stands for the blank box, report 404df7d2).
function Pick({ label, value, onChange, options, opt, bad, hi, noDash }: { label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[]; opt?: boolean; bad?: boolean; hi?: boolean; noDash?: boolean }) {
  const ro = useContext(RO);
  const red = !ro && !opt && (bad ?? !value);
  return (
    <div><Lbl text={label} req={!opt} />
      <select style={{ ...inp, ...(ro ? roInp : {}), ...(red ? badInp : {}), ...(hi ? hiInp : {}) }} disabled={ro} value={value || ''} onChange={e => onChange(e.target.value)}>
        {!noDash && <option value="">—</option>}
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
// A name box: capitals as the client's keyboard types a name, the browser's own name suggestions, and
// the house capitals rule (utils/personName.ts — "SMITH" → "Smith") as the box is left. Never on the
// view link: a tidied answer there would be a save that link may not make. `opt` = it may stay blank
// (a Middle name).
function NameBox({ label, value, onChange, part, opt, hi }: {
  label: string; value: string; onChange: (v: string) => void; part: 'given-name' | 'additional-name' | 'family-name'; opt?: boolean; hi?: boolean;
}) {
  const ro = useContext(RO);
  return <Txt label={label} value={value} onChange={onChange} opt={opt} hi={hi} input={{
    autoComplete: part, autoCapitalize: 'words',
    onBlur: () => { if (ro) return; const v = normalisePersonNamePart(value || ''); if (v !== (value || '')) onChange(v); },
  }} />;
}
// The Lease section's Account name box (report 4c63e9a1, opening #6: "do not allow all caps and always use
// 'Company Limited' in full or 'Partnership Limited' in full"): capitals as the keyboard types a name, and the
// rule of utils/accountName.ts as the box is left — a name in capitals written in capitalised words, "Co., Ltd." /
// "Part., Ltd." written out as "Company Limited" / "Limited Partnership" (never in capitals). Never on the view
// link, as NameBox. A lease document's reading comes through the same rule (the server's, before the screen sees
// it, and leaseRead's), and the Lease Agreement prints the box through it.
function AccountNameBox({ label, value, onChange, hi }: { label: string; value: string; onChange: (v: string) => void; hi?: boolean }) {
  const ro = useContext(RO);
  return <Txt label={label} value={value} onChange={onChange} hi={hi} input={{
    autoCapitalize: 'words',
    onBlur: () => { if (ro) return; const v = accountNameInFull(value || ''); if (v !== (value || '')) onChange(v); },
  }} />;
}
// An email box: type=email, and an answer that is not an email address is marked once the box is left.
// `check` = the answer was filled in from Contacts: the box is amber with a line asking for it to be
// checked, as a Post Code a document's reading filled is, until somebody changes it (`onChecked`).
// `guess` = it was the AI's pick among the addresses the system holds for a company (report e2975736),
// and the line says so. `history` = it was the one address the email history holds for a person's name
// (report 540019ef), and the line says that. `note` = it was the Email address in the hidden note on the
// person's stored document (report 14486e36), and the line says that.
function EmailBox({ label, value, onChange, check, guess, history, note, onChecked, hi }: { label: string; value: string; onChange: (v: string) => void; check?: boolean; guess?: boolean; history?: boolean; note?: boolean; onChecked?: () => void; hi?: boolean }) {
  const { t } = useTranslation();
  const ro = useContext(RO);
  const [typing, setTyping] = useState(false);
  const wrong = !blank(value) && !isEmail(value) && !typing;
  const checking = !!check && !ro && !blank(value);
  return (
    <div>
      <Txt label={label} type="email" value={value} onChange={v => { if (check && onChecked) onChecked(); onChange(v); }} bad={blank(value) || wrong} check={checking} hi={hi}
        input={{ autoComplete: 'email', onFocus: () => setTyping(true), onBlur: () => setTyping(false) }} />
      {wrong && !ro && <div style={badHint}>{t('pf_email_bad')}</div>}
      {checking && <div style={{ ...checkHint, marginTop: 3 }}><i className={`ti ${guess ? 'ti-sparkles' : history ? 'ti-history' : note ? 'ti-note' : 'ti-address-book'}`} aria-hidden style={{ marginRight: 4 }} />{t(guess ? 'pf_email_guess' : history ? 'pf_email_history' : note ? 'pf_email_note' : 'pf_email_check')}</div>}
    </div>
  );
}
// A number box: digits only (and a decimal point unless it counts whole things), a number keypad on a phone.
// `bad` as Txt's (false = not red while blank: a land unit on a form whose m² is from before the units, m2Only).
function NumBox({ label, value, onChange, whole, bad, hi }: { label: string; value: string; onChange: (v: string) => void; whole?: boolean; bad?: boolean; hi?: boolean }) {
  return <Txt label={label} value={value} onChange={v => onChange(numText(v, whole))} bad={bad} hi={hi} input={{ inputMode: whole ? 'numeric' : 'decimal' }} />;
}
// A box the form works out itself (the land's m², from its units — report f7b09e0d): read-only and grey, as on
// the view link, with neither a * nor "(optional)" — the boxes it is worked out from carry the red.
// `type` = 'date' for a worked-out day (the Lease's Expiry date), shown as the date boxes beside it are.
// `tag` is written inside its right end, as a price box's currency code is (the Lease's Rent per period).
function CalcBox({ label, value, type, tag, ph, hi }: { label: string; value: string; type?: string; tag?: string; ph?: string; hi?: boolean }) {
  return (
    <div><span style={lbl}>{label}</span>
      <div style={{ position: 'relative' }}>
        <input style={{ ...inp, ...(tag ? { paddingRight: 50 } : {}), ...roInp, ...(hi ? hiInp : {}) }} type={type || 'text'} placeholder={ph} value={value || ''} readOnly />
        {tag && <span style={curTag}>{tag}</span>}
      </div>
    </div>
  );
}
// A price box: grouped in thousands as it is typed, two decimals once it is left (not on the view
// link), the firm's currency code (its region settings) inside the right end. What is kept is the
// plain number.
function MoneyBox({ label, value, onChange, cur, hi }: { label: string; value: string; onChange: (v: string) => void; cur: string; hi?: boolean }) {
  const ro = useContext(RO);
  return <Txt label={label} value={grouped(value)} onChange={v => onChange(moneyText(v))} tag={cur} hi={hi} input={{
    inputMode: 'decimal',
    onBlur: () => { if (ro) return; const v = twoDp(value || ''); if (v !== (value || '')) onChange(v); },
  }} />;
}
// A percentage box (a payment stage's, report e897c965): it keeps its number as a price box does —
// digits, one point, two decimals once it is left (not on the view link) — with '%' inside the right end.
function PctBox({ label, value, onChange, hi }: { label: string; value: string; onChange: (v: string) => void; hi?: boolean }) {
  const ro = useContext(RO);
  return <Txt label={label} value={value} onChange={v => onChange(moneyText(v))} tag="%" hi={hi} input={{
    inputMode: 'decimal',
    onBlur: () => { if (ro) return; const v = twoDp(value || ''); if (v !== (value || '')) onChange(v); },
  }} />;
}
// The totals line under the payment stages (report e897c965): the amounts' share of the purchase price
// and their sum, each under its column. Green when that is 100% and exactly the price; red with the
// difference otherwise — and until it matches the form cannot be moved to Pending or Confirmed
// (missingBoxes). The % is worked out from the summed amounts, so amounts that come to the price to the
// cent read 100.00% however each stage's own % was rounded. The view link shows it without the colours.
function StageTotals({ price, stages, cur }: { price: string; stages: any[]; cur: string }) {
  const { t } = useTranslation();
  const ro = useContext(RO);
  const p = hundredths(price);
  const sum = stageSum(stages);
  const diff = p ? sum - p : 0;
  const ok = !!p && !diff;
  const share = (cents: number): string => (p ? grouped(fromHundredths(Math.round(Math.abs(cents) * 10000 / p))) : '—');
  const withCur = (cents: number): string => [grouped(fromHundredths(Math.abs(cents))), cur].filter(Boolean).join(' ');
  const color = ro ? 'var(--text-secondary)' : ok ? '#16a34a' : '#B42318';
  const msg = ro ? '' : !p ? t('pf_total_no_price') : ok ? t('pf_total_ok')
    : t(diff > 0 ? 'pf_total_over' : 'pf_total_short', { amount: withCur(diff), pct: `${share(diff)}%` });
  // Number at the left and unit at the right end, where the boxes above have theirs.
  const cell = (n: string, unit: string) => (
    <span style={{ display: 'flex', justifyContent: 'space-between', gap: 6, padding: '0 10px 0 11px', minWidth: 0 }}>
      <span>{n}</span><span style={{ fontSize: 12.5 }}>{unit}</span>
    </span>
  );
  return (
    <div style={{ display: 'grid', gap: 4, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: STAGE_COLS, gap: 10, fontSize: 13.5, fontWeight: 700, color }}>
        <span>{t('pf_stages_total')}</span>
        {cell(share(sum), p ? '%' : '')}
        {cell(grouped(fromHundredths(sum)), cur)}
      </div>
      {msg && (
        <div style={{ fontSize: 12.5, color }}>
          <i className={`ti ${ok ? 'ti-circle-check' : 'ti-alert-circle'}`} aria-hidden style={{ marginRight: 4 }} />{msg}
        </div>
      )}
    </div>
  );
}
// An address in parts: Line 1, Line 2 (optional), City, Post Code and a Country dropdown. `check` = a
// document's reading filled the Post Code: that box is amber with a line asking for it to be checked,
// until somebody changes it (`onChecked`). `hi` = the parts the upload under the pointer fills, each filled light red.
// `noteCheck` = the address was filled in from the hidden note on the person's stored passport (report 73f2d29a — its
// typed boxes, report 14486e36, or its free text into Line 1): Line 1 is amber with a line of its own, until
// somebody changes it (`onNoteChecked`).
function AddressBox({ label, value, onChange, check, onChecked, noteCheck, onNoteChecked, hi }: { label: string; value: any; onChange: (v: any) => void; check?: boolean; onChecked?: () => void; noteCheck?: boolean; onNoteChecked?: () => void; hi?: string[] }) {
  const { t } = useTranslation();
  const ro = useContext(RO);
  const a = normAddress(value);
  const checking = !!check && !ro && !blank(a.postal);
  const noteChecking = !!noteCheck && !ro && !blank(a.line1);
  const ring = (k: string): React.CSSProperties => (hi && hi.indexOf(k) >= 0 ? hiInp : {});
  const part = (k: string, ph: string, ac: string, opt?: boolean) => (
    <input style={{ ...inp, ...(ro ? roInp : {}), ...(!ro && !opt && blank(a[k]) ? badInp : {}), ...((k === 'postal' && checking) || (k === 'line1' && noteChecking) ? checkInp : {}), ...ring(k) }} placeholder={ro ? '' : ph} autoComplete={ac}
      value={a[k] || ''} readOnly={ro} onChange={e => { if (k === 'postal' && check && onChecked) onChecked(); if (k === 'line1' && noteCheck && onNoteChecked) onNoteChecked(); onChange({ ...a, [k]: e.target.value }); }} />
  );
  return (
    <div><Lbl text={label} req />
      <div style={{ display: 'grid', gap: 8 }}>
        <div>
          {part('line1', t('pf_addr_line1'), 'address-line1')}
          {noteChecking && <div style={{ ...checkHint, marginTop: 3 }}><i className="ti ti-note" aria-hidden style={{ marginRight: 4 }} />{t('pf_note_check')}</div>}
        </div>
        {part('line2', t('pf_addr_line2'), 'address-line2', true)}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.35fr)', gap: 8 }}>
          {part('city', t('pf_addr_city'), 'address-level2')}
          {part('postal', t('pf_addr_postal'), 'postal-code')}
          <select style={{ ...inp, ...(ro ? roInp : {}), ...(!ro && blank(a.country) ? badInp : {}), ...ring('country') }} disabled={ro} autoComplete="country-name"
            value={a.country || ''} onChange={e => onChange({ ...a, country: e.target.value })}>
            <option value="">{t('pf_addr_country')}</option>
            {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {checking && <div style={{ ...checkHint, gridColumn: '2 / -1' }}><i className="ti ti-sparkles" aria-hidden style={{ marginRight: 4 }} />{t('pf_postal_check')}</div>}
        </div>
      </div>
    </div>
  );
}
// A buyer's or lessee's tick boxes (report 21148c83): land on the left, building on the right, Buyer above
// Lessee. As many as apply, at least one — red while none is ticked. Ticking Buyer of a thing takes the tick
// off Lessee of the same thing, and the other way round. Under Buyer of the land, the reporter's reminder
// that a foreigner can never own land directly: a hint only, shown whatever the buyer's Nationality says.
function RolesBox({ value, onChange }: { value: any; onChange: (v: string[]) => void }) {
  const { t } = useTranslation();
  const ro = useContext(RO);
  const on = rolesOf(value);
  const flip = (r: string) => {
    const next = on.indexOf(r) >= 0 ? on.filter(x => x !== r) : on.filter(x => x !== ROLE_OTHER[r]).concat([r]);
    onChange(ROLE_NAMES.filter(x => next.indexOf(x) >= 0));
  };
  return (
    <div><Lbl text={t('pf_roles')} req />
      <div style={{ ...inp, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 8, ...(ro ? roInp : {}), ...(!ro && !on.length ? badInp : {}) }}>
        {ROLES.map(([r, key]) => (
          <div key={r} style={{ minWidth: 0 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: ro ? 'default' : 'pointer' }}>
              <input type="checkbox" checked={on.indexOf(r) >= 0} disabled={ro} onChange={() => flip(r)} style={{ width: 15, height: 15, margin: 0, flexShrink: 0 }} />
              {t(key)}
            </label>
            {r === 'Buyer of the land' && !ro && (
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 3, paddingLeft: 22 }}>
                <i className="ti ti-info-circle" aria-hidden style={{ marginRight: 4 }} />{t('pf_role_land_foreigner')}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// What the form body needs from whoever shows it — the staff screen (by form id, logged in) or the
// client's link (by token, no login).
interface Ctl {
  data: any;
  // A change to the form. `auto` = one the screen makes by itself as it opens, not one somebody made: it moves no default
  // date (report 90208a98, defaultDates.ts).
  set: (fn: (d: any) => any, auto?: boolean) => void;
  files: any[];
  upload: (file: File, path: string, kind: string) => Promise<any>;
  remove: (fileId: string) => Promise<any>;
  open: (f: any) => void;
  // Staff only: the one email Contacts holds for a company or person name ('' = none, or more than one).
  // A company seller's lookup carries its Tax ID ('' while that box is blank); when Contacts has no answer
  // for it, the email is the AI's pick among the addresses the system holds, `guess` (report e2975736).
  // A person's carries the Middle name; when Contacts has no answer for them, the email is the one address
  // the email history holds for that name, `history` (report 540019ef).
  // The client's links have none, so nothing from Contacts ever reaches a client.
  contactEmail?: (name: string, taxId?: string, middle?: string) => Promise<{ email: string; guess: boolean; history: boolean }>;
  // Staff only: one Special condition's wording drafted by AI as a clause (report c96a33b3; '' = it states no
  // condition). The client's links have none, so their Special condition boxes carry no AI button.
  improveClause?: (text: string) => Promise<string>;
  // Staff only: the hidden note on a person's stored document — their passport first — found by their ID /
  // passport number or their First and Last name ('' = none; report 73f2d29a). `file` is the document's name,
  // `passport` whether it is one, `fields` the note's typed boxes (report 14486e36; none on a note written as free
  // text before the boxes). The client's links have none, so a note never reaches a client.
  docNote?: (idNo: string, first: string, last: string) => Promise<{ note: string; file: string; passport: boolean; fields?: FileNoteFields }>;
  // Staff only: the date of birth alone, read again from a buyer's upload already on the form ('' = none; report
  // beb72eec), and an attorney's kept on their entry in the firm's list. The client's links have neither.
  rereadBirth?: (fileId: string) => Promise<string>;
  attorneyBirth?: (name: { en: string; th: string }, birthDate: string, was?: string) => Promise<void>;
  // Staff only: the AI's Thai for the POA card's buyer boxes still empty once what is on screen is saved, and how many it left
  // (report b4f708fa). The client's links have none, so their POA card's Thai boxes stay as a person types them.
  poaTranslate?: () => Promise<{ fills: PoaThaiFill[]; left: number }>;
}

// The paste box beside a block's upload (report f3d358ea): focus it and paste a screenshot or a copied
// file, or drag a file onto it. What it gets goes to `onFile` — the copied file, else the clipboard's first
// image — and nothing else on the page sees the paste (an open chat would take a screenshot too). It is
// drawn only while the block can take a file, so its focus and drag state start afresh each time.
function PasteBox({ onFile }: { onFile: (file?: File | null) => void }) {
  const { t } = useTranslation();
  const [focus, setFocus] = useState(false);
  const [over, setOver] = useState(false);
  const hot = focus || over;
  return (
    <div tabIndex={0} onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
      onPaste={e => {
        e.preventDefault(); e.stopPropagation();
        const shot = Array.from(e.clipboardData?.items || []).find(it => /^image\//.test(it.type))?.getAsFile();
        onFile(e.clipboardData?.files?.[0] || shot);
      }}
      onDragOver={e => { e.preventDefault(); e.stopPropagation(); if (!over) setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); e.stopPropagation(); setOver(false); onFile(e.dataTransfer?.files?.[0]); }}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', fontSize: 12.5, borderRadius: 8, outline: 'none', cursor: 'default',
        border: `1px dashed ${hot ? 'var(--accent)' : 'var(--border-strong)'}`, color: hot ? 'var(--accent)' : 'var(--text-tertiary)', background: over ? 'var(--bg-hover)' : 'transparent' }}>
      <i className="ti ti-clipboard" aria-hidden style={{ pointerEvents: 'none' }} />{t('pf_paste_here')}
    </div>
  );
}

// What a reading did, for the line under its upload: how many boxes it filled and whether one was the Post
// Code, or a line of its own (`msg`). A reading may first need the operator's Yes or No (`ask` — a second
// agreement: combine the totals?); the answer then does the filling and says what it did.
type Read = { n: number; postal?: boolean; msg?: string; ask?: Ask };
type Ask = { text: string; yes: string; no: string; answer: (yes: boolean) => Read };

// One block's documents: the upload (read by AI), the paste box beside it (PasteBox, the same way in) and
// the files already given to it. `onRead` says how many boxes the reading filled, and whether one was the
// Post Code — that one is asked to be checked.
// A question the reading asks stays on the page, under the upload, until it is answered: a dialog could
// be closed unseen while the operator is in another tab, and nothing more is uploaded here meanwhile.
// `onRemoved` runs once a file is removed, with its id: the block clears the boxes that kind of document fills.
// `plain` = the document is only kept with the form, never read by AI (the Or.Chor.2, report
// 4ba8eca0): the button carries the upload icon rather than the AI's sparkle, and the line under it
// says the document was saved rather than how many boxes a reading filled.
// `onHover` is told when the pointer comes onto the upload — its button or its paste box — and when it
// leaves (report fefad31c), so that the form can light the boxes this upload's reading fills meanwhile. An
// upload that goes while the pointer is on it (its block removed) tells it the pointer has left.
// The button's text and a file's line wrap: every upload stands in the page's margin, which may be narrow (MarginDocs).
// The button says only "Upload <document>" (report 8347170a: "remove all other text"), and everything here stands
// against its right edge — the reporter's "right-align (to the block)": in the margin the uploads hug the card, in a
// narrow window (inside their block) its right edge, and a wrapped label, a reading's line, a question and a file's
// line all end flush right.
function DocBox({ label, path, kind, ctl, onRead, onRemoved, plain, onHover }: { label: string; path: string; kind: string; ctl: Ctl; onRead: (fields: Record<string, string>, file: any) => Read; onRemoved: (fileId: string) => void; plain?: boolean; onHover?: (on: boolean) => void }) {
  const { t } = useTranslation();
  const ro = useContext(RO);
  const [st, setSt] = useState<{ busy: boolean; msg: string; bad: boolean }>({ busy: false, msg: '', bad: false });
  const [ask, setAsk] = useState<Ask | null>(null);
  const [over, setOver] = useState(false);
  useEffect(() => {
    if (!over || !onHover) return undefined;
    onHover(true);
    return () => onHover(false);
  }, [over]); // eslint-disable-line
  const input = useRef<HTMLInputElement>(null);
  const mine = ctl.files.filter((f: any) => f.path === path);
  const wait = st.busy || !!ask;
  const said = (got: Read): string => got.msg ?? (plain ? t('pf_doc_saved')
    : got.n ? [t('pf_ai_filled', { n: got.n }), got.postal ? t('pf_ai_postal') : ''].filter(Boolean).join(' ') : t('pf_ai_nothing'));
  const pick = async (file?: File | null) => {
    if (!file) return;
    setSt({ busy: true, msg: plain ? t('pf_uploading') : t('pf_reading'), bad: false });
    try {
      const r: any = await ctl.upload(file, path, kind);
      if (r?.aiError) setSt({ busy: false, msg: t('pf_ai_failed', { msg: r.aiError }), bad: true });
      else {
        const got: Read = r?.fields ? onRead(r.fields, r.file) : { n: 0 };
        setAsk(got.ask || null);
        setSt({ busy: false, msg: got.ask ? '' : said(got), bad: false });
      }
    } catch (e: any) { setSt({ busy: false, msg: e?.message || '', bad: true }); }
    if (input.current) input.current.value = '';
  };
  // A file pasted or dropped on the paste box goes the upload's way. Only an image or a PDF, as the upload
  // offers; a screenshot that comes without a name of its own is called 'pasted image' (the upload needs one).
  const take = (file?: File | null) => {
    if (!file || !(/^image\//.test(file.type) || file.type === 'application/pdf')) { setSt({ busy: false, msg: t('pf_paste_bad'), bad: true }); return; }
    pick(file.name ? file : new File([file], `${t('pf_pasted')}.${file.type.split('/')[1].split('+')[0]}`, { type: file.type }));
  };
  const reply = (yes: boolean) => {
    if (!ask) return;
    const got = ask.answer(yes);
    setAsk(null);
    setSt({ busy: false, msg: said(got), bad: false });
  };
  // Only once the file is really gone: the block clears its boxes, and the last reading's line goes with
  // them ("Filled 7 boxes…" would no longer be true) unless a new document is being read right now. A
  // question still waiting goes too: the figures it asked about have just been cleared.
  const drop = async (fileId: string) => {
    if (!window.confirm(t('pf_remove_file_confirm'))) return;
    try { await ctl.remove(fileId); } catch { return; }
    setAsk(null);
    setSt(s => (s.busy ? s : { busy: false, msg: '', bad: false }));
    onRemoved(fileId);
  };
  // The block's Clear (report 6bc596bb) removes its documents without this bin: once none is left, the last
  // reading's line and a question still waiting go too, as after the bin, unless a document is being read now.
  useEffect(() => {
    if (mine.length) return;
    setAsk(null);
    setSt(s => (s.busy || !s.msg ? s : { busy: false, msg: '', bad: false }));
  }, [mine.length]);
  if (ro && !mine.length) return null;
  return (
    <div style={{ display: 'grid', gap: 6, justifyItems: 'end' }}>
      {!ro && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
          <span onMouseEnter={() => setOver(true)} onMouseLeave={() => setOver(false)} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" disabled={wait} onClick={() => input.current?.click()} style={{ ...btnG, padding: '6px 12px', fontSize: 12.5, display: 'inline-flex', alignItems: 'center', gap: 6, textAlign: 'right', cursor: wait ? 'default' : 'pointer' }}>
              <i className={`ti ${st.busy ? 'ti-loader' : plain ? 'ti-upload' : 'ti-sparkles'}`} aria-hidden />{label}
            </button>
            <input ref={input} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={e => pick(e.target.files?.[0])} />
            {!wait && <PasteBox onFile={take} />}
          </span>
          {st.msg && <span style={{ fontSize: 12.5, textAlign: 'right', color: st.bad ? '#B42318' : 'var(--text-tertiary)' }}>{st.msg}</span>}
        </div>
      )}
      {!ro && ask && (
        <div style={{ ...noteBox, display: 'grid', gap: 8, color: 'var(--text-primary)', border: '1px solid #D97706' }}>
          <span style={{ textAlign: 'right' }}>{ask.text}</span>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => reply(true)} style={{ ...btnG, padding: '6px 12px', fontSize: 12.5, background: 'var(--accent)', borderColor: 'var(--accent)', color: '#fff' }}>{ask.yes}</button>
            <button type="button" onClick={() => reply(false)} style={{ ...btnG, padding: '6px 12px', fontSize: 12.5 }}>{ask.no}</button>
          </div>
        </div>
      )}
      {mine.map((f: any) => (
        <div key={f.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap', fontSize: 12.5, minWidth: 0 }}>
          <i className="ti ti-file-text" aria-hidden style={{ color: 'var(--text-tertiary)' }} />
          <button type="button" onClick={() => ctl.open(f)} style={{ border: 'none', background: 'transparent', padding: 0, color: 'var(--accent)', cursor: 'pointer', fontSize: 12.5, textAlign: 'right', overflowWrap: 'anywhere' }}>{f.filename}</button>
          <span style={{ color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{[f.created_by_name, f.created_at ? fmtDateTime(f.created_at) : ''].filter(Boolean).join(' · ')}</span>
          {!ro && (
            <button type="button" title={t('pf_remove')} onClick={() => drop(f.id)}
              style={{ border: 'none', background: 'transparent', padding: 0, color: '#b91c1c', cursor: 'pointer', fontSize: 14 }}>
              <i className="ti ti-trash" aria-hidden />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

// A block's uploads stand in the page's left margin, outside the card, each at the height of the first row its
// reading fills (report 46a11e5a: "float the upload requests to the left-hand margin of the page so it's not
// included in the physical block … at the appropriate height for where the information is going to be filled
// in"; report 8347170a, opening #2: "Move all upload requests into the floating left column"), each with its paste
// box: a person's ID card beside the name row, the passport beside the ID / passport row and the address document
// beside the Residing address; the company documents beside the Company name; the property address's document
// beside the address, the title deed beside the Deed type, the house book beside the Unit no. and the Reservation
// Agreement beside the Building sqm, the Or.Chor.2 beside its number; an agreement beside its Agreement date (a
// split sale's beside each part's Price) and the lease document beside the Term. The column is one flow, not
// pins: each upload is given at least its row's height, so an untouched column stands exactly beside its rows,
// and one that has grown (a reading's line, a file listed under it) pushes the next down rather than over it.
// Measured, not styled: how much margin the page has beside the card (the form is centred, so that is the
// window's), and where each row's first box begins — measured again as the rows or the window change. With
// less than DOC_COL_MIN of margin (a small window) the uploads stay inside their block, after `rest`, where
// they were. `rows` = the rows the uploads stand beside, in the uploads' order; `rest` = what follows them inside
// the block (a buyer's parents, the land's area, a building's warranties…); `docs` = the uploads, in the rows'
// order — none for a split sale's Agreement card, whose uploads are each part's own, so no column is drawn. The
// column's containing block is the nearest positioned ancestor — the block (rowBox) the rows are in, or the card
// itself for a row of the card's own (card is positioned too) — so in a narrow window the uploads are drawn as
// that block's own grid rows; the column stands beside the nearest card (.pf-card: the block's own, or the block itself).
const DOC_GUTTER = 14;    // between the column and the card: the page's own gap between its cards
const DOC_PAGE_EDGE = 8;  // the column never comes nearer the page's edge than this
const DOC_COL_MAX = 440;  // wide enough for the longest upload's button with its paste box beside it
const DOC_COL_MIN = 160;  // with less margin than this the uploads stay inside the card
const DOC_LIFT = 2;       // an upload's button is ~5px shorter than a box: centred on the row's boxes
const DOC_ROW_GAP = 10;   // under each upload — the block's own gap — kept when it grows past its row
type DocCol = { top: number; right: number; width: number; pitch: number[] };
function MarginDocs({ rows, rest, docs }: { rows: React.ReactNode[]; rest?: React.ReactNode; docs: React.ReactNode[] }) {
  const els = useRef<(HTMLDivElement | null)[]>([]);
  const [col, setCol] = useState<DocCol | null>(null);
  useLayoutEffect(() => {
    const first = els.current[0];
    const block = first?.offsetParent;           // the nearest positioned ancestor, a rowBox or the card — the column's containing block
    const cardEl = block?.closest('.pf-card');   // the card the column stands beside: the block's own, or the block itself
    if (!first || !block || !cardEl) return undefined;
    const page = block.closest('.property-form') || document.documentElement;
    const measure = () => {
      const b = block.getBoundingClientRect();
      const c = cardEl.getBoundingClientRect();
      const width = Math.floor(Math.min(DOC_COL_MAX, c.left - page.getBoundingClientRect().left - DOC_GUTTER - DOC_PAGE_EDGE));
      if (width < DOC_COL_MIN) { setCol(null); return; }
      // Each row's first box, from the block's padding edge (what `top` is measured from).
      const tops = els.current.map(el => {
        const box = el && (el.querySelector('input, select') || el);
        return box ? Math.round(box.getBoundingClientRect().top - b.top - block.clientTop) : 0;
      });
      const borderRight = b.width - block.clientWidth - block.clientLeft;
      const next: DocCol = {
        top: tops[0] + DOC_LIFT,
        right: Math.round(b.right - borderRight - (c.left - DOC_GUTTER)),
        width,
        pitch: tops.map((t, i) => (i + 1 < tops.length ? Math.max(0, tops[i + 1] - t) : 0)),
      };
      setCol(prev => (prev && JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(block);
    ro.observe(page);
    return () => ro.disconnect();
  }, []);
  return (
    <>
      {rows.map((r, i) => <div key={i} ref={el => { els.current[i] = el; }}>{r}</div>)}
      {rest}
      {docs.length > 0 && (
        <div style={col ? { position: 'absolute', top: col.top, right: col.right, width: col.width, display: 'grid', alignContent: 'start' } : { display: 'grid', gap: DOC_ROW_GAP }}>
          {docs.map((d, i) => (
            <div key={i} style={col ? { minHeight: col.pitch[i] || undefined, paddingBottom: DOC_ROW_GAP, boxSizing: 'border-box' } : undefined}>{d}</div>
          ))}
        </div>
      )}
    </>
  );
}

// The Due Diligence report a form started (report 0c21500b), on the Questions card, folded or open, for staff: its number,
// which opens the report in a new tab for someone with that module. One deleted since is struck through, the
// house mark, with nothing to open: it is why no other is started.
type DdReportRef = { id: string; report_no: string; deleted?: boolean };
function DdReportLink({ report }: { report: DdReportRef }) {
  const { t } = useTranslation();
  const me = useStore(s => s.me);
  const can = me?.role === 'admin' || (me?.modules || []).includes('due_diligence');
  return (
    <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>
      {t('mod_due_diligence')}:{' '}
      {can && !report.deleted
        ? <a href={`/m/slf_property?v=due_diligence&open=${encodeURIComponent(report.id)}`} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', fontWeight: 700 }}>{report.report_no}</a>
        : <span style={report.deleted ? { textDecoration: 'line-through' } : undefined}>{report.report_no}</span>}
    </div>
  );
}

// ── The form itself: Questions · Buyer or Lessee · Seller · Property · Agreement ────────────────
// `cur` is the firm's currency code (its region settings), shown inside the price boxes. `spa` is the
// staff screen's hold on the Sale & Purchase Agreement (report 4ba8eca0): which form, how to save what
// is on screen first, and a count of its saves — the client's links hand none over, so the agreement
// under the boxes (SpaPanel) is drawn for staff only, as the Due Diligence question is (report 0c21500b). `poaPeople`
// is staff-only the same way: who the POA card's dropdowns offer (report dc39d5a0). So is `ddReport`, the report that question started.
function FormBody({ ctl, cur, spa, poaPeople, ddReport }: { ctl: Ctl; cur: string; spa?: SpaCtl; poaPeople?: PoaPeople; ddReport?: DdReportRef | null }) {
  const { t } = useTranslation();
  const ro = useContext(RO);
  const { data, set } = ctl;
  // The answers on screen right now — what a document's reading is counted against before it is
  // applied (the update itself re-checks the blanks on the latest answers).
  const live = useRef<any>(data);
  live.current = data;
  // The documents on the form right now: an agreement's reading looks for another agreement already there.
  const liveFiles = useRef<any[]>(ctl.files);
  liveFiles.current = ctl.files;

  const setTop = (patch: any) => set((d: any) => ({ ...d, ...patch }));
  // 'Are we doing a POA for the buyer?' answered Yes: each buyer's blank Age is worked out from the date of birth its
  // block kept (report 06eee624, poaAges), as a reading made after the answer would have filled it.
  const setDoPoa = (v: string) => set((d: any) => poaAges({ ...d, doPoa: v }).next);
  const setRow = (key: 'buyers' | 'sellers', i: number, patch: any) => set((d: any) => {
    const l = d[key].slice(); l[i] = { ...l[i], ...patch }; return { ...d, [key]: l };
  });
  const addRow = (key: 'buyers' | 'sellers', make: () => any) => set((d: any) => (d[key].length >= MAX_PEOPLE ? d : { ...d, [key]: d[key].concat([make()]) }));
  const removeRow = (key: 'buyers' | 'sellers', i: number) => set((d: any) => ({ ...d, [key]: d[key].filter((_: any, j: number) => j !== i) }));
  const setProp = (patch: any) => set((d: any) => ({ ...d, property: { ...d.property, ...patch } }));
  // A land unit typed → the m² worked out again from all of them, as DD's sDim does (report f7b09e0d).
  const setUnit = (k: string) => (v: string) => set((d: any) => {
    const p = { ...d.property, [k]: v };
    return { ...d, property: { ...p, landSqm: landAreaM2(p) } };
  });
  const setAgr = (patch: any) => set((d: any) => ({ ...d, agreement: { ...d.agreement, ...patch } }));
  // One payment block's figures changed: the card's own (''), or the Land or the Building block's when the sale
  // is split (report 0d7e130c). Each block works exactly as the one block does, against its own price only —
  // nothing is divided between the parts or added up across them.
  const setFigures = (k: PartKey, fn: (f: any) => any) => set((d: any) => ({ ...d, agreement: withFigures(d.agreement, k, fn(figuresOf(d.agreement, k))) }));
  // A new purchase price works each stage's % out again from its amount — the amounts are what is paid
  // (report e897c965). A stage with no amount, or no price to take a share of, keeps its % as it is.
  const setPrice = (k: PartKey) => (v: string) => setFigures(k, f => {
    const l = Array.isArray(f.stages) ? f.stages : [];
    const stages = hundredths(v)
      ? l.map((s: any) => (s && typeof s === 'object' && hundredths(s.amount) != null ? { ...s, pct: stagePct(v, s.amount) } : s))
      : l;
    return { ...f, price: v, stages };
  });
  // The number of stages decides how many stage rows there are; the rows beyond it go, so what is
  // kept is what the screen shows.
  const setStageCount = (k: PartKey) => (v: string) => setFigures(k, f => {
    const n = Math.max(0, Math.min(MAX_STAGES, parseInt(v, 10) || 0));
    const l = (Array.isArray(f.stages) ? f.stages : []).slice(0, n);
    while (l.length < n) l.push({ stage: '', pct: '', amount: '' });
    return { ...f, stageCount: v, stages: l };
  });
  // A typed % fills the amount and a typed amount fills the % (report e897c965), each against the
  // purchase price; emptying one empties the other. With no price yet, the other box keeps what it has.
  const setStage = (k: PartKey, i: number, patch: any) => setFigures(k, f => {
    const l = (Array.isArray(f.stages) ? f.stages : []).slice();
    while (l.length <= i) l.push({ stage: '', pct: '', amount: '' });
    const row = { ...l[i], ...patch };
    const price = f.price;
    if (hundredths(price)) {
      if ('pct' in patch) row.amount = stageAmount(price, patch.pct);
      else if ('amount' in patch) row.pct = stagePct(price, patch.amount);
    }
    l[i] = row;
    return { ...f, stages: l };
  });

  // The blocks (by uid; the Property address by PROP_ADDR) whose Post Code a document's reading filled. The
  // AI works the post code out from the address when the document prints none (a Thai ID card never does), so
  // that box asks to be checked until somebody changes it. Kept on this screen only — nothing about it is
  // saved with the form.
  const [postalToCheck, setPostalToCheck] = useState<Record<string, boolean>>({});
  const postalChecked = (rowUid: string) => () => setPostalToCheck(m => ({ ...m, [rowUid]: false }));

  // The blocks (by uid) whose Email was filled in from Contacts (report 33b5ec4e). No document carries an
  // email, so once a block's Company name — or a person's First and Last name — is filled in while its
  // Email box is blank, Contacts is asked for that name; the one email it holds fills the box, which asks to
  // be checked until somebody changes it, as the Post Code does. A company seller's ask carries its Tax ID,
  // and when Contacts has no answer for it the box gets the AI's pick among the addresses the system holds
  // for that company instead ('ai', report e2975736), which asks to be checked in its own words. A person's
  // ask carries the Middle name, and when Contacts has no answer for them the box gets the one address the
  // email history holds for that name ('history', report 540019ef), asking to be checked in its own words
  // too. Each block's name (and a company's Tax ID) is asked about once (`asked`), so an Email emptied by
  // hand stays empty until one of them changes. Opening a form asks too: the filled box is an answer like
  // any other and saves. The staff screen only — the client's links have no `contactEmail`, so nothing
  // from Contacts or the email history, and no guess, reaches a client.
  const [emailToCheck, setEmailToCheck] = useState<Record<string, '' | 'contacts' | 'ai' | 'history' | 'note'>>({});
  const emailChecked = (rowUid: string) => () => setEmailToCheck(m => ({ ...m, [rowUid]: '' }));
  const asked = useRef<Record<string, string>>({});
  const lookup = ro ? undefined : ctl.contactEmail;
  const wanted = lookup
    ? (['buyers', 'sellers'] as const).flatMap(key => (data[key] || []).map((r: any) => {
        const name = blank(r.email) ? contactName(key, r) : '';
        const tax = contactTax(key, r);
        // A person's Middle name (a company has none); it is part of `name`, so a new one asks again.
        const middle = tax === undefined ? String(r.middle ?? '').trim() : '';
        return { key, uid: String(r.uid), name, tax, middle, ask: tax === undefined ? name : `${name}\n${tax}` };
      }))
      .filter(w => w.name && asked.current[w.uid] !== w.ask)
    : [];
  const wantedKey = JSON.stringify(wanted.map(w => [w.uid, w.ask]));
  useEffect(() => {
    if (!lookup || !wanted.length) return;
    const h = setTimeout(() => {
      for (const w of wanted) {
        asked.current[w.uid] = w.ask;
        lookup(w.name, w.tax, w.middle).then(({ email, guess, history }) => {
          // Only while the block still has that name (and Tax ID) and a blank Email (the update re-checks the blank).
          const cur = (live.current?.[w.key] || []).find((r: any) => r.uid === w.uid);
          if (!email || !cur || !blank(cur.email) || contactName(w.key, cur) !== w.name || contactTax(w.key, cur) !== w.tax) return;
          set((d: any) => ({ ...d, [w.key]: d[w.key].map((r: any) => (r.uid === w.uid && blank(r.email) ? { ...r, email } : r)) }), true);
          setEmailToCheck(m => ({ ...m, [w.uid]: guess ? 'ai' : history ? 'history' : 'contacts' }));
        }).catch(() => { /* no answer, no fill */ });
      }
    }, 600);
    return () => clearTimeout(h);
  }, [wantedKey]); // eslint-disable-line

  // The hidden note on a person's stored document (report 73f2d29a, "Invisible ink"): a client's passport stored
  // in Our Documents can carry their residing address as a staff-only note (File Library → Hidden note), and the
  // staff form pulls it in. Once a block's ID / passport number (6 characters or more, spaces and dashes aside) or
  // its First and Last name is filled in — a passport's reading fills both, typing does too — the library is asked,
  // after a pause, for the note on that person's stored passport (any stored document of theirs, failing that).
  // The note is shown in amber under the person's Residing address. Its typed boxes (report 14486e36 — an address in
  // parts, a phone, an email) fill, with "Fill in from the note", every blank part of the Residing address and a blank
  // Email: the note's line 1, postcode and country go to their own boxes; its city and province go in the order an
  // address prints — line 1, Line 2, City, Post Code, Country — so with both given the city takes Line 2 and the
  // province the City box (how a reading of a document fills them, services/propertyForm.ts), and one alone takes
  // the City box. A note written as free text before the boxes goes into a blank Address Line 1 on one line with
  // "Use as residing address". The boxes filled then ask to be checked until somebody changes them, as the Post
  // Code does. Each block's number and name are asked about once (`noteAsked`), as the Email's are: a changed number
  // or name asks again, one emptied takes the note off, and a block cleared starts afresh. Nothing is saved with the
  // form but the answers themselves, as typing would save them. The staff screen only — the client's links have no
  // `docNote`, so a note never reaches a client. A company seller has no passport: not asked.
  const [docNotes, setDocNotes] = useState<Record<string, { note: string; file: string; passport: boolean; fields?: FileNoteFields }>>({});
  const [noteToCheck, setNoteToCheck] = useState<Record<string, boolean>>({});
  const noteChecked = (rowUid: string) => () => setNoteToCheck(m => ({ ...m, [rowUid]: false }));
  const noteAsked = useRef<Record<string, string>>({});
  const dropDocNote = (rowUid: string) => setDocNotes(m => { if (!m[rowUid]) return m; const next = { ...m }; delete next[rowUid]; return next; });
  const noteLookup = ro ? undefined : ctl.docNote;
  const noteWanted = noteLookup
    ? (['buyers', 'sellers'] as const).flatMap(key => (data[key] || []).filter((r: any) => contactTax(key, r) === undefined).map((r: any) => {
        const idNo = idNoKey(r.idNo), first = String(r.first ?? '').trim(), last = String(r.last ?? '').trim();
        const ask = idNo.length >= 6 || (first && last) ? `${idNo}\n${first}\n${last}` : '';
        return { key, uid: String(r.uid), idNo, first, last, ask };
      }))
      .filter(w => noteAsked.current[w.uid] !== w.ask && (w.ask || noteAsked.current[w.uid] !== undefined))
    : [];
  const noteWantedKey = JSON.stringify(noteWanted.map(w => [w.uid, w.ask]));
  useEffect(() => {
    if (!noteLookup || !noteWanted.length) return;
    const h = setTimeout(() => {
      for (const w of noteWanted) {
        noteAsked.current[w.uid] = w.ask;
        if (!w.ask) { dropDocNote(w.uid); continue; }
        noteLookup(w.idNo, w.first, w.last).then(r => {
          // Only while the block still asks the same: one changed meanwhile has asked again on its own.
          if (noteAsked.current[w.uid] !== w.ask) return;
          if (r.note) setDocNotes(m => ({ ...m, [w.uid]: r })); else dropDocNote(w.uid);
        }).catch(() => { /* no answer, no note */ });
      }
    }, 600);
    return () => clearTimeout(h);
  }, [noteWantedKey]); // eslint-disable-line
  // What a note has for the form's boxes: its typed boxes mapped onto the address parts as described above (a country
  // only from the form's own list) and its Email address when it is one — or its free text on one line for Line 1.
  const noteFill = (n: { note: string; fields?: FileNoteFields }): { address: Record<string, string>; email: string } => {
    const f = n.fields;
    if (!f) return { address: { line1: n.note.replace(/\s+/g, ' ').trim() }, email: '' };
    const s = (v: any) => String(v ?? '').trim();
    const city = s(f.address?.city), province = s(f.address?.province), country = s(f.address?.country).toLowerCase();
    return {
      address: { line1: s(f.address?.line1), line2: province ? city : '', city: province || city, postal: s(f.address?.postcode), country: COUNTRIES.find(c => c.toLowerCase() === country) || '' },
      email: isEmail(f.email) ? s(f.email) : '',
    };
  };
  // Whether the note still has something for a blank box of this person's: the button shows while it does.
  const noteCanFill = (r: any, n: { note: string; fields?: FileNoteFields }): boolean => {
    const want = noteFill(n), a = normAddress(r.address);
    return ADDRESS_KEYS.some(k => blank(a[k]) && !!want.address[k]) || (blank(r.email) && !!want.email);
  };
  // "Fill in from the note" / "Use as residing address": the note into the person's blank boxes, each asked to be checked.
  const applyDocNote = (key: 'buyers' | 'sellers', rowUid: string) => () => {
    const n = docNotes[rowUid], row = (data[key] || []).find((r: any) => r.uid === rowUid);
    if (!n || !row) return;
    const want = noteFill(n), a = normAddress(row.address), address = { ...a };
    let addressFilled = false;
    for (const k of ADDRESS_KEYS) if (blank(a[k]) && want.address[k]) { address[k] = want.address[k]; addressFilled = true; }
    const emailFilled = blank(row.email) && !!want.email;
    if (!addressFilled && !emailFilled) return;
    set((d: any) => ({ ...d, [key]: d[key].map((r: any) => (r.uid !== rowUid ? r : { ...r, ...(addressFilled ? { address } : {}), ...(emailFilled ? { email: want.email } : {}) })) }));
    if (addressFilled) setNoteToCheck(m => ({ ...m, [rowUid]: true }));
    if (emailFilled) setEmailToCheck(m => ({ ...m, [rowUid]: 'note' }));
  };

  // The upload under the pointer and the boxes its reading fills (report fefad31c): each of those boxes is
  // filled light red until the pointer leaves the upload. `block` is the buyer's or seller's uid, else the part of the
  // form the boxes are in ('property', the agreement's upload path, 'lease'); an address part is
  // 'address.<part>', as a reading names it. Kept on this screen only.
  const [hover, setHover] = useState<{ block: string; keys: string[] } | null>(null);
  const hoverOn = (block: string, keys: string[]) => (on: boolean) => setHover(on ? { block, keys } : null);
  const lit = (block: string, k: string): boolean => !!hover && hover.block === block && hover.keys.indexOf(k) >= 0;
  const litAddress = (block: string): string[] => ADDRESS_KEYS.filter(k => lit(block, `address.${k}`));

  // A reading of one of a person's documents (PERSON_DOCS) → that block's blanks (found by uid, not
  // position): the boxes that document fills, with the residing address for the ID card and the address
  // document. Those two fill both parents' names as well where the document names them (a house
  // registration, a birth certificate, an ID back page; report f6b1169a) — but only where they are on
  // screen: a buyer's block while a POA is being done, which is the only place the form asks for them.
  const personKeys = (key: 'buyers' | 'sellers', doc: PersonDoc, poa: boolean): string[] =>
    doc.keys.concat(doc.parents && key === 'buyers' && poa ? PARENT_KEYS : []);
  // A buyer's ID card or passport gives the date of birth too (report 06eee624): kept on the block as a blank box is
  // filled (`birthDate`, never shown), and — while a POA is being done — the buyer's blank Age in the POA section is
  // worked out from the block's date of birth (poaAges), one more box filled. `auto` = the screen read it by itself (rereadBirth below).
  const readPerson = (key: 'buyers' | 'sellers', rowUid: string, doc: PersonDoc, auto?: boolean) => (fields: Record<string, string>): { n: number; postal: boolean } => {
    const keys = personKeys(key, doc, live.current?.doPoa === 'Yes');
    const birth = key === 'buyers' && doc.birth && isDay(fields.birthDate) ? String(fields.birthDate).trim() : '';
    const fill = (r: any) => {
      const own = doc.address ? fillWithAddress(r, fields, keys) : { ...fillBlanks(r, fields, keys), postal: false };
      return birth && blank(r.birthDate) ? { ...own, next: { ...own.next, birthDate: birth } } : own;
    };
    // The whole form with the block read in, then the buyer's blank Age.
    const read = (d: any): { next: any; n: number } => {
      const next = { ...d, [key]: d[key].map((r: any) => (r.uid === rowUid ? fill(r).next : r)) };
      return key === 'buyers' ? poaAges(next, rowUid) : { next, n: 0 };
    };
    const cur = (live.current?.[key] || []).find((r: any) => r.uid === rowUid);
    const got = cur ? fill(cur) : { n: 0, postal: false, next: cur };
    const aged = cur ? read(live.current).n : 0;
    const kept = !!cur && got.next.birthDate !== cur.birthDate;
    if (got.n || aged || kept) set((d: any) => read(d).next, auto);
    if (got.postal) setPostalToCheck(m => ({ ...m, [rowUid]: true }));
    return { n: got.n + aged, postal: got.postal };
  };
  // What the highlight shows for one of them: the boxes its reading fills on screen now, and — on the staff
  // screen — the Email, which Contacts is asked for once the reading has filled the name; a buyer's ID card and
  // passport light the POA section's Age too while it shows (report 06eee624).
  const personFills = (key: 'buyers' | 'sellers', doc: PersonDoc): string[] => personKeys(key, doc, data.doPoa === 'Yes')
    .concat(doc.address ? ADDRESS_PATHS : [], doc.keys.length && lookup ? ['email'] : [], key === 'buyers' && doc.birth && data.doPoa === 'Yes' ? ['poa.age'] : []);
  // A reading of a company seller's company documents → that seller's blanks (by uid too).
  const readCompany = (rowUid: string) => (fields: Record<string, string>): { n: number; postal: boolean } => {
    const cur = (live.current?.sellers || []).find((r: any) => r.uid === rowUid);
    const got = cur ? fillWithAddress(cur, fields, COMPANY_KEYS) : { n: 0, postal: false };
    if (got.n) set((d: any) => ({ ...d, sellers: d.sellers.map((r: any) => (r.uid === rowUid ? fillWithAddress(r, fields, COMPANY_KEYS).next : r)) }));
    if (got.postal) setPostalToCheck(m => ({ ...m, [rowUid]: true }));
    return { n: got.n, postal: got.postal };
  };
  const readProperty = (keys: string[]) => (fields: Record<string, string>): { n: number } => {
    const n = fillBlanks(live.current?.property, fields, keys).n;
    if (n) set((d: any) => ({ ...d, property: fillBlanks(d.property, fields, keys).next }));
    return { n };
  };
  // The title deed's: the same, with the m² worked out again from any land unit it fills (fillDeed).
  const readDeed = (fields: Record<string, string>): { n: number } => {
    const n = fillDeed(live.current?.property, fields).n;
    if (n) set((d: any) => ({ ...d, property: fillDeed(d.property, fields).next }));
    return { n };
  };
  // The Property address's document (report 3904c89f) — the house book, or any other document that gives the
  // address → the address's blank parts, with a Post Code it fills asked to be checked as a person's is; and a
  // blank House book number too, while the Building block that asks for it is on screen.
  const bookShown = (): boolean => (live.current?.sellers || []).some((s: any) => BUILDING_TYPES.indexOf(String(s?.propType || '')) >= 0);
  const readAddress = (fields: Record<string, string>): { n: number; postal: boolean } => {
    const keys = bookShown() ? ['houseBookNo'] : [];
    const got = fillWithAddress(live.current?.property, fields, keys);
    if (got.n) set((d: any) => ({ ...d, property: fillWithAddress(d.property, fields, keys).next }));
    if (got.postal) setPostalToCheck(m => ({ ...m, [PROP_ADDR]: true }));
    return { n: got.n, postal: got.postal };
  };
  // A document removed from a block (report 1d398933) → the boxes that kind of document fills go blank,
  // found by uid as the reading found them, and the change saves as typing does. A Post Code that goes
  // with them no longer asks to be checked.
  const clearRow = (key: 'buyers' | 'sellers', rowUid: string, keys: string[]) => () => {
    set((d: any) => ({ ...d, [key]: d[key].map((r: any) => (r.uid === rowUid ? clearWithAddress(r, keys) : r)) }));
    setPostalToCheck(m => ({ ...m, [rowUid]: false }));
  };
  // One of a person's documents removed (PERSON_DOCS): only the boxes that kind fills go blank — a passport
  // takes no address back, an address document no name. A box another of the block's documents still on the
  // form fills stays, as the House book number does while the Building block's house book is there; the
  // parents' names stay, as removing the ID always left them. A buyer's kept date of birth (report 06eee624) goes
  // with the last of its ID card and passport, so the next one's reading gives it afresh; the POA's Age stays, as
  // the parents' names do.
  const clearPersonDoc = (key: 'buyers' | 'sellers', rowUid: string, doc: PersonDoc) => () => {
    const others = PERSON_DOCS.filter(o => o !== doc && liveFiles.current.some((f: any) => f.path === personDocPath(o, rowUid)));
    const keys = doc.keys.filter(k => !others.some(o => o.keys.indexOf(k) >= 0))
      .concat(key === 'buyers' && doc.birth && !others.some(o => o.birth) ? ['birthDate'] : []);
    const address = doc.address && !others.some(o => o.address);
    if (!keys.length && !address) return;
    set((d: any) => ({ ...d, [key]: d[key].map((r: any) => (r.uid !== rowUid ? r : address ? clearWithAddress(r, keys) : clearKeys(r, keys))) }));
    if (address) setPostalToCheck(m => ({ ...m, [rowUid]: false }));
  };
  const clearProperty = (keys: string[]) => () => set((d: any) => ({ ...d, property: clearKeys(d.property, keys) }));
  // The Building block's two uploads, the house book and the Reservation Agreement (report 6ae27545): removing one
  // takes back its boxes, except those the other one, still on the form, fills too — as a person's documents do.
  const clearBuilding = (keys: string[], otherPath: string, otherKeys: string[]) => () => {
    const kept: string[] = liveFiles.current.some((f: any) => f.path === otherPath) ? otherKeys : [];
    const l = keys.filter(k => kept.indexOf(k) < 0);
    if (l.length) clearProperty(l)();
  };
  // The Property address's document: the whole address, as a person's ID takes back theirs, and the House book
  // number while the Building block is on screen — unless the Building block's own house book is still on the
  // form, which gives that number too.
  const clearAddress = () => {
    const keys = bookShown() && !liveFiles.current.some((f: any) => f.path === 'houseBook') ? ['houseBookNo'] : [];
    set((d: any) => ({ ...d, property: clearWithAddress(d.property, keys) }));
    setPostalToCheck(m => ({ ...m, [PROP_ADDR]: false }));
  };
  // An agreement's reading (report eaf3b006) → the Agreement's blanks, as every reading fills. When another
  // agreement is already on the form and the purchase price holds a figure, it first asks whether to combine
  // the totals (the reporter: a land lease and a building sale): Yes adds this agreement's price and stages
  // to the figures there, No keeps them. Stages past the form's MAX_STAGES are reported, never kept.
  // A part's own upload (`k`, the sale split — report 0d7e130c) is read into that part's block only, its own
  // fees box with it (report 7dc394ad), and another agreement means one already uploaded to that part.
  const money = (v: any): string => [grouped(String(v ?? '')), cur].filter(Boolean).join(' ');
  const readAgreement = (k: PartKey) => (fields: Record<string, string>, file: any): Read => {
    const r = agrRead(fields);
    const over = (n: number): string => (n > 0 ? t('pf_agr_over', { n, max: MAX_STAGES }) : '');
    const a = figuresOf(live.current?.agreement, k);
    const other = liveFiles.current.some((f: any) => f.path === agrPath(k) && f.id !== file?.id);
    if (!(other && hundredths(a.price) && hundredths(r.price))) {
      const got = fillAgreement(a, r);
      if (got.n) setFigures(k, f => fillAgreement(f, r).next);
      return { n: got.n, msg: got.over ? `${t('pf_ai_filled', { n: got.n })} ${over(got.over)}` : undefined };
    }
    return { n: 0, ask: {
      text: t('pf_agr_combine_q', { price: money(a.price), add: money(r.price) }),
      yes: t('pf_agr_combine_yes'), no: t('pf_agr_combine_no'),
      answer: (yes: boolean): Read => {
        if (yes) {
          const got = addAgreement(figuresOf(live.current?.agreement, k), r);
          setFigures(k, f => addAgreement(f, r).next);
          return { n: got.added, msg: [t('pf_agr_combined', { price: money(got.next.price), n: got.added }), over(got.over)].filter(Boolean).join(' ') };
        }
        const got = feesOnly(figuresOf(live.current?.agreement, k), r);
        if (got.n) setFigures(k, f => feesOnly(f, r).next);
        return { n: got.n, msg: [t('pf_agr_kept'), got.n ? t('pf_ai_filled', { n: got.n }) : ''].filter(Boolean).join(' ') };
      },
    } };
  };
  // Removing an agreement takes back every box an agreement's reading fills: the price, the stages and the fees.
  // A part's takes back that part's figures and fees only, its conditions staying — no reading fills them. The tick
  // and the parts' blocks are no box of the one block's, so removing its agreement leaves them as they are. The one
  // block's blanks the card's Agreement date as well, which a New form fills again with its default (report 90208a98).
  const clearAgreement = (k: PartKey) => () => set((d: any) => ({ ...d, agreement: k
    ? withFigures(d.agreement, k, { ...figuresOf(d.agreement, k), price: '', stageCount: '', stages: [], feesPaidBy: '' })
    : { ...d.agreement, ...emptyAgreement(), separate: d.agreement.separate, parts: d.agreement.parts } }));

  // The particular Buyer and Seller conditions (report 4ba8eca0): the count decides how many boxes
  // there are, and the boxes past it go, so what is kept is what the screen shows — the payment
  // stages' rule. Each one becomes a numbered clause of its own in the agreement. They are the block's,
  // as the fees are (report 7dc394ad): the card's own, or a split sale's part's.
  const setCondCount = (k: PartKey) => (v: string) => setFigures(k, f => {
    const n = Math.max(0, Math.min(MAX_CONDS, parseInt(v, 10) || 0));
    const l = (Array.isArray(f.conds) ? f.conds : []).slice(0, n);
    while (l.length < n) l.push('');
    return { ...f, condCount: v, conds: l };
  });
  const setCond = (k: PartKey, i: number, v: string) => setFigures(k, f => {
    const l = (Array.isArray(f.conds) ? f.conds : []).slice();
    while (l.length <= i) l.push('');
    l[i] = v;
    return { ...f, conds: l };
  });

  const opts = (l: [string, string][]) => l.map(([value, key]) => ({ value, label: t(key) }));
  const yesNo = [{ value: 'Yes', label: t('pf_yes') }, { value: 'No', label: t('pf_no') }];
  const titleOpts = TITLES.map(v => ({ value: v, label: v }));
  const deedOpts = DEED_TYPES.map(v => ({ value: v, label: v }));
  const countryOpts = COUNTRIES.map(v => ({ value: v, label: v }));
  const countOpts = Array.from({ length: MAX_STAGES }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }));
  const warrantyOpts = [{ value: 'None', label: t('pf_warranty_none') }]
    .concat(Array.from({ length: WARRANTY_YEARS }, (_, i) => ({ value: String(i + 1), label: t(i ? 'pf_warranty_years' : 'pf_warranty_year', { n: i + 1 }) })));

  const types: string[] = (data.sellers || []).map((s: any) => String(s.propType || ''));
  const hasLand = types.some(x => LAND_TYPES.indexOf(x) >= 0);
  const landUnits = landAreaUnits();
  const hasBuilding = types.some(x => BUILDING_TYPES.indexOf(x) >= 0);
  // One condominium unit has its own title (the Or.Chor.2) and no house book (report 4ba8eca0).
  const hasCondo = types.indexOf('Condominium') >= 0;
  const agr = data.agreement || {};
  // One payment block (k '') — or, when the sale is split, the Land block and the Building block (report 0d7e130c):
  // Purchase price and Number of payment stages, the stage rows and the total underneath, each against its own price.
  const split = saleSplits(data);
  // The boxes an agreement's upload rings, in the payment block named by its upload path: the price, the count, every
  // stage row and the fees box — a split sale's part's own fees box too (report 7dc394ad).
  const agrFills = ['price', 'stageCount', 'stages', 'feesPaidBy'];
  // When the part transfers (report 8aa83b74): under the block's totals, a row for the part — a split sale's Land block
  // the land's, its Building block the building's; the one block one row for each part the one sale transfers
  // (transferParts). Beside Other alone, the Progress payment pick (report bb4ea111: "let the user choose the progress
  // payment for the land table (if different or separate from the building table)") lists the block's own stages —
  // 'Payment stage 2: Deposit (30.00%)', the % the row's own or worked out from its amount — and keeps the one picked by
  // its position; red until one is picked. A Percentage paid typed before the pick still prints until then, and the
  // line under the pick says so.
  const transferRow = (k: PartKey, part: 'land' | 'building') => {
    const choiceKey = `${part}Transfer`;
    const stageKey = `${part}TransferStage`;
    const choice: string = agr[choiceKey] || TRANSFER_DEFAULT[part];
    const f = figuresOf(agr, k);
    const n = stageCountOf(f);
    const rows: any[] = Array.isArray(f.stages) ? f.stages : [];
    const stageOpts = Array.from({ length: n }, (_, i) => {
      const s = rows[i] || {};
      const name = STAGES.find(([v]) => v === s.stage)?.[1];
      const pct = s.pct || stagePct(f.price, s.amount);
      return { value: String(i + 1), label: `${t('pf_stage_n', { n: i + 1 })}${name ? `: ${t(name)}` : ''}${pct ? ` (${pct}%)` : ''}` };
    });
    const picked = stageOpts.some(o => o.value === String(agr[stageKey] ?? '').trim());
    const typed: string = agr[`${part}TransferPct`] || '';
    const missing = transferPctMissing('Other', agr[stageKey], typed, n);
    return (
      <div key={part} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Pick label={t(part === 'land' ? 'pf_transfer_land' : 'pf_transfer_building')} value={choice} onChange={v => setAgr({ [choiceKey]: v })} options={opts(TRANSFER_AT)} />
        {choice === 'Other' && (
          <div>
            <Pick label={t('pf_transfer_stage')} value={String(agr[stageKey] ?? '')} onChange={v => setAgr({ [stageKey]: v })} options={stageOpts} bad={missing} />
            {!picked && !missing && <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 3 }}>{t('pf_transfer_pct_kept', { pct: typed })}</div>}
          </div>
        )}
      </div>
    );
  };
  // The agreement's figures: the Price and Number of stages row first, then the stages, their totals and when each
  // part transfers — given apart, so that a split sale's Upload Agreement can stand beside its part's Price row (MarginDocs).
  const figuresRows = (k: PartKey): { first: React.ReactNode; rest: React.ReactNode } => {
    const f = figuresOf(agr, k);
    const n = Math.max(0, Math.min(MAX_STAGES, parseInt(f.stageCount, 10) || 0));
    const l: any[] = Array.isArray(f.stages) ? f.stages : [];
    const at = agrPath(k);
    return {
      first: (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <MoneyBox label={t('pf_price')} cur={cur} value={f.price} onChange={setPrice(k)} hi={lit(at, 'price')} />
          <Pick label={t('pf_stage_count')} value={f.stageCount} onChange={setStageCount(k)} options={countOpts} hi={lit(at, 'stageCount')} />
        </div>
      ),
      rest: (
        <>
          {Array.from({ length: n }, (_, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: STAGE_COLS, gap: 10 }}>
              <Pick label={t('pf_stage_n', { n: i + 1 })} value={l[i]?.stage || ''} onChange={v => setStage(k, i, { stage: v })} options={opts(STAGES)} hi={lit(at, 'stages')} />
              <PctBox label={t('pf_pct')} value={l[i]?.pct || ''} onChange={v => setStage(k, i, { pct: v })} hi={lit(at, 'stages')} />
              <MoneyBox label={t('pf_amount')} cur={cur} value={l[i]?.amount || ''} onChange={v => setStage(k, i, { amount: v })} hi={lit(at, 'stages')} />
            </div>
          ))}
          {n > 0 && <StageTotals price={f.price} stages={l.slice(0, n)} cur={cur} />}
          {(k ? [k] : transferParts(data)).map(part => transferRow(k, part))}
        </>
      ),
    };
  };
  const figuresBlock = (k: PartKey) => { const f = figuresRows(k); return <>{f.first}{f.rest}</>; };
  // Under the block's figures, who pays the transfer fees and up to five particular Buyer or Seller conditions — none is
  // an answer of its own, and the agreement then says there are none (report 4ba8eca0): the card's own, or, when the
  // sale is split, each part's under its own block, as each part's agreement prints its own (report 7dc394ad). Each
  // condition box has the AI's 'Write a better clause' beside it on the staff screen (report c96a33b3).
  const feesRow = (k: PartKey) => (
    <Pick label={t('pf_fees_by')} value={figuresOf(agr, k).feesPaidBy} onChange={v => setFigures(k, f => ({ ...f, feesPaidBy: v }))} options={opts(FEES_BY)} hi={lit(agrPath(k), 'feesPaidBy')} />
  );
  const condRows = (k: PartKey) => {
    const f = figuresOf(agr, k);
    const n = Math.max(0, Math.min(MAX_CONDS, parseInt(f.condCount, 10) || 0));
    const conds: string[] = Array.isArray(f.conds) ? f.conds : [];
    return (
      <>
        <Pick label={t('pf_cond_count')} opt value={f.condCount} onChange={setCondCount(k)} options={opts(COND_COUNTS)} />
        {Array.from({ length: n }, (_, i) => (
          <CondBox key={i} label={t('pf_cond_n', { n: i + 1 })} value={conds[i] || ''} onChange={v => setCond(k, i, v)} improve={ro ? undefined : ctl.improveClause} />
        ))}
      </>
    );
  };
  // The Lease section (report eab2c428): its answers live in data.lease. The Total area starts as the
  // land's m² (the building's for a building or condominium lease) until somebody types another — beside Part
  // only while that m² is larger than the Portion area (report 90a529be): otherwise it is blank, to be typed.
  // A Total area that is not larger shows red, as missingBoxes asks for it (leaseTotalTooSmall).
  const lease: any = data.lease || {};
  const setLease = (patch: any) => set((d: any) => ({ ...d, lease: { ...(d.lease || {}), ...patch } }));
  // The POA section's answers (emptyPoa): the section's own, one buyer's, one seller's Thai name, the attorney's, the place names'.
  const poa = data.poa || emptyPoa();
  const setPoa = (patch: any) => set((d: any) => ({ ...d, poa: { ...(d.poa || {}), ...patch } }));
  const setPoaIn = (group: 'grantors' | 'sellersTh' | 'donee' | 'placeTh', patch: any) =>
    set((d: any) => ({ ...d, poa: { ...(d.poa || {}), [group]: { ...((d.poa && d.poa[group]) || {}), ...patch } } }));
  const setGrantor = (id: string, patch: any) => set((d: any) => {
    const p = d.poa || {};
    const gs = p.grantors || {};
    return { ...d, poa: { ...p, grantors: { ...gs, [id]: { ...(gs[id] || {}), ...patch } } } };
  });
  // The Attorney and Witness dropdowns (report dc39d5a0, poaOffer above). `poaOther` = 'Other' chosen on this screen, never
  // saved: what is typed shows as Other by itself, and boxes left blank show the default person again.
  const [poaOther, setPoaOther] = useState<Record<string, boolean>>({});
  const attorneys = poaOffer(poaPeople?.attorneys || [], poaPeople ? [poaPeople.donee] : []);
  const witnessList = poaOffer(poaPeople?.witnessList || [], poaPeople?.witnesses || []);
  const doneeNow = poa.donee || {};
  const attorneyPrints = blank(doneeNow.name) && blank(doneeNow.nameTh) ? poaPeople?.donee.name : { en: doneeNow.name, th: doneeNow.nameTh };
  const attorneyPick = (dn: any): string => poaPick(attorneys, !!poaOther.donee, DONEE_BOXES.every(k => blank(dn[k])), poaPeople?.donee.name, a => holdsAttorney(dn, a));
  const attorneyVal = attorneyPick(doneeNow);
  // What the POA prints as the attorney's age (formPoaReport in server/src/services/propertyPoa.ts — keep the two in step): the
  // block's Age, else worked out from the date of birth their card gave; with neither, the firm's default attorney's, from
  // the date of birth on their entry in the list, else stored. Shown beside the dropdown, which hides the Age box.
  const attorneyAge = String(doneeNow.age || ageOn(doneeNow.birthDate, ageDay(data))
    || ageOn(attorneys.find(a => samePerson(a.name, poaPeople?.donee.name))?.birthDate, ageDay(data)) || poaPeople?.donee.age || '');
  const witnessBoxes = (n: number) => ({ en: poa[`witness${n}`], th: poa[`witness${n}Th`] });
  const witnessBlank = (n: number) => blank(poa[`witness${n}`]) && blank(poa[`witness${n}Th`]);
  const witnessPrints = (n: number) => (witnessBlank(n) ? poaPeople?.witnesses[n - 1]?.name : witnessBoxes(n));
  const witnessVal = (n: number) => poaPick(witnessList, !!poaOther[n], witnessBlank(n), poaPeople?.witnesses[n - 1]?.name,
    w => poaKey(poa[`witness${n}`]) === poaKey(w.name.en) && poaKey(poa[`witness${n}Th`]) === poaKey(w.name.th));
  // The attorney cannot also be a witness (owner, 2026-10-27), nor one person both witnesses, so neither is offered.
  // The person the dropdown already shows stays in it, as what shows must be what prints; a line under it says to change them.
  const witnessClash = (n: number) => samePerson(witnessPrints(n), attorneyPrints) || samePerson(witnessPrints(n), witnessPrints(3 - n));
  const witnessOpts = (n: number) => witnessList
    .map((w, i) => ({ value: String(i), label: poaLabel(w), taken: samePerson(w.name, attorneyPrints) || samePerson(w.name, witnessPrints(3 - n)) }))
    .filter(o => !o.taken || o.value === witnessVal(n))
    .map(({ value, label }) => ({ value, label }))
    .concat([{ value: POA_OTHER, label: t('pf_poa_other') }]);
  // '—' empties the boxes again, so the firm's default person prints and shows. A witness box naming the new attorney goes
  // back to its default too. A birth date works the age out on the Agreement date; without one the stored age is filled.
  const pickAttorney = (v: string) => {
    if (v === POA_OTHER) { setPoaOther(o => ({ ...o, donee: true })); return; }
    const a = v ? attorneys[Number(v)] : null;
    const freed = [1, 2].filter(n => samePerson(witnessBoxes(n), a ? a.name : poaPeople?.donee.name));
    setPoaOther(o => ({ ...o, donee: false, ...Object.fromEntries(freed.map(n => [n, false])) }));
    set((d: any) => ({ ...d, poa: { ...(d.poa || {}),
      donee: { ...((d.poa && d.poa.donee) || {}), ...(a ? attorneyBoxes(a, d) : emptyPoa().donee) },
      ...Object.fromEntries(freed.flatMap(n => [[`witness${n}`, ''], [`witness${n}Th`, '']])),
    } }));
  };
  // The Attorney block's upload read (report beb72eec, doneeRead). The card is for the person the dropdown showed as it was
  // given: blank boxes show the firm's default attorney, so they are filled as picking that person fills them. If somebody
  // chose another attorney while it was being read it is not theirs, and fills nothing. A listed attorney's entry
  // keeps the date while its own is blank, and the upload's line says the Age, as the dropdown hides its box.
  const attorneyOf = (v: string): any => (v && v !== POA_OTHER ? attorneys[Number(v)] : undefined);
  const readDonee = (fields: Record<string, string>, file: any): Read => {
    const birth = ageOn(fields.birthDate, ageDay(live.current)) ? String(fields.birthDate).trim() : '';
    if (!birth || attorneyPick(live.current?.poa?.donee || {}) !== attorneyVal) return { n: 0 };
    const a = attorneyOf(attorneyVal);
    const got = doneeRead(live.current, birth, String(file?.id || ''), a);
    set((d: any) => doneeRead(d, birth, String(file?.id || ''), a).next);
    if (a && blank(a.birthDate) && ctl.attorneyBirth) ctl.attorneyBirth(a.name, birth).catch(() => { /* the block keeps it; the next card tries the entry again */ });
    return got.typed ? { n: 0, msg: t('pf_poa_age_typed') } : { n: 1, msg: t('pf_poa_age_read', { age: got.next.poa.donee.age }) };
  };
  // Removing the card the date of birth came from takes it back, here and on the attorney's entry when the entry holds that
  // same date, so a wrong card can be put right; an Age it filled goes too, a listed attorney's back to what a pick
  // fills. Any other upload here (an earlier attorney's, kept when another was picked) has nothing to take back.
  const clearDonee = (fileId: string) => {
    const dn = live.current?.poa?.donee || {};
    if (!fileId || dn.birthFile !== fileId) return;
    const a = attorneys.find(x => holdsAttorney(dn, x));
    const kept = a && a.birthDate !== dn.birthDate ? String(a.birthDate || '') : '';
    if (a && !kept && ctl.attorneyBirth) ctl.attorneyBirth(a.name, '', dn.birthDate).catch(() => { /* removing the next card tries again */ });
    set((d: any) => {
      const was = (d.poa && d.poa.donee) || {};
      const on = ageDay(d);
      const filled = poaKey(was.age) === poaKey(ageOn(was.birthDate, on));
      return { ...d, poa: { ...(d.poa || {}), donee: { ...was, birthDate: kept, birthFile: '', age: !filled ? was.age : a ? ageOn(kept, on) || a.age : '' } } };
    });
  };
  const pickWitness = (n: number) => (v: string) => {
    setPoaOther(o => ({ ...o, [n]: v === POA_OTHER }));
    if (v === POA_OTHER) return;
    const w = v ? witnessList[Number(v)] : null;
    setPoa({ [`witness${n}`]: w ? w.name.en : '', [`witness${n}Th`]: w ? w.name.th : '' });
  };
  // One Made at dropdown (report 404df7d2, madePick above): the firm's wording for that language, set in Admin →
  // Companies, then 'Other', which shows the typed box under it. A blank box shows that wording and prints it
  // (formPoaReport in server/src/services/propertyPoa.ts, language by language — keep the two in step); the first
  // choice empties the box. With no wording yet, the dropdown's '—' stands in for that first choice. Typing keeps
  // 'Other' on, so a saved value emptied to be retyped keeps its box. A client's link gets no `poaPeople`, so keeps
  // the plain box, as for the witnesses.
  const madeBox = (k: 'madeAt' | 'madeAtTh', label: string, lang: 'en' | 'th') => {
    const box = <Txt label={label} opt value={poa[k]} onChange={v => { setPoaOther(o => ({ ...o, [k]: true })); setPoa({ [k]: v }); }} />;
    if (!poaPeople?.madeAt) return box;
    const firm = poaPeople.madeAt[lang];
    const val = madePick(!!poaOther[k], poa[k], firm);
    const first: { value: string; label: string }[] = blank(firm) ? [] : [{ value: POA_MADE_FIRM, label: firm }];
    const pick = (v: string) => {
      setPoaOther(o => ({ ...o, [k]: v === POA_OTHER }));
      if (v !== POA_OTHER) setPoa({ [k]: '' });
    };
    // The wording can be longer than the box, which cuts it off, so the whole of it shows on hover.
    return (
      <div style={{ minWidth: 0 }} title={val === POA_MADE_FIRM ? firm : undefined}>
        <Pick label={label} opt noDash={first.length > 0} value={val} onChange={pick} options={first.concat([{ value: POA_OTHER, label: t('pf_poa_other') }])} />
        {val === POA_OTHER && <div style={{ marginTop: 12 }}>{box}</div>}
      </div>
    );
  };
  // The property's sub-district, district and province as the POA prints them in English (placeNames in
  // server/src/services/propertyPoa.ts): Line 1 after its last comma, Line 2, City — each Thai box's placeholder.
  const poaPlaces = (() => {
    const a = data.property.address || {};
    const l1 = String(a.line1 || '');
    const cut = l1.lastIndexOf(',');
    return { subdistrict: cut >= 0 ? l1.slice(cut + 1).trim() : '', district: String(a.line2 || '').trim(), province: String(a.city || '').trim() };
  })();
  // The Thai boxes' change (LeaseThai), worked out from the lease as it is when the change is made.
  const updateLease = (fn: (l: any) => any) => set((d: any) => ({ ...d, lease: fn(d.lease || {}) }));
  const leaseAreaFill = String((hasLand ? data.property.landSqm : data.property.buildingSqm) || '');
  const leaseArea = lease.area || (leaseTotalTooSmall(lease, leaseAreaFill) ? '' : leaseAreaFill);
  const g3: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 };
  const yearOpts = Array.from({ length: 30 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }));
  const renewOpts = [{ value: 'None', label: t('pf_lease_none') }].concat(yearOpts);
  // The Step-up periods the Term and the Rent period allow (leaseStepChoices, report 805f7320). A saved one they
  // no longer allow shows blank and red, to be chosen again; it is kept as saved, never changed, until then.
  const stepChoices = leaseStepChoices(lease.term, lease.period);
  const stepOpts = [{ value: 'None', label: t('pf_lease_none') }].concat(stepChoices.map(n => ({ value: n, label: n === '1' ? t('pf_lease_every_year') : t('pf_lease_every_n', { n }) })));
  const stepOn = !!lease.stepEvery && lease.stepEvery !== 'None';
  const stepFits = !stepOn || stepChoices.indexOf(String(lease.stepEvery)) >= 0;
  // Rent per period, worked out from the Total rent: one figure for each step-up band (leaseRents).
  const rents = leaseRents(lease);
  // A Permitted use typed before it was a choice stays listed as typed, so it shows until a choice replaces it.
  const useOpts = opts(LEASE_USES).concat(lease.use && !LEASE_USES.some(([v]) => v === lease.use) ? [{ value: lease.use, label: lease.use }] : []);
  // The Bank is a choice from the country's banks (THAI_BANKS, report 4c63e9a1 opening #6: "Choose from a list of Thai
  // banks rather than allow the text box to be filled in"), each shown as 'English / Thai' in every language. A bank
  // typed before it was a choice shows as the entry it means (thaiBankOf: "KASIKORN BANK PCL, Maenam Koh Samui Branch"
  // is Kasikornbank, the branch dropped) or, when it means none of them, stays listed as typed until a choice replaces
  // it, as a Permitted use typed before does. The saved answer changes only when a bank is picked.
  const bankPick = thaiBankOf(lease.bank)?.en || String(lease.bank || '');
  const bankOpts = THAI_BANKS.map(b => ({ value: b.en, label: thaiBankLabel(b) })).concat(bankPick && !THAI_BANKS.some(b => b.en === bankPick) ? [{ value: bankPick, label: bankPick }] : []);
  // A lease document's reading → the Lease section's BLANKS, as every reading fills, and an Agreement date that is
  // blank or still its default (fillLease). A choice goes in only when it is one its box offers: the Term one of yearOpts, the Renewal one
  // of renewOpts (a renewal of 0 years is None), the Rent period, Whole or part, Registration fees/taxes and
  // Permitted use one of theirs, and the Step-up period one the Term and the Rent period allow once the
  // reading's own are in (fillLease); a Step-up % only above 0, and a Total area only when it is larger than the
  // Portion area (fillLease, report 90a529be). Every figure is copied as printed. Rent per period is not read:
  // the form works it out from the Total rent (leaseRents, docs/lease-blueprint.md §6 (e)). The Bank goes in only
  // as the listed bank the reading means (thaiBankOf), and the Account name by its box's rule (accountNameInFull) —
  // the server does both before the answers arrive (tidyReading); here again, as the screen's own rule.
  const leaseRead = (f: Record<string, string>): Record<string, string> => {
    const raw = (k: string): string => String(f?.[k] ?? '').trim();
    const one = (v: string, l: string[]): string => (l.indexOf(v) >= 0 ? v : '');
    const years = (k: string): string => { const s = leadNum(raw(k), true); return s ? String(Number(s)) : ''; };
    const day = (k: string): string => (isDay(raw(k)) ? raw(k) : '');
    const renewal = years('renewal');
    const stepPct = leadNum(raw('stepPct'));
    return {
      term: one(years('term'), yearOpts.map(o => o.value)),
      renewal: one(renewal === '0' ? 'None' : renewal, renewOpts.map(o => o.value)),
      agreementDate: day('agreementDate'),
      area: leadNum(raw('area')),
      extent: one(raw('extent'), LEASE_EXTENT.map(([v]) => v)),
      portionArea: leadNum(raw('portionArea')),
      regDate: day('regDate'),
      period: one(raw('period'), LEASE_PERIODS.map(([v]) => v)),
      totalRent: readMoney(raw('totalRent')),
      stepEvery: years('stepEvery'),
      stepPct: Number(stepPct) > 0 ? stepPct : '',
      accName: accountNameInFull(raw('accName')), bank: thaiBankOf(raw('bank'))?.en || '', accNo: raw('accNo'),
      regFees: one(raw('regFees'), LEASE_FEES.map(([v]) => v)),
      use: one(raw('use'), LEASE_USES.map(([v]) => v)),
      witness1: raw('witness1'), witness2: raw('witness2'),
    };
  };
  const readLease = (fields: Record<string, string>): Read => {
    const r = leaseRead(fields);
    const n = fillLease(live.current, r).n;
    if (n) set((d: any) => fillLease(d, r).next);
    return { n };
  };
  // Removing a lease document takes back every Lease box its reading fills. The Agreement date stays: it is
  // the Agreement card's own box.
  const clearLease = () => set((d: any) => ({ ...d, lease: clearKeys(d.lease, LEASE_KEYS) }));

  // ── Clear: one block back to blank in one go (report 6bc596bb) ──────────────────────────────────
  // The Clear on a block's heading empties every box of that block, whoever answered it, and removes the
  // documents uploaded to it (opening #2), once the operator says OK; the question counts them, when any. Its
  // documents are all those filed under its uploads' paths (`docs`), an upload's not on screen now too — a
  // title deed's once the Land block has gone. Each goes as its bin takes it, by the same route, so the form
  // keeps it on record as removed, with who and when; one that cannot be removed stays listed under its
  // upload. Then the boxes empty, and the change saves as typing does. A buyer or seller starts again as a
  // new one does but keeps its uid. The block's amber "please check" marks go with its answers, and a cleared
  // name typed again is asked of Contacts afresh, as a new block's is. The Agreement date is the Agreement
  // card's own box, which the Lease section shows too: the Agreement's Clear empties it; the Lease's leaves
  // it while the Agreement card is on screen, and empties it with the rest when the Lease section is the
  // only place it shows. An Agreement date emptied by a Clear takes its marker with it, so a New form fills it
  // again with the default (report 90208a98, defaultDates.ts). The Agreement's Clear also unticks 'Separate agreements
  // for land and building' and empties the Land and the Building blocks, with the agreements uploaded to each (report 0d7e130c).
  const blankQuestions = () => setTop({ doPoa: '', doWill: '', doAgreements: '', ...(spa ? { doDd: '' } : {}) });
  const blankRow = (key: 'buyers' | 'sellers', rowUid: string) => () => {
    const make = key === 'buyers' ? emptyBuyer : emptySeller;
    set((d: any) => ({ ...d, [key]: d[key].map((r: any) => (r.uid === rowUid ? { ...make(), uid: rowUid } : r)) }));
    setPostalToCheck(m => ({ ...m, [rowUid]: false }));
    setEmailToCheck(m => ({ ...m, [rowUid]: '' }));
    delete asked.current[rowUid];
    setNoteToCheck(m => ({ ...m, [rowUid]: false }));
    dropDocNote(rowUid);
    delete noteAsked.current[rowUid];
  };
  const blankProperty = () => {
    set((d: any) => ({ ...d, property: emptyProperty() }));
    setPostalToCheck(m => ({ ...m, [PROP_ADDR]: false }));
  };
  const blankAgreement = () => set((d: any) => ({ ...d, agreement: emptyAgreement() }));
  const blankLease = () => set((d: any) => ({ ...d, lease: {}, ...(d.doAgreements === 'Yes' ? {} : { agreement: { ...d.agreement, date: '', dateDefault: undefined } }) }));
  const clearBlock = async (block: string, docs: string[], onClear: () => void) => {
    const gone = ctl.files.filter((f: any) => docs.indexOf(f.path) >= 0);
    if (!window.confirm(t(gone.length ? 'pf_clear_confirm' : 'pf_clear_confirm_none', { block, n: gone.length }))) return;
    for (const f of gone) {
      try { await ctl.remove(f.id); } catch { /* it stays listed under its upload, with its bin */ }
    }
    onClear();
  };
  const clearBtn = (block: string, docs: string[], onClear: () => void) => (
    <button type="button" onClick={() => { clearBlock(block, docs, onClear); }}
      style={{ ...btnG, padding: '3px 9px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <i className="ti ti-eraser" aria-hidden />{t('pf_clear')}
    </button>
  );
  // ── Folding (report 5f753cef) ────────────────────────────────────────────────────────────────────
  // On the staff screen each card folds to its heading, by the arrow before its title: a folded card shows only
  // 'Complete', or how many of its boxes are still blank (missingBoxes, by block key). Which cards are folded is
  // this screen's own — never saved with the form. A card that still had blanks when focus came into it and has
  // none when focus leaves it folds by itself, and the next card on screen opens and scrolls into view: filled in,
  // folded, on to the next. The client's links (no `spa`, or read-only) keep every card open as before, and the
  // agreements under the cards (SpaAgreements) never fold.
  const foldable = !!spa && !ro;
  const [closed, setClosed] = useState<Set<BlockKey>>(() => new Set<BlockKey>());
  const shut = (k: BlockKey): boolean => foldable && closed.has(k);
  const toggle = (k: BlockKey) => setClosed(s => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const gaps = foldable ? missingBoxes(data, t) : [];
  const blockDone = (k: BlockKey): boolean => !missingBoxes(live.current, t).some(g => g.key === k);
  const blockId = (k: BlockKey) => `pf-block-${k}`;
  // The cards on screen, in order — the Agreement's and the Lease's only while they show.
  const blocksShown = (d: any): BlockKey[] => BLOCK_ORDER.filter(k => (k === 'agreement' ? d?.doAgreements === 'Yes' : k === 'lease' ? leaseApplies(d) : k === 'poa' ? d?.doPoa === 'Yes' : true));
  // Which card focus is in, and whether it still had blanks when focus came in.
  const entered = useRef<{ k: BlockKey; incomplete: boolean } | null>(null);
  // Where the pointer last went down: Safari's blur names no relatedTarget when a button is clicked, so the
  // pointer says where focus went. Forgotten once that click's blur has had it.
  const lastDown = useRef<EventTarget | null>(null);
  useEffect(() => {
    if (!foldable) return;
    const h = (e: MouseEvent) => { lastDown.current = e.target; setTimeout(() => { lastDown.current = null; }, 0); };
    document.addEventListener('mousedown', h, true);
    return () => document.removeEventListener('mousedown', h, true);
  }, [foldable]);
  const foldOn = (k: BlockKey): React.HTMLAttributes<HTMLDivElement> => (!foldable ? {} : {
    id: blockId(k),
    onFocusCapture: e => {
      const from = e.relatedTarget;
      if (from instanceof Node && e.currentTarget.contains(from)) return;   // moving within the card
      entered.current = { k, incomplete: !blockDone(k) };
    },
    onBlurCapture: e => {
      const to = e.relatedTarget || lastDown.current;
      if (to instanceof Node && e.currentTarget.contains(to)) return;   // still in the card
      const was = entered.current;
      entered.current = null;
      if (!was || was.k !== k || !was.incomplete || !blockDone(k)) return;
      const shown = blocksShown(live.current);
      const next = shown[shown.indexOf(k) + 1];
      setClosed(s => { const n = new Set(s); n.add(k); if (next) n.delete(next); return n; });
      // Then look at the card focus went to — else the next card, unless focus went to some other control
      // (the status, a link, the agreement's buttons) — once the fold has been drawn.
      const landed = to instanceof Element ? to.closest('[id^="pf-block-"]') : null;
      const control = to instanceof Element && !!to.closest('button, input, select, textarea, a, [tabindex]');
      const target = landed ? landed.id : !control && next ? blockId(next) : '';
      if (target) setTimeout(() => { document.getElementById(target)?.scrollIntoView({ block: 'start', behavior: 'smooth' }); }, 0);
    },
  });
  // A section's heading: the fold arrow before its title on the staff screen, and its Clear at the right end
  // — none on the view link, where nothing can be typed. The Buyer and Seller cards have no Clear of their own:
  // each of their rows has one.
  const secHead = (k: BlockKey, label: string, docs?: string[], onClear?: () => void) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
        {foldable && (
          <button type="button" onClick={() => toggle(k)} aria-expanded={!closed.has(k)} title={t(closed.has(k) ? 'pf_block_open' : 'pf_block_close')}
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, marginTop: 4, padding: 0, border: 'none', borderRadius: 6, background: 'transparent', color: 'var(--accent)', fontSize: 16, cursor: 'pointer', flexShrink: 0 }}>
            <i className={`ti ${closed.has(k) ? 'ti-chevron-right' : 'ti-chevron-down'}`} aria-hidden />
          </button>
        )}
        <div style={{ ...hdr, cursor: foldable ? 'pointer' : undefined }} onClick={foldable ? () => toggle(k) : undefined}>{label}</div>
      </div>
      {!ro && onClear && clearBtn(label, docs || [], onClear)}
    </div>
  );
  // What a folded card says in place of its boxes: Complete, or how many are still blank.
  const foldNote = (k: BlockKey) => {
    const n = gaps.filter(g => g.key === k).reduce((s, g) => s + g.boxes.length, 0);
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: n ? '#B42318' : '#16a34a' }}>
        <i className={`ti ${n ? 'ti-alert-circle' : 'ti-circle-check'}`} aria-hidden />{n ? t('pf_block_missing', { n }) : t('pf_block_done')}
      </div>
    );
  };

  // ── A buyer's date of birth from an upload read before it was asked for (report beb72eec) ───────
  // An ID card or passport read before report 06eee624 left the block with no date of birth, so the POA's Age had nothing to
  // be worked out from. While the POA card is open, on the staff screen only, each such upload of a buyer with no date of
  // birth is read again for that date alone: once on this screen (`birthAsked`), the buyer's next upload only if
  // the one before gave none, and the server reads each upload only once (services/propertyForm.ts rereadBirth). The answer
  // goes the way a reading does (readPerson): a blank date of birth, then a blank Age (poaAges), so a typed Age stays.
  // A buyer with no such upload keeps a blank Age, whose box says where it comes from. The client's links have no `rereadBirth`.
  const rereadBirth = ro ? undefined : ctl.rereadBirth;
  const birthAsked = useRef<Record<string, boolean>>({});
  const birthWanted: { uid: string; ids: string[] }[] = rereadBirth && data.doPoa === 'Yes' && !shut('poa')
    ? (data.buyers || []).filter((b: any) => blank(b.birthDate)).map((b: any) => ({ uid: String(b.uid),
        ids: BIRTH_DOCS.flatMap(doc => ctl.files.filter((f: any) => f.path === personDocPath(doc, b.uid)).map((f: any) => String(f.id))).filter(id => !birthAsked.current[id]) }))
      .filter((w: any) => w.ids.length)
    : [];
  const birthWantedKey = JSON.stringify(birthWanted);
  useEffect(() => {
    if (!rereadBirth || !birthWanted.length) return;
    const h = setTimeout(() => {
      for (const w of birthWanted) {
        w.ids.forEach(id => { birthAsked.current[id] = true; });
        (async () => {
          for (const id of w.ids) {
            const birthDate = await rereadBirth(id).catch(() => '');
            if (birthDate) { readPerson('buyers', w.uid, BIRTH_DOCS[0], true)({ birthDate }); return; }
          }
        })();
      }
    }, 600);
    return () => clearTimeout(h);
  }, [birthWantedKey]); // eslint-disable-line

  // ── A buyer's Thai on the POA from the AI (report b4f708fa, poaThai.ts) ────────────────────────
  // While the POA card is open, on the staff screen only, the boxes still empty are asked for after a pause: once on
  // this screen for each box and its English (`thaiAsked`), so a box emptied by hand stays empty and a failure is not
  // asked again until the form is next opened. The pause is longer than the lookups' above, as an ask saves the form and
  // calls the AI for every empty box. A name typed with a longer pause in it is asked about part-way; the rest of it makes
  // that answer stale, and the box is asked about again. The answers go only into boxes still empty, and save as typing
  // does. What could not be filled keeps printing its English, and the line under the card's hint says so.
  const poaTranslate = ro ? undefined : ctl.poaTranslate;
  const thaiAsked = useRef<Record<string, boolean>>({});
  const [thaiBusy, setThaiBusy] = useState(0);
  const [thaiLeft, setThaiLeft] = useState(false);
  const thaiEmpty: string[] = poaTranslate && data.doPoa === 'Yes' && !shut('poa') ? poaThaiWanted(data) : [];
  const thaiWanted = thaiEmpty.filter(k => !thaiAsked.current[k]);
  const thaiWantedKey = JSON.stringify(thaiWanted);
  useEffect(() => {
    if (!poaTranslate || !thaiWanted.length) return;
    const h = setTimeout(async () => {
      thaiWanted.forEach(k => { thaiAsked.current[k] = true; });
      setThaiBusy(n => n + 1);
      try {
        const r = await poaTranslate();
        set((d: any) => poaThaiFilled(d, r.fills), true);
        setThaiLeft(r.left > 0);
      } catch { setThaiLeft(true); }
      setThaiBusy(n => n - 1);
    }, POA_THAI_PAUSE);
    return () => clearTimeout(h);
  }, [thaiWantedKey]); // eslint-disable-line

  // A buyer's or seller's card heading: its Clear, then — while there is more than one — its Remove, still at the right end.
  const rowHead = (label: string, docs: string[], onClear: () => void, onRemove?: () => void) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-tertiary)' }}>{label}</span>
      {!ro && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {clearBtn(label, docs, onClear)}
          {onRemove && <button type="button" onClick={onRemove} style={{ ...btnG, padding: '3px 9px', fontSize: 12, color: '#b91c1c' }}>{t('pf_remove')}</button>}
        </div>
      )}
    </div>
  );
  // Title · first · middle · last, email · ID / passport No. · nationality, address — a buyer's block and an
  // individual seller's. The Middle name is optional (report c39980c2): it has its own box, so a middle name no
  // longer has to go in First name. A buyer's block also asks for both parents' names while a POA is being done
  // (the POA needs them). The block's three uploads (PERSON_DOCS) — ID card, passport, address document — stand in
  // the page's left margin beside the name row, the ID / passport row and the Residing address (MarginDocs, report
  // 46a11e5a), and inside the card under the address only when the window leaves no margin for them.
  const personFields = (key: 'buyers' | 'sellers', p: any, i: number) => (
    <MarginDocs
      rows={[
        <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr 1fr 1fr', gap: 10 }}>
          <Pick label={t('pf_title')} value={p.title} onChange={v => setRow(key, i, { title: v })} options={titleOpts} hi={lit(p.uid, 'title')} />
          <NameBox label={t('pf_first')} part="given-name" value={p.first} onChange={v => setRow(key, i, { first: v })} hi={lit(p.uid, 'first')} />
          <NameBox label={t('pf_middle')} part="additional-name" opt value={p.middle} onChange={v => setRow(key, i, { middle: v })} hi={lit(p.uid, 'middle')} />
          <NameBox label={t('pf_last')} part="family-name" value={p.last} onChange={v => setRow(key, i, { last: v })} hi={lit(p.uid, 'last')} />
        </div>,
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <EmailBox label={t('pf_email')} value={p.email} onChange={v => setRow(key, i, { email: v })}
            check={!!emailToCheck[p.uid]} guess={emailToCheck[p.uid] === 'ai'} history={emailToCheck[p.uid] === 'history'} note={emailToCheck[p.uid] === 'note'} onChecked={emailChecked(p.uid)} hi={lit(p.uid, 'email')} />
          <Txt label={t('pf_id_no')} value={p.idNo} onChange={v => setRow(key, i, { idNo: fmtIdNo(v) })} hi={lit(p.uid, 'idNo')} />
          <Pick label={t('pf_nationality')} value={p.nationality} onChange={v => setRow(key, i, { nationality: v })} options={countryOpts} hi={lit(p.uid, 'nationality')} />
        </div>,
        <>
          <AddressBox label={t('pf_address')} value={p.address} onChange={v => setRow(key, i, { address: v })}
            check={!!postalToCheck[p.uid]} onChecked={postalChecked(p.uid)} noteCheck={!!noteToCheck[p.uid]} onNoteChecked={noteChecked(p.uid)} hi={litAddress(p.uid)} />
          {/* The hidden note on this person's stored passport (docNotes, report 73f2d29a): "Fill in from the note" while its typed
              boxes (report 14486e36) still have something for a blank part of the address or a blank Email — a free-text note
              from before the boxes offers "Use as residing address" while Line 1 is blank. */}
          {docNotes[p.uid] && !ro && (
            <div style={{ ...checkHint, marginTop: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ whiteSpace: 'pre-wrap' }}><i className="ti ti-note" aria-hidden style={{ marginRight: 4 }} />{t(docNotes[p.uid].passport ? 'pf_note_from' : 'pf_note_from_doc')}: {docNotes[p.uid].note}</span>
              {noteCanFill(p, docNotes[p.uid]) && <button type="button" onClick={applyDocNote(key, p.uid)} style={{ ...btnG, padding: '3px 9px', fontSize: 12, color: '#B45309', borderColor: '#D97706' }}>{t(docNotes[p.uid].fields ? 'pf_note_fill' : 'pf_note_use')}</button>}
            </div>
          )}
        </>,
      ]}
      rest={key === 'buyers' && data.doPoa === 'Yes' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <NameBox label={t('pf_father_first')} part="given-name" value={p.fatherFirst} onChange={v => setRow(key, i, { fatherFirst: v })} hi={lit(p.uid, 'fatherFirst')} />
            <NameBox label={t('pf_father_last')} part="family-name" value={p.fatherLast} onChange={v => setRow(key, i, { fatherLast: v })} hi={lit(p.uid, 'fatherLast')} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <NameBox label={t('pf_mother_first')} part="given-name" value={p.motherFirst} onChange={v => setRow(key, i, { motherFirst: v })} hi={lit(p.uid, 'motherFirst')} />
            <NameBox label={t('pf_mother_last')} part="family-name" value={p.motherLast} onChange={v => setRow(key, i, { motherLast: v })} hi={lit(p.uid, 'motherLast')} />
          </div>
        </>
      )}
      docs={PERSON_DOCS.map(doc => (
        <DocBox key={doc.kind} label={t(doc.label)} path={personDocPath(doc, p.uid)} kind={doc.kind} ctl={ctl} onRead={readPerson(key, p.uid, doc)}
          onRemoved={clearPersonDoc(key, p.uid, doc)} onHover={hoverOn(p.uid, personFills(key, doc))} />
      ))}
    />
  );

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {/* Questions — recorded only, but for Due Diligence (report 0c21500b): staff alone answer it, and its Yes starts
          that report once, as the form moves to Pending or Confirmed (the server does that, not this screen).
          190px holds the longest label on one line, and lets all four stand on one row: the card is 858px inside. */}
      <div className="pf-card" style={{ ...card, display: 'grid', gap: 12 }} {...foldOn('questions')}>
        {secHead('questions', t('pf_sec_questions'), [], blankQuestions)}
        {shut('questions') ? foldNote('questions') : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10 }}>
            <Pick label={t('pf_q_poa')} value={data.doPoa} onChange={setDoPoa} options={yesNo} />
            <Pick label={t('pf_q_agreements')} value={data.doAgreements} onChange={v => setTop({ doAgreements: v })} options={yesNo} />
            {spa && <Pick label={t('pf_q_dd')} value={data.doDd} onChange={v => setTop({ doDd: v })} options={yesNo} />}
            <Pick label={t('pf_q_will')} value={data.doWill} onChange={v => setTop({ doWill: v })} options={yesNo} />
          </div>
        )}
        {spa && ddReport && <DdReportLink report={ddReport} />}
      </div>

      {/* Buyer or lessee — up to three */}
      <div className="pf-card" style={{ ...card, display: 'grid', gap: 12 }} {...foldOn('buyer')}>
        {secHead('buyer', t('pf_sec_buyer'))}
        {shut('buyer') ? foldNote('buyer') : (
          <>
            {data.buyers.map((b: any, i: number) => (
              <div key={b.uid} style={rowBox}>
                {rowHead(t('pf_buyer_n', { n: i + 1 }), PERSON_DOCS.map(doc => personDocPath(doc, b.uid)), blankRow('buyers', b.uid), data.buyers.length > 1 ? () => removeRow('buyers', i) : undefined)}
                <RolesBox value={b.roles} onChange={v => setRow('buyers', i, { roles: v })} />
                {personFields('buyers', b, i)}
              </div>
            ))}
            {!ro && data.buyers.length < MAX_PEOPLE && (
              <button type="button" onClick={() => addRow('buyers', emptyBuyer)} style={{ ...btnG, justifySelf: 'start' }}>
                + {data.buyers.length === 1 ? t('pf_add_buyer2') : t('pf_add_buyer3')}
              </button>
            )}
          </>
        )}
      </div>

      {/* Seller — up to three; an individual or a company, each with its own kind of property */}
      <div className="pf-card" style={{ ...card, display: 'grid', gap: 12 }} {...foldOn('seller')}>
        {secHead('seller', t('pf_sec_seller'))}
        {shut('seller') ? foldNote('seller') : (
          <>
            {data.sellers.map((s: any, i: number) => (
              <div key={s.uid} style={rowBox}>
                {rowHead(t('pf_seller_n', { n: i + 1 }), PERSON_DOCS.map(doc => personDocPath(doc, s.uid)), blankRow('sellers', s.uid), data.sellers.length > 1 ? () => removeRow('sellers', i) : undefined)}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <Pick label={t('pf_seller_type')} value={s.kind} onChange={v => setRow('sellers', i, { kind: v })} options={opts(SELLER_KINDS)} />
                  <Pick label={t('pf_prop_type')} value={s.propType} onChange={v => setRow('sellers', i, { propType: v })} options={opts(PROP_TYPES)} />
                </div>
                {s.kind === 'Company' ? (
                  /* The company documents' upload stands in the page's margin beside the Company name row (MarginDocs). */
                  <MarginDocs
                    rows={[
                      /* A company's name is kept as typed (its capitals are its own); the house capitals rule is for people */
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <Txt label={t('pf_company_name')} value={s.companyName} onChange={v => setRow('sellers', i, { companyName: v })}
                          input={{ autoComplete: 'organization', autoCapitalize: 'words' }} hi={lit(s.uid, 'companyName')} />
                        <EmailBox label={t('pf_email')} value={s.email} onChange={v => setRow('sellers', i, { email: v })}
                          check={!!emailToCheck[s.uid]} guess={emailToCheck[s.uid] === 'ai'} history={emailToCheck[s.uid] === 'history'} note={emailToCheck[s.uid] === 'note'} onChecked={emailChecked(s.uid)} hi={lit(s.uid, 'email')} />
                      </div>,
                    ]}
                    rest={(
                      <>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                          <Txt label={t('pf_tax_id')} value={s.taxId} onChange={v => setRow('sellers', i, { taxId: v })} hi={lit(s.uid, 'taxId')} />
                        </div>
                        <AddressBox label={t('pf_reg_address')} value={s.address} onChange={v => setRow('sellers', i, { address: v })}
                          check={!!postalToCheck[s.uid]} onChecked={postalChecked(s.uid)} hi={litAddress(s.uid)} />
                      </>
                    )}
                    docs={[
                      /* Filed under this seller's uid, as an ID card is, so it stays with the seller if the type changes. Its
                         outline takes the Email too on the staff screen, which Contacts is asked for once the name is filled. */
                      <DocBox label={t('pf_upload_company')} path={`person:${s.uid}`} kind="company" ctl={ctl} onRead={readCompany(s.uid)}
                        onRemoved={clearRow('sellers', s.uid, COMPANY_KEYS)} onHover={hoverOn(s.uid, COMPANY_KEYS.concat(ADDRESS_PATHS, lookup ? ['email'] : []))} />,
                    ]}
                  />
                ) : personFields('sellers', s, i)}
              </div>
            ))}
            {!ro && data.sellers.length < MAX_PEOPLE && (
              <button type="button" onClick={() => addRow('sellers', emptySeller)} style={{ ...btnG, justifySelf: 'start' }}>
                + {data.sellers.length === 1 ? t('pf_add_seller2') : t('pf_add_seller3')}
              </button>
            )}
          </>
        )}
      </div>

      {/* Property — the land and building questions follow the sellers' property types */}
      <div className="pf-card" style={{ ...card, display: 'grid', gap: 12 }} {...foldOn('property')}>
        {secHead('property', t('pf_sec_property'), ['addressDoc', 'titleDeed', 'houseBook', 'reservation', 'orChor2'], blankProperty)}
        {shut('property') ? foldNote('property') : (
          <>
            <Txt label={t('pf_prop_name')} value={data.property.name} onChange={v => setProp({ name: v })} ph={t('pf_prop_name_ph')} />
            {/* Where the property is. The agreement prints it as the Property's location, and its City is
                the Land Office the transfer is registered at (report 4ba8eca0) — so it is asked for only
                while agreements are being prepared. */}
            {data.doAgreements === 'Yes' && (
              <MarginDocs
                rows={[
                  <AddressBox label={t('pf_prop_address')} value={data.property.address} onChange={v => setProp({ address: v })}
                    check={!!postalToCheck[PROP_ADDR]} onChecked={postalChecked(PROP_ADDR)} hi={litAddress('property')} />,
                ]}
                docs={[
                  /* The address's own upload (report 3904c89f), in the page's margin beside the address: the house book, or
                     another document giving the address, fills this first half of the block, as the title deed under Land
                     fills the second. */
                  <DocBox label={t('pf_upload_address')} path="addressDoc" kind="propAddress" ctl={ctl} onRead={readAddress} onRemoved={clearAddress}
                    onHover={hoverOn('property', ADDRESS_PATHS.concat(hasBuilding ? ['houseBookNo'] : []))} />,
                ]}
              />
            )}
            {hasLand && (
              <div style={rowBox}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-tertiary)' }}>{t('pf_land')}</span>
                {/* The title deed's upload stands in the page's margin beside the Deed type row (MarginDocs). */}
                <MarginDocs
                  rows={[
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                      <Pick label={t('pf_deed_type')} value={data.property.deedType} onChange={v => setProp({ deedType: v })} options={deedOpts} hi={lit('property', 'deedType')} />
                      <Txt label={t('pf_deed_no')} value={data.property.deedNo} onChange={v => setProp({ deedNo: v })} hi={lit('property', 'deedNo')} />
                      <Txt label={t('poa_land_no')} value={data.property.landNo} onChange={v => setProp({ landNo: v })} hi={lit('property', 'landNo')} />
                    </div>,
                  ]}
                  rest={(
                    <>
                      {/* The land's size in the country's own units, then the m² worked out from them (report f7b09e0d); a
                          country with no units types the m². At least three columns, so the m² alone keeps a box's width. */}
                      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(3, landUnits.length + 1)}, minmax(0, 1fr))`, gap: 10 }}>
                        {landUnits.map(u => (
                          <NumBox key={u.key} label={u.label} value={data.property[u.key]} onChange={setUnit(u.key)} bad={m2Only(data.property) ? false : undefined} hi={lit('property', u.key)} />
                        ))}
                        {landUnits.length
                          ? <CalcBox label={t('pf_land_sqm')} value={data.property.landSqm} hi={lit('property', 'landSqm')} />
                          : <NumBox label={t('pf_land_sqm')} value={data.property.landSqm} onChange={v => setProp({ landSqm: v })} hi={lit('property', 'landSqm')} />}
                      </div>
                      {landUnits.length > 0 && <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('pf_land_sqm_auto', { hint: landAreaHint() })}</div>}
                    </>
                  )}
                  docs={[
                    <DocBox label={t('pf_upload_deed')} path="titleDeed" kind="titleDeed" ctl={ctl} onRead={readDeed} onRemoved={clearProperty(deedKeys())}
                      onHover={hoverOn('property', deedKeys())} />,
                  ]}
                />
              </div>
            )}
            {hasBuilding && (
              <div style={rowBox}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-tertiary)' }}>{t('pf_building')}</span>
                {/* The house book's upload stands in the page's margin beside the Unit no. / House book number row, then the
                    Reservation Agreement's beside the Building sqm row, one flow (MarginDocs; report 6ae27545: the house book,
                    then the Reservation Agreement under it). */}
                <MarginDocs
                  rows={[
                    /* The House book number is optional — the house book may not be issued yet — and there is no Levels box
                       (report 6ae27545). */
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <Txt label={t('pf_unit_no')} value={data.property.unitNo} onChange={v => setProp({ unitNo: v })} hi={lit('property', 'unitNo')} />
                      <Txt label={t('pf_house_book_no')} opt value={data.property.houseBookNo} onChange={v => setProp({ houseBookNo: v })} hi={lit('property', 'houseBookNo')} />
                    </div>,
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10 }}>
                      <NumBox label={t('pf_building_sqm')} value={data.property.buildingSqm} onChange={v => setProp({ buildingSqm: v })} hi={lit('property', 'buildingSqm')} />
                      <NumBox label={t('pf_bedrooms')} whole value={data.property.bedrooms} onChange={v => setProp({ bedrooms: v })} hi={lit('property', 'bedrooms')} />
                      <NumBox label={t('pf_bathrooms')} whole value={data.property.bathrooms} onChange={v => setProp({ bathrooms: v })} hi={lit('property', 'bathrooms')} />
                    </div>,
                  ]}
                  rest={data.doAgreements === 'Yes' && !hasCondo && (
                    /* The agreement's building warranties, its clauses 8.3 and 8.4 (report f87182e0): None leaves the clause out.
                       No answer shows — and the agreement prints — the template's own 5 and 1 years. Asked only while
                       agreements are being prepared, and never beside a condominium unit, whose agreement has neither. */
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <Pick label={t('pf_warranty_structural')} value={data.property.structuralWarranty || '5'} onChange={v => setProp({ structuralWarranty: v })} options={warrantyOpts} />
                      <Pick label={t('pf_warranty_architectural')} value={data.property.architecturalWarranty || '1'} onChange={v => setProp({ architecturalWarranty: v })} options={warrantyOpts} />
                    </div>
                  )}
                  docs={[
                    <DocBox label={t('pf_upload_house_book')} path="houseBook" kind="houseBook" ctl={ctl} onRead={readProperty(BOOK_KEYS)}
                      onRemoved={clearBuilding(BOOK_KEYS, 'reservation', RESERVATION_KEYS)} onHover={hoverOn('property', BOOK_KEYS)} />,
                    <DocBox label={t('pf_upload_reservation')} path="reservation" kind="reservation" ctl={ctl} onRead={readProperty(RESERVATION_KEYS)}
                      onRemoved={clearBuilding(RESERVATION_KEYS, 'houseBook', BOOK_KEYS)} onHover={hoverOn('property', RESERVATION_KEYS)} />,
                  ]}
                />
              </div>
            )}
            {/* One condominium unit: its own title deed (the Or.Chor.2) in place of the land deed and the
                house book, which building and floor it is on, and its share of the common property
                (report 4ba8eca0). The Or.Chor.2 itself is only kept with the form — no AI reading. */}
            {hasCondo && (
              <div style={rowBox}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-tertiary)' }}>{t('pf_condo')}</span>
                {/* The Or.Chor.2's upload stands in the page's margin beside the Or.Chor.2 No row (MarginDocs). */}
                <MarginDocs
                  rows={[
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <Txt label={t('pf_orchor2_no')} value={data.property.orChor2No} onChange={v => setProp({ orChor2No: v })} />
                      <Txt label={t('pf_condo_share')} value={data.property.condoShare} onChange={v => setProp({ condoShare: v })} />
                    </div>,
                  ]}
                  rest={(
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <Txt label={t('pf_condo_building')} value={data.property.condoBuilding} onChange={v => setProp({ condoBuilding: v })} />
                      <Txt label={t('pf_condo_floor')} value={data.property.condoFloor} onChange={v => setProp({ condoFloor: v })} />
                    </div>
                  )}
                  docs={[
                    <DocBox label={t('pf_upload_orchor2')} path="orChor2" kind="" ctl={ctl} plain
                      onRead={() => ({ n: 0 })} onRemoved={() => { /* nothing was read from it, so no box to take back */ }} />,
                  ]}
                />
              </div>
            )}
            {!hasLand && !hasBuilding && <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('pf_choose_type')}</div>}
          </>
        )}
      </div>

      {/* Agreement — only when agreements are being prepared */}
      {data.doAgreements === 'Yes' && (
        <div className="pf-card" style={{ ...card, display: 'grid', gap: 12 }} {...foldOn('agreement')}>
          {secHead('agreement', agreementLabel(data, t), ['agreement', ...PARTS.map(([k]) => agrPath(k))], blankAgreement)}
          {shut('agreement') ? foldNote('agreement') : (
            <>
              {/* A condominium's clauses are an approved draft awaiting a lawyer's check
                  (docs/spa-blueprint.md §5). Said here, never printed in the agreement itself. */}
              {spa && hasCondo && (
                <div style={noteBox}><i className="ti ti-alert-triangle" aria-hidden style={{ marginRight: 5 }} />{t('pf_spa_condo_draft')}</div>
              )}
              {/* The agreement's upload stands in the page's margin beside the Agreement date row (MarginDocs). A split
                  sale's two uploads stand beside each part's Price row inside its block instead, and the card's own
                  column is then empty. */}
              <MarginDocs
                rows={[
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <Txt label={t('pf_agr_date')} type="date" value={agr.date} onChange={v => setAgr({ date: v, dateDefault: '' })} hi={lit('lease', 'date')} />
                    <Pick label={t('pf_signs_as')} value={agr.signAs} onChange={v => setAgr({ signAs: v })} options={opts(SIGN_AS)} />
                  </div>,
                ]}
                rest={(
                  <>
                    {/* Somebody signing for the owner: the agreement has to name the owner they sign for. */}
                    {agr.signAs === 'Authorized Representative' && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <Txt label={t('pf_owner_name')} value={agr.ownerName} onChange={v => setAgr({ ownerName: v })}
                          input={{ autoCapitalize: 'words' }} />
                      </div>
                    )}
                    {/* The same buyers taking the land and the building may still have an agreement for each (report
                        0d7e130c); different buyers always do, and a condominium unit never, so the tick shows only then. */}
                    {separateOffered(data) && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13.5, color: 'var(--text-primary)', cursor: ro ? 'default' : 'pointer', justifySelf: 'start' }}>
                        <input type="checkbox" checked={agr.separate === true} disabled={ro} onChange={e => setAgr({ separate: e.target.checked })} style={{ width: 15, height: 15, margin: 0, flexShrink: 0 }} />
                        {t('pf_agr_separate')}
                      </label>
                    )}
                    {/* A split sale: the Land block and the Building block, each with its own agreement upload, read into it
                        alone, standing in the margin beside the block's Price row — and its own fees and conditions under
                        its figures (report 7dc394ad: "we also need to split the following fields"). */}
                    {split ? PARTS.map(([k, key]) => {
                      const f = figuresRows(k);
                      return (
                        <div key={k} style={rowBox}>
                          <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-tertiary)' }}>{t(key)}</span>
                          <MarginDocs
                            rows={[f.first]}
                            rest={<>{f.rest}{feesRow(k)}{condRows(k)}</>}
                            docs={[
                              <DocBox label={t('pf_upload_agreement')} path={agrPath(k)} kind="agreement" ctl={ctl} onRead={readAgreement(k)} onRemoved={clearAgreement(k)}
                                onHover={hoverOn(agrPath(k), agrFills)} />,
                            ]}
                          />
                        </div>
                      );
                    }) : <>{figuresBlock('')}{feesRow('')}{condRows('')}</>}
                    {/* The Lease Agreement prints the card's own conditions, so a clause inserted there is the lease's
                        too: a split sale, whose parts have their own, keeps the card's under the Lease heading while a
                        lease is being prepared. */}
                    {split && leaseApplies(data) && (
                      <div style={rowBox}>
                        <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-tertiary)' }}>{t('pf_sec_lease')}</span>
                        {condRows('')}
                      </div>
                    )}
                  </>
                )}
                docs={split ? [] : [
                  <DocBox label={t('pf_upload_agreement')} path={agrPath('')} kind="agreement" ctl={ctl} onRead={readAgreement('')} onRemoved={clearAgreement('')}
                    onHover={hoverOn(agrPath(''), agrFills)} />,
                ]}
              />
            </>
          )}
        </div>
      )}
      {/* Lease — only when a Lease stage is chosen or a buyer is ticked Lessee (report eab2c428). Its
          answers fill the Lease Agreement (docs/lease-blueprint.md §3); the parties and the property come
          from the blocks above and are never asked again, and its Agreement date is the Agreement card's own. */}
      {leaseApplies(data) && (
        <div className="pf-card" style={{ ...card, display: 'grid', gap: 12 }} {...foldOn('lease')}>
          {secHead('lease', t('pf_sec_lease'), ['leaseDoc'], blankLease)}
          {shut('lease') ? foldNote('lease') : (
            <>
              {spa && <div style={noteBox}><i className="ti ti-alert-triangle" aria-hidden style={{ marginRight: 5 }} />{t('pf_lease_draft_note')}</div>}
              {/* The report's markup (docs/lease-blueprint.md §6 (e)): one Agreement date — the Agreement card's own
                  box, held once — and the Registration date, the lease's start, with its Expiry date worked out. */}
              <MarginDocs
                rows={[
                  <div style={g3}>
                    <Pick label={t('pf_lease_term')} value={lease.term} onChange={v => setLease({ term: v })} options={yearOpts} hi={lit('lease', 'term')} />
                    <Pick label={t('pf_lease_renewal')} value={lease.renewal} onChange={v => setLease({ renewal: v })} options={renewOpts} hi={lit('lease', 'renewal')} />
                    <Txt label={t('pf_agr_date')} type="date" value={agr.date} onChange={v => setAgr({ date: v, dateDefault: '' })} bad={!isDay(agr.date)} hi={lit('lease', 'date')} />
                  </div>,
                ]}
                docs={[
                  /* A lease document — a lease agreement, or the Land Office's lease registration — read by AI, standing in
                     the page's margin beside the Term row (MarginDocs): it fills the section's blank boxes and an
                     Agreement date that is blank or still its default; removing it takes the lease boxes back. */
                  <DocBox label={t('pf_upload_lease')} path="leaseDoc" kind="lease" ctl={ctl} onRead={readLease} onRemoved={clearLease}
                    onHover={hoverOn('lease', LEASE_KEYS.concat(['date']))} />,
                ]}
              />
              {/* The Total area, and the Portion area leased only when Whole or part is Part (the report's markup on its
                  latest screenshot, docs/lease-blueprint.md §6 (f)): the Land parcel no. box is gone. */}
              <div style={g3}>
                <NumBox label={t('pf_lease_area')} value={leaseArea} onChange={v => setLease({ area: v })} bad={blank(leaseArea) || leaseTotalTooSmall(lease, leaseArea)} hi={lit('lease', 'area')} />
                <Pick label={t('pf_lease_extent')} value={lease.extent} onChange={v => setLease({ extent: v })} options={opts(LEASE_EXTENT)} hi={lit('lease', 'extent')} />
                {lease.extent === 'Part' && <NumBox label={t('pf_lease_portion')} value={lease.portionArea} onChange={v => setLease({ portionArea: v })} hi={lit('lease', 'portionArea')} />}
              </div>
              <div style={g3}>
                <Txt label={t('pf_lease_reg_date')} type="date" value={lease.regDate} onChange={v => setLease({ regDate: v })} bad={!isDay(lease.regDate)} hi={lit('lease', 'regDate')} />
                <CalcBox label={t('pf_lease_expiry')} type="date" value={leaseExpiry(lease.regDate, lease.term)} />
              </div>
              <div style={g3}>
                <Pick label={t('pf_lease_period')} value={lease.period} onChange={v => setLease({ period: v })} options={opts(LEASE_PERIODS)} hi={lit('lease', 'period')} />
                <MoneyBox label={t('pf_lease_total_rent')} cur={cur} value={lease.totalRent} onChange={v => setLease({ totalRent: v })} hi={lit('lease', 'totalRent')} />
              </div>
              {/* The rent's step-up: none, or a plus percentage every so many years, a number that divides the Term
                  into whole bands (report 805f7320). Blank reads as None. */}
              <div style={g3}>
                <Pick label={t('pf_lease_step_every')} value={stepFits ? lease.stepEvery || 'None' : ''} onChange={v => setLease({ stepEvery: v })} options={stepOpts} hi={lit('lease', 'stepEvery')} />
                {stepOn && <NumBox label={t('pf_lease_step_pct')} value={lease.stepPct} onChange={v => setLease({ stepPct: v })} bad={!(Number(lease.stepPct) > 0)} hi={lit('lease', 'stepPct')} />}
              </div>
              {/* Rent per period, worked out from the boxes above and read-only (report 805f7320): one box, or with a
                  step-up one box for each band, labelled with its years. */}
              <div style={g3}>
                {rents.length > 1
                  ? rents.map(b => <CalcBox key={b.from} tag={cur} value={grouped(b.rent)}
                      label={b.from === b.to ? t('pf_lease_rent_year', { n: b.from }) : t('pf_lease_rent_years', { from: b.from, to: b.to })} />)
                  : <CalcBox tag={cur} label={t('pf_lease_rent')} value={grouped(rents[0]?.rent || '')} />}
              </div>
              <div style={g3}>
                <AccountNameBox label={t('pf_lease_acc_name')} value={lease.accName} onChange={v => setLease({ accName: v })} hi={lit('lease', 'accName')} />
                <Pick label={t('pf_lease_bank')} value={bankPick} onChange={v => setLease({ bank: v })} options={bankOpts} hi={lit('lease', 'bank')} />
                <Txt label={t('pf_lease_acc_no')} value={lease.accNo} onChange={v => setLease({ accNo: v })} input={{ inputMode: 'numeric' }} hi={lit('lease', 'accNo')} />
              </div>
              <div style={g3}>
                <Pick label={t('pf_lease_reg_costs')} value={lease.regFees} onChange={v => setLease({ regFees: v })} options={opts(LEASE_FEES)} hi={lit('lease', 'regFees')} />
                <Pick label={t('pf_lease_use')} value={lease.use} onChange={v => setLease({ use: v })} options={useOpts} hi={lit('lease', 'use')} />
                <Pick label={t('pf_lease_assign')} value={lease.assignment} onChange={v => setLease({ assignment: v })} options={opts(LEASE_ASSIGN)} />
              </div>
              <div style={g3}>
                <Txt label={t('pf_lease_witness1')} value={lease.witness1} onChange={v => setLease({ witness1: v })} input={{ autoCapitalize: 'words' }} hi={lit('lease', 'witness1')} />
                <Txt label={t('pf_lease_witness2')} value={lease.witness2} onChange={v => setLease({ witness2: v })} input={{ autoCapitalize: 'words' }} hi={lit('lease', 'witness2')} />
              </div>
              {/* The Thai the Lease Agreement prints for these answers, and 'AI: fill the Thai' — staff only, as the agreement is. */}
              {spa && <LeaseThai spa={spa} lease={lease} update={updateLease} />}
            </>
          )}
        </div>
      )}
      {/* POA — only while 'Are we doing a POA for the buyer?' is Yes (report 2ce6d3b5). The buyers' names, ID,
          nationality, address and parents come from their cards above and are never asked again: this card asks
          only what the POA needs besides. Each buyer's POA prints as its own bubble under the agreements. A buyer's
          Age is worked out from the date of birth their ID card or passport gave (report 06eee624, poaAges), and with
          neither on the form its box says where it comes from (report beb72eec). Its Date is printed at the POA's head:
          the question going to Yes fills a blank one with the default (report 90208a98, defaultDates.ts). */}
      {data.doPoa === 'Yes' && (
        <div className="pf-card" style={{ ...card, display: 'grid', gap: 12 }} {...foldOn('poa')}>
          {secHead('poa', t('pf_sec_poa'))}
          {shut('poa') ? foldNote('poa') : (
            <>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('pf_poa_hint')}</div>
              {thaiBusy > 0 && <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}><i className="ti ti-loader" aria-hidden style={{ marginRight: 4 }} />{t('pf_lease_th_filling')}</div>}
              {thaiBusy === 0 && thaiLeft && thaiEmpty.length > 0 && <div style={badHint}>{t('pf_poa_th_left')}</div>}
              {data.buyers.map((b: any, i: number) => {
                const g = (poa.grantors || {})[b.uid] || {};
                const who = [b.title, b.first, b.middle, b.last].filter((x: any) => !blank(x)).join(' ');
                // The buyer's Thai boxes (poaThai.ts): each shows the English it stands for while it is empty, and one the AI
                // filled asks to be checked until somebody changes it, on the staff screen only.
                const boxes = poaThaiBoxes(b);
                const thaiBox = (key: string, label: string) => {
                  const x = boxes.find(o => o.key === key) || { key, en: '' };
                  return <Txt key={key} label={label} opt value={poaThaiNow(g, x)} ph={x.en} check={!!poaTranslate && poaThaiIsAi(g, x)} onChange={v => set((d: any) => poaThaiTyped(d, b.uid, key, v))} />;
                };
                return (
                  <div key={b.uid || i} style={{ display: 'grid', gap: 10 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-tertiary)' }}>{t('pf_buyer_n', { n: i + 1 })}{who ? ` — ${who}` : ''}</span>
                    <div style={g3}>
                      <Pick label={t('pf_poa_as')} value={g.as || poaAsOf(b)} onChange={v => setGrantor(b.uid, { as: v })} options={opts(POA_AS)} />
                      <Txt label={t('pf_poa_age')} opt value={g.age} ph={ageOn(b.birthDate, ageDay(data)) || t('pf_poa_age_ph')} onChange={v => setGrantor(b.uid, { age: v.replace(/[^0-9]/g, '').slice(0, 3) })} input={{ inputMode: 'numeric' }} hi={lit(b.uid, 'poa.age')} />
                    </div>
                    <div style={g3}>
                      {thaiBox('firstTh', t('pf_poa_first_th'))}
                      {thaiBox('lastTh', t('pf_poa_last_th'))}
                    </div>
                    <div style={g3}>
                      {thaiBox('fatherTh', t('pf_poa_father_th'))}
                      {thaiBox('motherTh', t('pf_poa_mother_th'))}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      {POA_TH_ADDRESS.map(([key, label]) => thaiBox(key, t(label)))}
                    </div>
                    {!!poaTranslate && boxes.some(x => poaThaiIsAi(g, x)) && <div style={checkHint}><i className="ti ti-sparkles" aria-hidden style={{ marginRight: 4 }} />{t('pf_poa_th_check')}</div>}
                  </div>
                );
              })}
              <div style={g3}>
                {data.sellers.map((s: any, i: number) => (
                  <Txt key={s.uid || i} label={t('pf_poa_seller_th', { n: i + 1 })} opt value={(poa.sellersTh || {})[s.uid]}
                    ph={s.kind === 'Company' ? s.companyName : [s.title, s.first, s.middle, s.last].filter((x: any) => !blank(x)).join(' ')}
                    onChange={v => setPoaIn('sellersTh', { [s.uid]: v })} />
                ))}
              </div>
              <div style={g3}>
                <Txt label={t('pf_poa_subdistrict_th')} opt value={(poa.placeTh || {}).subdistrict} ph={poaPlaces.subdistrict} onChange={v => setPoaIn('placeTh', { subdistrict: v })} />
                <Txt label={t('pf_poa_district_th')} opt value={(poa.placeTh || {}).district} ph={poaPlaces.district} onChange={v => setPoaIn('placeTh', { district: v })} />
                <Txt label={t('pf_poa_province_th')} opt value={(poa.placeTh || {}).province} ph={poaPlaces.province} onChange={v => setPoaIn('placeTh', { province: v })} />
              </div>
              {data.sellers.some((s: any) => s.propType === 'Condominium') && (
                <div style={g3}>
                  <Txt label={t('pf_poa_condo_reg')} opt value={poa.condoRegNo} onChange={v => setPoa({ condoRegNo: v })} />
                </div>
              )}
              <div style={{ ...hdr, fontSize: 13.5 }}>{t('pf_poa_donee')}</div>
              {/* The attorney's ID card or passport stands in the page's margin beside the block's first row (MarginDocs,
                  report beb72eec). Staff only: the attorney is the firm's person, so a client's link neither offers nor shows it. */}
              <MarginDocs
                rows={[
                  <div style={{ display: 'grid', gap: 12 }}>
                    {attorneys.length > 0 && (
                      <div style={g3}>
                        <Pick label={t('pf_poa_attorney')} opt value={attorneyVal} onChange={pickAttorney}
                          options={attorneys.map((a, i) => ({ value: String(i), label: poaLabel(a) })).concat([{ value: POA_OTHER, label: t('pf_poa_other') }])} />
                        {attorneyVal !== POA_OTHER && <CalcBox label={t('pf_poa_age')} value={attorneyAge} ph={t('pf_poa_age_ph')} hi={lit('donee', 'age')} />}
                      </div>
                    )}
                    {(!attorneys.length || attorneyVal === POA_OTHER) && (
                      <>
                        <div style={g3}>
                          <Txt label={t('pf_poa_name')} opt value={(poa.donee || {}).name} onChange={v => setPoaIn('donee', { name: v })} input={{ autoCapitalize: 'words' }} />
                          <Txt label={t('pf_poa_name_th')} opt value={(poa.donee || {}).nameTh} onChange={v => setPoaIn('donee', { nameTh: v })} />
                          <Txt label={t('pf_poa_age')} opt value={(poa.donee || {}).age} ph={ageOn(doneeNow.birthDate, ageDay(data)) || (spa ? t('pf_poa_age_ph') : '')} onChange={v => setPoaIn('donee', { age: v.replace(/[^0-9]/g, '').slice(0, 3) })} input={{ inputMode: 'numeric' }} hi={lit('donee', 'age')} />
                        </div>
                        <div style={g3}>
                          <Txt label={t('pf_id_no')} opt value={(poa.donee || {}).idNo} onChange={v => setPoaIn('donee', { idNo: fmtIdNo(v) })} />
                          <Txt label={t('pf_poa_addr')} opt value={(poa.donee || {}).addr} onChange={v => setPoaIn('donee', { addr: v })} />
                          <Txt label={t('pf_poa_addr_th')} opt value={(poa.donee || {}).addrTh} onChange={v => setPoaIn('donee', { addrTh: v })} />
                        </div>
                      </>
                    )}
                  </div>,
                ]}
                docs={spa ? [
                  <DocBox label={t('pf_upload_attorney')} path={ATTORNEY_DOC} kind="attorney" ctl={ctl} onRead={readDonee} onRemoved={clearDonee} onHover={hoverOn('donee', ['age'])} />,
                ] : []}
              />
              {witnessList.length > 0 && (
                <div style={g3}>
                  {[1, 2].map(n => (
                    <div key={n}>
                      <Pick label={t('pf_poa_witness_n', { n })} opt value={witnessVal(n)} onChange={pickWitness(n)} options={witnessOpts(n)} />
                      {witnessClash(n) && <div style={badHint}>{t('pf_poa_witness_clash')}</div>}
                    </div>
                  ))}
                </div>
              )}
              {(!witnessList.length || witnessVal(1) === POA_OTHER) && (
                <div style={g3}>
                  <Txt label={t('pf_poa_witness_n', { n: 1 })} opt value={poa.witness1} onChange={v => setPoa({ witness1: v })} input={{ autoCapitalize: 'words' }} />
                  <Txt label={t('pf_poa_witness_th_n', { n: 1 })} opt value={poa.witness1Th} onChange={v => setPoa({ witness1Th: v })} />
                </div>
              )}
              {(!witnessList.length || witnessVal(2) === POA_OTHER) && (
                <div style={g3}>
                  <Txt label={t('pf_poa_witness_n', { n: 2 })} opt value={poa.witness2} onChange={v => setPoa({ witness2: v })} input={{ autoCapitalize: 'words' }} />
                  <Txt label={t('pf_poa_witness_th_n', { n: 2 })} opt value={poa.witness2Th} onChange={v => setPoa({ witness2Th: v })} />
                </div>
              )}
              <div style={g3}>
                {madeBox('madeAt', t('pf_poa_made_at'), 'en')}
                {madeBox('madeAtTh', t('pf_poa_made_at_th'), 'th')}
                <Txt label={t('pf_poa_date')} opt type="date" value={poa.date} onChange={v => setPoa({ date: v, dateDefault: '' })} />
              </div>
            </>
          )}
        </div>
      )}
      {/* The Sale & Purchase Agreement itself, under the boxes it reads (report 4ba8eca0) — one for
          each agreement the answers call for (report b16d1d09). Each is a bubble of its own straight
          after the Agreement card, no longer drawn inside it (report b16d1d09, 3rd opening: "Separate
          each agreement to make it its own separate bubble"). Staff only — the client's links hand
          over no `spa`. The form's POAs (report 2ce6d3b5) follow them, and show while a POA is being
          done even when no agreement is being prepared. */}
      {(data.doAgreements === 'Yes' || data.doPoa === 'Yes') && spa && <SpaAgreements spa={spa} sales={data.doAgreements === 'Yes'} />}
    </div>
  );
}

// ── The Lease Agreement's Thai lines (report eab2c428, docs/lease-blueprint.md §3) ────────────────
// Under the Lease section's boxes, staff only as the agreement is: one Thai box for each value the Lease
// Agreement prints in Thai, beside the English it prints there until that box is filled — read-only, from
// the saved form (the server's leaseThaiLines, the agreement's own readings), read again after each save.
// 'AI: fill the Thai' saves what is on screen first, has the English of every Thai box still empty put
// into Thai, and fills only the boxes that are STILL empty when the answers come back, as a document's
// reading fills only blanks: a Thai value somebody typed is never overwritten. A box the AI filled is
// yellow, as the agreement's highlight is, until its Checked tick is ticked (data.lease.thChecked) — that
// is what takes the highlight off the agreement; typing in a box is checking it. The Thai boxes are
// optional: the English prints until a Thai value exists. A line whose English is blank, or which the
// land's and the building's own leases print differently (`split`, which no one Thai value can serve),
// shows only while it still holds Thai, so that it can be emptied.
type ThaiLine = { key: string; en: string; split: boolean; n?: number };
// Each line's label: the box above that its English comes from, or the party and what of theirs it is. A Lessor is
// numbered as the lease prints them (`n`): the number in their key is the seller's place on the form, and the lease may
// leave out a seller listed before them (report 702508f6).
// `use` is there only for a Permitted use typed before it was a choice: a choice prints its own Thai.
const THAI_LABEL: Record<string, string> = {
  lessorSign: 'pf_signs_as', landAddr: 'pf_prop_address', landOffice: 'pf_lease_th_land_office',
  owners: 'pf_lease_th_owners', use: 'pf_lease_use', conds: 'pf_cond_count',
};
const THAI_PARTY_LABEL: Record<string, string> = {
  lessorName: 'pf_lease_th_lessor_name', lessorAddr: 'pf_lease_th_lessor_addr',
  lesseeName: 'pf_lease_th_lessee_name', lesseeAddr: 'pf_lease_th_lessee_addr', lesseeNat: 'pf_lease_th_lessee_nat',
};
const thaiLabel = (t: TFunction, key: string, n?: number): string => {
  const m = /^(lessor|lessee)(\d+)(Name|Addr|Nat)$/.exec(key);
  const party = m ? THAI_PARTY_LABEL[m[1] + m[3]] : '';
  return party ? t(party, { n: n || Number(m[2]) + 1 }) : t(THAI_LABEL[key] || key);
};
// The agreement's own highlight for a Thai value the AI filled (services/leaseDocument.ts, mark.ai).
const aiInp: React.CSSProperties = { background: '#FFF3A3', color: '#1F2937' };
/** `thChecked` with `key` ticked or not. */
const thaiChecked = (c: any, key: string, on: boolean): any => ({ ...(c && typeof c === 'object' ? c : {}), [key]: on });
const thaiOf = (l: any): any => (l?.th && typeof l.th === 'object' ? l.th : {});
function LeaseThai({ spa, lease, update }: { spa: SpaCtl; lease: any; update: (fn: (l: any) => any) => void }) {
  const { t } = useTranslation();
  const [lines, setLines] = useState<ThaiLine[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ text: '', bad: false });
  // The lease on screen right now: the AI's answers are counted against it before they are applied.
  const live = useRef<any>(lease);
  live.current = lease;
  const seq = useRef(0);
  const load = async () => {
    const n = ++seq.current;
    try {
      const r: any = await api.pfLeaseThai(spa.id);
      if (n === seq.current) setLines(Array.isArray(r?.lines) ? r.lines : []);
    } catch { /* the lines on screen stay */ }
  };
  useEffect(() => { load(); }, [spa.id]); // eslint-disable-line
  const seenSaves = useRef(spa.saves);
  useEffect(() => {
    if (spa.saves === seenSaves.current) return;
    seenSaves.current = spa.saves;
    const h = setTimeout(() => { load(); }, 800);
    return () => clearTimeout(h);
  }, [spa.saves]); // eslint-disable-line
  const TH = thaiOf(lease);
  const CHECKED: any = lease?.thChecked && typeof lease.thChecked === 'object' ? lease.thChecked : {};
  const typeThai = (key: string, v: string) => update((l: any) => ({ ...l, th: { ...thaiOf(l), [key]: v }, thChecked: thaiChecked(l.thChecked, key, !blank(v)) }));
  const tick = (key: string, on: boolean) => update((l: any) => ({ ...l, thChecked: thaiChecked(l.thChecked, key, on) }));
  const fill = async () => {
    setBusy(true); setMsg({ text: t('pf_lease_th_filling'), bad: false });
    try {
      await spa.flush();
      const r: any = await api.pfLeaseTranslate(spa.id);
      const got: Record<string, string> = r?.th && typeof r.th === 'object' ? r.th : {};
      const keys = Object.keys(got).filter(k => !blank(got[k]) && blank(thaiOf(live.current)[k]));
      if (keys.length) {
        update((l: any) => {
          const th = { ...thaiOf(l) };
          let checked = l.thChecked;
          for (const k of keys) if (blank(th[k])) { th[k] = String(got[k]).trim(); checked = thaiChecked(checked, k, false); }
          return { ...l, th, thChecked: checked };
        });
      }
      const left = Number(r?.left) || 0;
      setMsg({ text: [t(keys.length ? 'pf_lease_th_filled' : 'pf_lease_th_nothing', { n: keys.length }), left ? t('pf_lease_th_left', { n: left }) : ''].filter(Boolean).join(' '), bad: false });
    } catch (e: any) { setMsg({ text: e?.message || '', bad: true }); }
    setBusy(false);
  };
  // A line shows while it prints English, or while it still holds Thai.
  const shown = lines.filter(l => !blank(TH[l.key]) || (!l.split && !blank(l.en)));
  return (
    <div style={{ display: 'grid', gap: 10, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={hdr}>{t('pf_lease_th')}</div>
        <div style={{ flex: 1 }} />
        <button type="button" disabled={busy} onClick={fill} style={{ ...btnG, padding: '6px 12px', fontSize: 12.5, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: busy ? 'default' : 'pointer' }}>
          <i className={`ti ${busy ? 'ti-loader' : 'ti-sparkles'}`} aria-hidden />{t('pf_lease_th_fill')}
        </button>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('pf_lease_th_hint')}</div>
      {msg.text && <div style={{ fontSize: 12.5, color: msg.bad ? '#B42318' : 'var(--text-tertiary)' }}>{msg.text}</div>}
      {shown.map(l => {
        const v = String(TH[l.key] ?? '');
        const has = !blank(v);
        return (
          <div key={l.key}>
            <Lbl text={thaiLabel(t, l.key, l.n)} req={false} />
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) 96px', gap: 8, alignItems: 'center' }}>
              <input style={{ ...inp, ...roInp }} value={l.en} title={l.en} readOnly tabIndex={-1} />
              <input style={{ ...inp, fontFamily: 'Sarabun, sans-serif', ...(has && !CHECKED[l.key] ? aiInp : {}) }} placeholder={t('pf_lease_th')}
                value={v} onChange={e => typeThai(l.key, e.target.value)} />
              {has ? (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-secondary)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!CHECKED[l.key]} onChange={e => tick(l.key, e.target.checked)} style={{ width: 15, height: 15, margin: 0, flexShrink: 0 }} />
                  {t('pf_lease_th_checked')}
                </label>
              ) : <span />}
            </div>
            {l.split && <div style={{ ...checkHint, marginTop: 3 }}>{t('pf_lease_th_split')}</div>}
          </div>
        );
      })}
    </div>
  );
}

// ── The Sale & Purchase Agreement, under the boxes (report 4ba8eca0, 5th opening) ─────────────────
// What a POA's Document tab is: the agreement as a white printed page, with Open PDF and Download PDF.
// "Original (system)" is the agreement the system builds from the saved answers — read again after each
// save, so it follows the boxes — and folded under it are the versions staff saved, newest first, each
// with who saved it and when. Edit wording lets the page itself be typed into, as in Word; Save as new
// version keeps it under the day's date (2026-10-22.1, .2 …) and never writes over an earlier one.
// The look and the wording are the agreement's own (services/spaDocument.ts): nothing here restyles it.
// `agr` is which of the form's agreements this panel is (SpaAgreements below): its page, PDFs and
// versions are its own.
type SpaVer = { label: string; byName: string; at: number };
/** `scroller` is the staff form's own scroller (PFForm), which the agreements' corner arrows move. */
type SpaCtl = { id: string; reportNo: string; flush: () => Promise<any>; saves: number; scroller: React.RefObject<HTMLDivElement> };
/** One agreement a form calls for: a sale's key is '' (the one combined agreement), 'land' or 'building'; a lease's 'lease', 'lease-land' or 'lease-building'. */
/** A POA (report 2ce6d3b5): key 'poa-<buyer block uid>-<type>', `part` its POA type, `name` the buyer who grants it. */
type SpaAgr = { key: string; kind: 'sale' | 'lease' | 'poa'; part: string; name?: string };
const COMBINED_SPA: SpaAgr = { key: '', kind: 'sale', part: '' };
/** A Property type answer (or the part an agreement covers) in the operator's language. */
const propTypeLabel = (t: TFunction, v: string): string => { const p = PROP_TYPES.find(x => x[0] === v); return p ? t(p[1]) : v; };
/** What a POA is for, by its POA type (the POA module's: server/src/services/poaTemplates.ts). */
const POA_TXN: Record<string, string> = { buy_condo: 'pf_poa_txn_buy_condo', buy_land: 'pf_poa_txn_buy_land', buy_building: 'pf_poa_txn_buy_building',
  lease_land: 'pf_poa_txn_lease_land', sublease_land: 'pf_poa_txn_sublease_land' };
/** An agreement's title, on its bubble and on the corner arrows that jump to it. The combined agreement
 *  keeps its one title; a part's own says which part it sells, and a lease which part it lets; a POA whose it is and what for. */
const agrTitle = (t: TFunction, a: SpaAgr): string => (a.kind === 'poa' ? t('pf_poa_for', { name: a.name || '…', what: POA_TXN[a.part] ? t(POA_TXN[a.part]) : a.part })
  : a.kind === 'lease' ? t('pf_lease_for', { part: propTypeLabel(t, a.part) })
  : a.key ? t('pf_spa_for', { part: propTypeLabel(t, a.part) }) : t('pf_spa'));
/** An agreement's bubble, which the corner arrows jump to. */
const agrId = (a: SpaAgr): string => `pf-agr-${a.key || 'combined'}-${a.kind}`;
const spaLoadingBox: React.CSSProperties = { padding: '10px 12px', background: 'var(--bg-app)', border: '1px dashed var(--border)', borderRadius: 8, fontSize: 12.5, color: 'var(--text-tertiary)' };
// The page's margins on screen only; printing takes the agreement's own @page margins, which these mirror
// (AGREEMENT_MARGINS, server/src/services/spaDocument.ts — keep the two in step). Taken off again before a
// version is saved.
const SPA_SCREEN = '<style data-pf-screen="1">@media screen { body { padding: 18mm 10mm 20mm; } }</style>';
const withScreen = (html: string): string => (/<\/head>/i.test(html) ? html.replace(/<\/head>/i, `${SPA_SCREEN}</head>`) : SPA_SCREEN + html);
// The schedules on the page, and the text's mentions of them (report 900aff18: "Links that take you directly to
// schedules with a 'Return to Clause' button that appears in the bottom right of the screen. Include a link and return
// for Schedule 4 as well."). A schedule is a heading 'SCHEDULE N' — the sale's, 'SCHEDULE NA', 'NB' … when it is
// lettered — or 'Schedule N — …', the lease's. A mention is 'Schedule N' or 'Schedules N' in the text; a range,
// 'Schedules 1–6', is one mention of its first.
const SCHEDULE_HEAD = /^\s*schedule\s+(\d+)([a-z]?)(?![a-z0-9])/i;
const SCHEDULE_REF = /\bschedules?\s+(\d+)([a-z]?)(?![a-z0-9])(?:\s*[–-]\s*\d+(?![a-z0-9]))?/gi;
/** Takes the schedule links out of a page again, each one's words left where they stood. */
const unlinkSchedules = (root: ParentNode): void => {
  const links = root.querySelectorAll('a[data-pf-sched]');
  for (let i = 0; i < links.length; i++) {
    const a = links[i];
    const p = a.parentNode;
    if (!p) continue;
    while (a.firstChild) p.insertBefore(a.firstChild, a);
    p.removeChild(a);
    p.normalize();
  }
};

function SpaPanel({ spa, agr }: { spa: SpaCtl; agr: SpaAgr }) {
  const { t } = useTranslation();
  const title = agrTitle(t, agr);
  const frame = useRef<HTMLIFrameElement>(null);
  const [page, setPage] = useState('');
  const [gen, setGen] = useState(0);                // a fresh page — Cancel throws the typing away
  const [vers, setVers] = useState<SpaVer[]>([]);
  const [sel, setSel] = useState('');               // '' = Original (system), else a version's label
  const [unfold, setUnfold] = useState(false);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<'' | 'open' | 'download' | 'save'>('');
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const seq = useRef(0);
  const load = async (label: string) => {
    const n = ++seq.current;
    setLoading(true); setErr('');
    try {
      if (!label) await spa.flush();
      const h = await api.pfSpa(spa.id, label, agr.key);
      if (n === seq.current) setPage(withScreen(h));
    } catch (e: any) { if (n === seq.current) setErr(e?.message || ''); }
    if (n === seq.current) setLoading(false);
  };
  useEffect(() => {
    api.pfSpaVersions(spa.id, agr.key).then((r: any) => setVers(Array.isArray(r?.versions) ? r.versions : [])).catch(() => { /* the Original still shows */ });
  }, [spa.id, agr.key]);
  useEffect(() => { load(sel); }, [sel]); // eslint-disable-line
  // The Original follows the boxes: read again shortly after each save — never over typing in progress.
  const seenSaves = useRef(spa.saves);
  useEffect(() => {
    if (spa.saves === seenSaves.current) return;
    seenSaves.current = spa.saves;
    if (sel || editing) return;
    const h = setTimeout(() => { load(''); }, 800);
    return () => clearTimeout(h);
  }, [spa.saves]); // eslint-disable-line

  // The schedule links (report 900aff18), made once the page has loaded and only while Edit is off: every mention of a
  // schedule the page prints becomes a link to that schedule's heading, the first of 'NA', 'NB' … when it is lettered.
  // The numbers are read from the page, so they follow the agreement's own (change (m), services/spaDocument.ts) and a
  // saved version's. The page runs no scripts (sandbox) and a #anchor in it would resolve against the app's address, so
  // the panel makes the jump: it scrolls the page's own window — never scrollIntoView, which would move the form too —
  // and keeps the link the reader left by, and where it stood, for Return to Clause. The links are for the screen only
  // (data-pf-screen, as SPA_SCREEN), in the headings' own colour, and come out again before Edit wording and before a
  // version is saved, so no saved version and no PDF carries them.
  const [back, setBack] = useState('');             // Return to Clause's arrow; '' = no button
  const from = useRef<{ el: Element; y: number } | null>(null);
  const onLink = useRef<(e: MouseEvent) => void>(); // the page's one click listener: a page linked again keeps one
  const linkSchedules = () => {
    from.current = null; setBack('');
    const d = frame.current?.contentDocument;
    const w = frame.current?.contentWindow;
    if (!d || !d.body || !w || d.designMode === 'on') return;
    if (onLink.current) d.removeEventListener('click', onLink.current);
    const heads: Record<string, Element> = {};
    const hs = d.querySelectorAll('h2, h3');
    for (let i = 0; i < hs.length; i++) {
      const m = SCHEDULE_HEAD.exec(hs[i].textContent || '');
      if (!m) continue;
      const lettered = m[1] + m[2].toUpperCase();
      if (!heads[m[1]]) heads[m[1]] = hs[i];
      if (!heads[lettered]) heads[lettered] = hs[i];
    }
    if (!Object.keys(heads).length) return;
    // The text's words only: a heading, a schedule's own among them, stays plain.
    const texts: Text[] = [];
    const walk = d.createTreeWalker(d.body, NodeFilter.SHOW_TEXT);
    for (let n = walk.nextNode(); n; n = walk.nextNode()) {
      if (n.parentElement && !n.parentElement.closest('h1, h2, h3, h4, a, style, script')) texts.push(n as Text);
    }
    for (const n of texts) {
      const s = n.data;
      const bits: Node[] = [];
      let at = 0;
      SCHEDULE_REF.lastIndex = 0;
      for (let m = SCHEDULE_REF.exec(s); m; m = SCHEDULE_REF.exec(s)) {
        const key = heads[m[1] + m[2].toUpperCase()] ? m[1] + m[2].toUpperCase() : m[1];
        if (!heads[key]) continue;
        const a = d.createElement('a');
        a.setAttribute('data-pf-screen', '1');
        a.setAttribute('data-pf-sched', key);
        a.style.cssText = `color: ${w.getComputedStyle(heads[key]).color}; text-decoration: underline; cursor: pointer;`;
        a.textContent = m[0];
        if (m.index > at) bits.push(d.createTextNode(s.slice(at, m.index)));
        bits.push(a);
        at = m.index + m[0].length;
      }
      const p = n.parentNode;
      if (!bits.length || !p) continue;
      if (at < s.length) bits.push(d.createTextNode(s.slice(at)));
      for (const b of bits) p.insertBefore(b, n);
      p.removeChild(n);
    }
    const onClick = (e: MouseEvent) => {
      const el = e.target as Element | null;
      const a = el && el.nodeType === 1 ? el.closest('a[data-pf-sched]') : null;
      const h = a && heads[a.getAttribute('data-pf-sched') || ''];
      if (!a || !h) return;
      e.preventDefault();
      const ay = a.getBoundingClientRect().top;
      const hy = h.getBoundingClientRect().top;
      from.current = { el: a, y: ay };
      setBack(hy > ay ? '↑' : '↓');
      w.scrollTo({ top: w.scrollY + hy - 12, behavior: 'smooth' });
    };
    onLink.current = onClick;
    d.addEventListener('click', onClick);
  };
  // Return to Clause: the link the reader left by goes back where it stood in the page's window, and the button goes.
  const toClause = () => {
    const w = frame.current?.contentWindow;
    const f = from.current;
    if (w && f && f.el.isConnected) w.scrollTo({ top: w.scrollY + f.el.getBoundingClientRect().top - f.y, behavior: 'smooth' });
    from.current = null; setBack('');
  };

  const startEdit = () => {
    const d = frame.current?.contentDocument;
    if (!d) return;
    unlinkSchedules(d);
    from.current = null; setBack('');
    d.designMode = 'on';
    setEditing(true); setNote(''); setErr('');
  };
  // Cancel: the typing goes (a fresh page), and the page is read again — the boxes may have been saved meanwhile.
  const cancelEdit = () => { setEditing(false); setGen(g => g + 1); load(sel); };
  const saveVersion = async () => {
    const d = frame.current?.contentDocument;
    if (!d || !d.documentElement) return;
    const root = d.documentElement.cloneNode(true) as HTMLElement;
    const extra = root.querySelectorAll('style[data-pf-screen]');
    for (let i = 0; i < extra.length; i++) extra[i].remove();
    unlinkSchedules(root);
    setBusy('save'); setErr('');
    try {
      const r: any = await api.pfSpaSaveVersion(spa.id, `<!doctype html>${root.outerHTML}`, agr.key);
      d.designMode = 'off';
      linkSchedules();                              // the saved page may read back the same, and not load again
      setVers(Array.isArray(r?.versions) ? r.versions : []);
      setEditing(false); setUnfold(true);
      setNote(t('pf_spa_saved_as', { label: r?.label || '' }));
      setSel(String(r?.label || ''));
    } catch (e: any) { setErr(e?.message || ''); }
    setBusy('');
  };
  // The PDF is printed from what is saved: the Original from the saved answers, a version as it was kept.
  const pdf = async (kind: 'open' | 'download') => {
    setBusy(kind); setErr(''); setNote('');
    try {
      if (!sel) await spa.flush();
      if (kind === 'download') await api.pfSpaDownloadPdf(spa.id, sel, `${(agr.kind === 'poa' ? [spa.reportNo || 'PROP', 'POA', sel] : [spa.reportNo || 'PROP', agr.kind === 'lease' ? 'Lease' : 'SPA', agr.key ? agr.part : '', sel]).filter(Boolean).join('-')}.pdf`, agr.key);
      else if (!(await api.pfSpaOpenPdf(spa.id, sel, agr.key))) setErr(t('pf_spa_popup'));
    } catch (e: any) { setErr(e?.message || ''); }
    setBusy('');
  };

  const pick = (label: string, main: string, sub: string) => {
    const on = sel === label;
    return (
      <button key={label || '_original'} type="button" disabled={editing} onClick={() => { setNote(''); setSel(label); }}
        style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 10, textAlign: 'left', padding: '6px 10px', paddingLeft: label ? 34 : 10, border: 'none', borderRadius: 6, fontSize: 13, cursor: editing ? 'default' : 'pointer', background: on ? PROP_CARD_HOVER : 'transparent', color: on ? 'var(--accent)' : 'var(--text-primary)' }}>
        <span style={{ fontWeight: 700 }}>{main}</span>
        {sub && <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{sub}</span>}
      </button>
    );
  };
  const btn = (on: boolean): React.CSSProperties => ({ ...btnG, fontSize: 12.5, display: 'inline-flex', alignItems: 'center', gap: 6, opacity: on ? 1 : 0.6, cursor: on ? 'pointer' : 'default' });
  const idle = busy === '' && !loading;
  // No rule above the title: the panel's own bubble (SpaAgreements) is what parts it from the boxes now.
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div style={hdr}>{title}</div>
        <div style={{ flex: 1 }} />
        {editing ? (
          <>
            <button type="button" disabled={busy !== ''} onClick={saveVersion}
              style={{ ...btn(busy === ''), background: 'var(--accent)', borderColor: 'var(--accent)', color: '#fff' }}>
              <i className={`ti ${busy === 'save' ? 'ti-loader' : 'ti-device-floppy'}`} aria-hidden />{t('pf_spa_save_version')}
            </button>
            <button type="button" disabled={busy !== ''} onClick={cancelEdit} style={btn(busy === '')}>{t('pf_spa_cancel_edit')}</button>
          </>
        ) : (
          <>
            <button type="button" disabled={!idle || !page} onClick={startEdit} style={btn(idle && !!page)}>
              <i className="ti ti-pencil" aria-hidden />{t('pf_spa_edit')}
            </button>
            <button type="button" disabled={busy !== ''} onClick={() => pdf('open')} style={btn(busy === '')}>
              {busy === 'open' ? `⏳ ${t('pf_spa_generating')}` : t('pf_spa_open_pdf')}
            </button>
            <button type="button" disabled={busy !== ''} onClick={() => pdf('download')} style={btn(busy === '')}>
              {busy === 'download' ? `⏳ ${t('pf_spa_generating')}` : t('pf_spa_download_pdf')}
            </button>
          </>
        )}
      </div>
      {/* Which page is shown: the Original, with the saved versions folded beneath it */}
      <div style={{ display: 'grid', gap: 2, border: '1px solid var(--border)', borderRadius: 8, padding: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {pick('', t('pf_spa_original'), '')}
          {vers.length > 0 && (
            <button type="button" onClick={() => setUnfold(u => !u)} aria-expanded={unfold}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', border: 'none', borderRadius: 6, background: 'transparent', color: 'var(--text-secondary)', fontSize: 12.5, cursor: 'pointer', flexShrink: 0 }}>
              <i className={`ti ${unfold ? 'ti-chevron-down' : 'ti-chevron-right'}`} aria-hidden />{t('pf_spa_versions_n', { n: vers.length })}
            </button>
          )}
        </div>
        {unfold && vers.map(v => (
          <div key={v.label} style={{ display: 'flex' }}>{pick(v.label, v.label, [v.byName, fmtDateTime(v.at)].filter(Boolean).join(' · '))}</div>
        ))}
      </div>
      {editing && <div style={noteBox}><i className="ti ti-pencil" aria-hidden style={{ marginRight: 5 }} />{t('pf_spa_edit_hint')}</div>}
      {!editing && sel && <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('pf_spa_version_note')}</div>}
      {note && <div style={noteBox}>{note}</div>}
      {err && <div style={errBox}>{err}</div>}
      {page
        ? (
          <div>
            <iframe key={gen} ref={frame} title={title} srcDoc={page} sandbox="allow-same-origin" onLoad={linkSchedules}
              style={{ display: 'block', width: '100%', height: 640, border: `1px solid ${editing ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 8, background: '#fff', opacity: loading ? 0.6 : 1 }} />
            {/* Return to Clause (report 900aff18): in the page's bottom-right corner, clear of its scroll bar, in the
                agreements' corner arrows' own style. When the page runs past the foot of the form it stays in
                sight, held 76px (62 + 14) above that edge, which also keeps it clear of those arrows (SpaAgreements:
                26px up, one button high). */}
            {back && (
              <div style={{ position: 'sticky', bottom: 62, height: 0 }}>
                <button type="button" onClick={toClause} style={{ ...agrFab, position: 'absolute', right: 26, bottom: 14 }}>{back} {t('pf_agr_back_clause')}</button>
              </div>
            )}
          </div>
        )
        : loading && <div style={spaLoadingBox}>{t('pf_spa_loading')}</div>}
    </div>
  );
}

// ── One bubble per agreement (report b16d1d09) ───────────────────────────────────────────────────
// Which agreements the saved answers call for comes from the server (agreementsOf in
// services/spaDocument.ts): the one combined Sale & Purchase Agreement, as before — or, when different
// buyers take the land and the building, the land's and the building's own, each with its own page,
// PDFs and edited versions — and for the Lessee ticks a Lease Agreement that is a placeholder only:
// its wording is not in the system yet, so nothing is printed for it. Read again after each save, as
// the Original is. Should the list not come, the one combined agreement shows, as before.
// Each agreement is a bubble of its own, the form's card as every other section's, one under another
// after the Agreement card (report b16d1d09, 3rd opening: "Separate each agreement to make it its own
// separate bubble and provide a clickable scroller like the due diligence report (bottom right-hand
// corner)"). The scroller is Due Diligence's section-hopper in the form's own colour: ↑ names the
// agreement before the one being read and ↓ the one after it, so the reader knows where a jump lands;
// above the agreements ↓ names the first, and ↑ Top goes back to the top of the form. The one being
// read is the last bubble whose top has passed a third of the way down the form's scroller, worked out
// again as it scrolls — or, once the form is scrolled to its very end, the last bubble: a short last
// one (a lease waiting for its wording) never reaches that third. ↑ is the nearest agreement before it
// whose top is still above the screen, and at the very end there is no ↓, so no arrow is ever one that
// cannot move the page (Due Diligence's "won't take me any further down", report 7d7c834f). A
// direction with nothing left in it has no button.
const agrFab: React.CSSProperties = { padding: '11px 18px', borderRadius: 999, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontFamily: RC, fontSize: 13.5, boxShadow: '0 4px 14px rgba(20,40,70,.35)', cursor: 'pointer', whiteSpace: 'nowrap' };
/** `sales` = agreements are being prepared: without it only the form's POAs show (report 2ce6d3b5). */
function SpaAgreements({ spa, sales }: { spa: SpaCtl; sales: boolean }) {
  const { t } = useTranslation();
  const [all, setAgrs] = useState<SpaAgr[] | null>(null);
  const agrs = all && all.filter(a => sales || a.kind === 'poa');
  const seq = useRef(0);
  const load = async () => {
    const n = ++seq.current;
    try {
      const r: any = await api.pfSpaAgreements(spa.id);
      const l: SpaAgr[] = Array.isArray(r?.agreements) ? r.agreements : [];
      if (n === seq.current) setAgrs(l.length ? l : [COMBINED_SPA]);
    } catch { if (n === seq.current) setAgrs(prev => prev || [COMBINED_SPA]); }
  };
  useEffect(() => { load(); }, [spa.id]); // eslint-disable-line
  const seenSaves = useRef(spa.saves);
  useEffect(() => {
    if (spa.saves === seenSaves.current) return;
    seenSaves.current = spa.saves;
    const h = setTimeout(() => { load(); }, 800);
    return () => clearTimeout(h);
  }, [spa.saves]); // eslint-disable-line
  // The corner scroller: which bubble ↑ and ↓ jump to (-1 = no button), and whether the form is at its
  // very top, read again on each scroll of the form.
  const sc = spa.scroller;
  const ids = (agrs || []).map(agrId);
  const idKey = ids.join('|');
  const [nav, setNav] = useState({ up: -1, down: -1, top: true });
  useEffect(() => {
    const el = sc.current;
    if (!el || !ids.length) return;
    const on = () => {
      const edge = el.getBoundingClientRect().top;
      const tops = ids.map(id => { const b = document.getElementById(id); return b ? b.getBoundingClientRect().top - edge : Infinity; });
      const end = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
      let cur = -1;                                // the one being read; -1 = still above them all
      tops.forEach((y, i) => { if (y <= el.clientHeight / 3) cur = i; });
      if (end) cur = ids.length - 1;
      let up = -1;
      for (let i = cur - 1; i >= 0; i--) if (tops[i] < -2) { up = i; break; }
      const down = !end && cur + 1 < ids.length ? cur + 1 : -1;
      const top = el.scrollTop <= 1;
      setNav(p => (p.up === up && p.down === down && p.top === top ? p : { up, down, top }));
    };
    on();
    el.addEventListener('scroll', on, { passive: true });
    window.addEventListener('resize', on);
    return () => { el.removeEventListener('scroll', on); window.removeEventListener('resize', on); };
  }, [idKey, sc]); // eslint-disable-line
  const jump = (i: number) => { document.getElementById(ids[i])?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
  const prev = agrs && nav.up >= 0 ? agrs[nav.up] : null;
  const next = agrs && nav.down >= 0 ? agrs[nav.down] : null;
  return (
    <div id="pf-spa" style={{ display: 'grid', gap: 14 }}>
      {!agrs && <div style={spaLoadingBox}>{t('pf_spa_loading')}</div>}
      {(agrs || []).map(a => (
        <div key={agrId(a)} id={agrId(a)} style={{ ...card, display: 'grid', gap: 10 }}>
          {/* A lease is printed from the firm's lease template now (report eab2c428), so it gets the same
              page, PDFs and versions as a sale — its own, under its own key. */}
          <SpaPanel spa={spa} agr={a} />
        </div>
      ))}
      {ids.length > 0 && (prev || next || !nav.top) && (
        <div style={{ position: 'fixed', right: 22, bottom: 26, zIndex: 60, display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 8, maxWidth: 'calc(100vw - 44px)' }}>
          {prev && <button type="button" onClick={() => jump(nav.up)} style={agrFab}>↑ {agrTitle(t, prev)}</button>}
          {next && <button type="button" onClick={() => jump(nav.down)} style={agrFab}>↓ {agrTitle(t, next)}</button>}
          {!nav.top && <button type="button" onClick={() => sc.current?.scrollTo({ top: 0, behavior: 'smooth' })} style={agrFab}>↑ {t('pf_agr_top')}</button>}
        </div>
      )}
    </div>
  );
}

// Whole-object autosave with the revision this screen loaded (the will's rule): a save over somebody
// else's newer one is refused and says so. Nothing is sent until an answer actually changes, so just
// opening a form — or reading it through the view link — leaves it, and its audit trail, as it was
// (unless the staff screen fills a blank Email: from Contacts, else the AI's pick for a company or the
// one address the email history holds for a person — FormBody, reports 33b5ec4e, e2975736, 540019ef — a buyer's
// date of birth and blank Age from an upload read again, report beb72eec, or the POA card's empty Thai boxes, report b4f708fa).
// Saves run one after another, each with the revision the last one returned. `flush` saves what is on
// screen now, without waiting for the pause — a status change waits for it, so the server checks the
// same answers the screen shows. `saves` counts the saves that went through (the agreement's page under
// the boxes is read again after each one). The default Agreement and POA dates (report 90208a98) move in `set`, in a
// change somebody makes (defaultDates.ts) and only while the form `isNew`: opening a form leaves them as they are too.
function useAutosave(saveFn: (data: any, baseRev: number) => Promise<any>, isNew: boolean) {
  const [data, setData] = useState<any>(null);
  const [save, setSave] = useState<'' | 'saving' | 'saved'>('');
  const [saves, setSaves] = useState(0);
  const [err, setErr] = useState('');
  const saved = useRef('');
  const rev = useRef(0);
  const chain = useRef<Promise<any>>(Promise.resolve());
  const latest = useRef<any>(null);
  latest.current = data;
  // What the default dates follow: the day the server last gave (a view link is given none) and whether the form is New.
  const rule = useRef<DateRule>({ day: '', isNew });
  rule.current = { ...rule.current, isNew };
  const init = (d: any, r: number, day?: any) => { const n = normForm(d); saved.current = JSON.stringify(n); rev.current = r; rule.current = { ...rule.current, day: String(day || '') }; setData(n); };
  // A screen left open overnight: each save says today's default day, and a default date still showing goes on to it, which
  // saves in its turn.
  const freshDay = (day: any) => {
    if (!day || day === rule.current.day) return;
    const r = { ...rule.current, day: String(day) };
    rule.current = r;
    setData((d: any) => (d == null ? d : rollDates(d, r)));
  };
  // One save, queued behind those already running; answers that are already saved are not sent again.
  const push = (d: any): Promise<any> => {
    chain.current = chain.current.then(() => {
      const json = JSON.stringify(d);
      if (json === saved.current) return undefined;
      return saveFn(d, rev.current)
        .then((r: any) => { saved.current = json; rev.current = Number(r?.rev || 0); setSave('saved'); setSaves(n => n + 1); setErr(''); freshDay(r?.defaultDay); })
        .catch((e: any) => { setSave(''); setErr(e?.message || ''); });
    });
    return chain.current;
  };
  useEffect(() => {
    if (data == null) return;
    if (JSON.stringify(data) === saved.current) return;
    setSave('saving');
    const h = setTimeout(() => { push(data); }, 700);
    return () => clearTimeout(h);
  }, [data]); // eslint-disable-line
  const flush = (): Promise<any> => (latest.current == null ? chain.current : push(latest.current));
  const set = (fn: (d: any) => any, auto?: boolean) => {
    const r = rule.current;
    setData((d: any) => (d == null ? d : auto ? fn(d) : staffChange(d, fn, r, agreementShows)));
  };
  // A form moving on from New: the last change its default dates take, on the day the server has just given. Sets
  // `latest` itself, so that a flush straight after saves them.
  const settleDates = (day: any) => {
    const r = { day: String(day || rule.current.day), isNew: true };
    rule.current = { ...rule.current, day: r.day };
    if (latest.current == null) return;
    latest.current = staffChange(latest.current, (d: any) => d, r, agreementShows);
    setData(latest.current);
  };
  return { data, set, init, save, saves, err, setErr, flush, settleDates };
}

// ── Staff: one form ──────────────────────────────────────────────────────────────────────────────
function PFForm({ id, onBack, toSpa }: { id: string; onBack: () => void; toSpa?: boolean }) {
  const { t } = useTranslation();
  const [meta, setMeta] = useState<any>(null);
  const [files, setFiles] = useState<any[]>([]);
  const [status, setStatus] = useState('new');
  const [note, setNote] = useState('');
  const st = useAutosave((d, baseRev) => api.pfUpdate(id, { data: d, baseRev }), status === 'new');
  useEffect(() => {
    api.pfGet(id).then((r: any) => {
      setMeta(r); setFiles(r.files || []); setStatus(r.status || 'new');
      st.init(r.data, Number(r.data_rev || 0), r.defaultDay);
    }).catch((e: any) => st.setErr(e.message));
  }, [id]); // eslint-disable-line
  // Who the POA card's dropdowns offer (report dc39d5a0). Until it answers, or if it fails, the card shows its plain boxes.
  const [poaPeople, setPoaPeople] = useState<PoaPeople | undefined>();
  useEffect(() => { api.pfPoaPeople().then((r: any) => setPoaPeople(r)).catch(() => { /* the plain boxes still work */ }); }, []);
  // Opened from SLF Property → Agreements: straight down to the agreement, once, as soon as it is drawn.
  const atSpa = useRef(false);
  useEffect(() => {
    if (!toSpa || atSpa.current || !st.data) return;
    const el = document.getElementById('pf-spa');
    if (el) { atSpa.current = true; el.scrollIntoView({ block: 'start' }); }
  }, [toSpa, st.data]);
  // Pending and Confirmed need every required box answered (report 2999c435). A move that would leave
  // boxes blank is not made: the boxes are listed instead, and the list shrinks as they are filled in.
  // The server checks the saved form again, so the answers on screen are saved first.
  const [tried, setTried] = useState('');
  const [stErr, setStErr] = useState('');
  const gaps = tried && st.data ? missingBoxes(st.data, t) : [];
  const changeStatus = async (v: string) => {
    setStErr('');
    if (v !== 'new' && st.data && missingBoxes(st.data, t).length) { setTried(v); return; }
    setTried('');
    const prev = status;
    setStatus(v);
    try {
      // The default dates' last change (report 90208a98): today's default day, asked for afresh, as a screen may stay open for days.
      if (prev === 'new' && v !== 'new') st.settleDates(((await api.pfGet(id)) as any)?.defaultDay);
      await st.flush();
      const r: any = await api.pfUpdate(id, { status: v });
      // The Due Diligence report this move started (report 0c21500b): its number goes straight on the Questions card.
      if (r?.dd_report) setMeta((m: any) => ({ ...m, dd_report: r.dd_report }));
    } catch (e: any) { setStatus(prev); setStErr(e?.message || ''); }
  };
  // The client's two links. Copied, not emailed — sending them is the operator's own message.
  const copyLink = async (mode: 'edit' | 'view') => {
    try {
      const r: any = await api.pfLink(id, mode);
      if (await copyText(r.url)) setNote(t('pf_copied'));
      else window.prompt(t('pf_copy_manual'), r.url);
    } catch (e: any) { st.setErr(e.message); }
  };
  const ctl: Ctl | null = st.data ? {
    data: st.data, set: st.set, files,
    upload: async (file, path, kind) => { const r: any = await api.pfUpload(id, file, path, kind); if (r?.file) setFiles(l => l.concat([r.file])); return r; },
    remove: async fileId => { await api.pfFileDelete(id, fileId); setFiles(l => l.filter(f => f.id !== fileId)); },
    open: f => { api.pfFileOpen(id, f.id).catch(() => {}); },
    contactEmail: async (name, taxId, middle) => { const r: any = await api.pfContactEmail(name, taxId, middle); return { email: String(r?.email || ''), guess: !!r?.guess, history: !!r?.history }; },
    improveClause: async text => { const r: any = await api.pfClauseImprove(id, text); return String(r?.clause || ''); },
    docNote: async (idNo, first, last) => { const r: any = await api.pfDocNote(idNo, first, last); return { note: String(r?.note || ''), file: String(r?.file?.name || ''), passport: !!r?.passport, ...(r?.fields && typeof r.fields === 'object' ? { fields: r.fields as FileNoteFields } : {}) }; },
    rereadBirth: async fileId => { const r: any = await api.pfReadBirth(id, fileId); return String(r?.birthDate || ''); },
    attorneyBirth: async (name, birthDate, was) => { const r: any = await api.pfPoaAttorneyBirth(name, birthDate, was); setPoaPeople(r); },
    poaTranslate: async () => { await st.flush(); const r: any = await api.pfPoaTranslate(id); return { fills: Array.isArray(r?.fills) ? r.fills : [], left: Number(r?.left) || 0 }; },
  } : null;
  const stLabel = (s: string) => (s === 'new' ? t('pf_st_new') : s === 'pending' ? t('pf_st_pending') : t('pf_st_confirmed'));
  // What scrolls the form: the agreements' corner scroller (SpaAgreements) reads it and moves it.
  const scroller = useRef<HTMLDivElement>(null);
  return (
    <div ref={scroller} style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 16 }}>
      <div style={{ width: '100%', maxWidth: 892, margin: '0 auto', display: 'grid', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" onClick={onBack} style={{ ...btnG, display: 'inline-flex', alignItems: 'center', gap: 6 }}><i className="ti ti-arrow-left" aria-hidden />{t('pf_back')}</button>
          <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{meta?.report_no || ''}</span>
          <select value={status} onChange={e => changeStatus(e.target.value)} title={t('pf_status')} style={{ ...inp, width: 'auto' }}>
            {STATUSES.filter(Boolean).map(s => <option key={s} value={s}>{stLabel(s)}</option>)}
          </select>
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{st.save === 'saving' ? t('pf_saving') : st.save === 'saved' ? t('pf_saved') : ''}</span>
          <div style={{ flex: 1 }} />
          <button type="button" onClick={() => copyLink('edit')} style={{ ...btnG, display: 'inline-flex', alignItems: 'center', gap: 6 }}><i className="ti ti-link" aria-hidden />{t('pf_copy_edit')}</button>
          <button type="button" onClick={() => copyLink('view')} style={{ ...btnG, display: 'inline-flex', alignItems: 'center', gap: 6 }}><i className="ti ti-eye" aria-hidden />{t('pf_copy_view')}</button>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('pf_link_note')}</div>
        {note && <div style={noteBox}>{note}</div>}
        {st.err && <div style={errBox}>{st.err}</div>}
        {stErr && <div style={errBox}>{stErr}</div>}
        {gaps.length > 0 && (
          <div style={errBox}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>{t('pf_missing', { status: stLabel(tried) })}</div>
            {gaps.map((g, i) => <div key={i}>{g.block}: {g.boxes.join(', ')}</div>)}
          </div>
        )}
        {/* The Sale & Purchase Agreement is built from the SAVED form, so what is on screen is saved
            first — the same order a status change uses — and read again after every save (report 4ba8eca0). */}
        {ctl ? <FormBody ctl={ctl} cur={currencyCode()} spa={{ id, reportNo: String(meta?.report_no || ''), flush: st.flush, saves: st.saves, scroller }} poaPeople={poaPeople} ddReport={meta?.dd_report} />
          : !st.err && <div style={{ color: 'var(--text-tertiary)' }}>{t('pf_loading')}</div>}
      </div>
    </div>
  );
}

// ── Staff: the list ──────────────────────────────────────────────────────────────────────────────
function PFList({ onOpen, onNew }: { onOpen: (id: string) => void; onNew: () => void }) {
  const { t } = useTranslation();
  const me = useStore(s => s.me);
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const d: any = await api.pfList(q, status, page, limit); setRows(d.rows || []); setTotal(Number(d.total || 0)); }
    catch { /* the empty list says it */ }
    setLoading(false);
  };
  useEffect(() => { const h = setTimeout(load, 250); return () => clearTimeout(h); }, [q, status, page, limit]); // eslint-disable-line
  const del = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!window.confirm(t('pf_delete_confirm'))) return;
    try { await api.pfDelete(id); setRows(prev => prev.filter(r => r.id !== id)); } catch { /* */ }
  };
  const stLabel = (s: string) => (s === 'new' ? t('pf_st_new') : s === 'pending' ? t('pf_st_pending') : s === 'confirmed' ? t('pf_st_confirmed') : t('pf_all'));
  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 16 }}>
        <div style={{ width: '100%', maxWidth: PROP_LIST_WIDTH, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <input style={{ ...inp, flex: 1, minWidth: 120 }} placeholder={t('pf_search')} value={q} onChange={e => { setPage(1); setQ(e.target.value); }} />
            <div style={{ display: 'flex', gap: 6, flexShrink: 0, overflowX: 'auto' }}>
              {STATUSES.map(s => (
                <button key={s || 'all'} onClick={() => { setPage(1); setStatus(s); }}
                  style={{ ...btnG, whiteSpace: 'nowrap', background: status === s ? 'var(--accent)' : 'var(--bg-surface)', color: status === s ? '#fff' : 'var(--text-secondary)', borderColor: status === s ? 'var(--accent)' : 'var(--border)' }}>
                  {stLabel(s)}
                </button>
              ))}
            </div>
          </div>
          {/* ONE bubble per form, where the list was a single bordered box of rows parted by thin grey
              lines (Calem, report 19597a46, 3rd opening: "Bubble everything in the menu below (except
              Lease Registration), separate the bubbles, and give them all a green border and a green ID
              number"). Due Diligence's and Legal Matters' card exactly — white, a green edge, 10px
              corners, 10px of page between them, the palest green under the pointer (propCard,
              utils/propertyCard.ts). The form number was already the green one, and the loading and
              "no forms" lines wear the card too. Everything on the row keeps its place. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {loading ? <div style={{ ...propCard, padding: 36, textAlign: 'center', color: 'var(--text-tertiary)' }}>{t('pf_loading')}</div>
              : rows.length === 0 ? <div style={{ ...propCard, padding: 36, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t('pf_none')}</div>
                : rows.map(r => (
                  <div key={r.id} onClick={() => onOpen(r.id)} style={{ ...propCard, display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', cursor: 'pointer' }}
                    onMouseEnter={e => (e.currentTarget.style.background = PROP_CARD_HOVER)} onMouseLeave={e => (e.currentTarget.style.background = PROP_CARD_BG)}>
                    <span style={{ color: 'var(--accent)', fontWeight: 700, minWidth: 104 }}>{r.report_no}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                        {r.property_name || [r.buyer_name, r.seller_name].filter(Boolean).join(' ← ') || t('pf_untitled')}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                        {r.property_name && (r.buyer_name || r.seller_name) ? `${[r.buyer_name, r.seller_name].filter(Boolean).join(' ← ')} · ` : ''}
                        {r.updated_at ? `${t('pf_updated')} ${fmtDateTime(r.updated_at)}` : ''}
                        {r.updated_by_name ? ` · ${r.updated_by_name}` : ''}
                        {r.edited ? ` · ${t('pf_edited')}` : ''}
                      </div>
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-tertiary)' }}>{stLabel(r.status)}</span>
                    {me?.role === 'admin' && (
                      <button title={t('pf_delete')} onClick={e => del(e, r.id)} style={{ ...btnG, padding: '4px 9px', fontSize: 14, color: '#b91c1c', flexShrink: 0 }}>
                        <i className="ti ti-trash" aria-hidden />
                      </button>
                    )}
                  </div>
                ))}
          </div>
        </div>
      </div>
      <Pagination page={page} total={total} limit={limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />
      <Fab onClick={onNew} title={t('pf_new')} />
    </div>
  );
}

// ── SLF Property → Agreements (report 4ba8eca0, 5th opening) ──────────────────────────────────────
// Every Sale & Purchase Agreement being prepared — each Property Form whose agreements question is
// answered Yes — as its own record, one bubble each, drawn as the Property Form list's: the form's
// number, its people and property, which agreement the sellers' Property type prints, the newest saved
// version (or the Original) and who last changed it, when. 100 a page. A click opens the form at its
// agreement. Nothing else yet: no status, no check before sending, no Send to client.
// ONE ROW PER AGREEMENT (report b16d1d09): a form whose land and building go to different buyers has
// a row for the land's agreement and one for the building's, each naming its own buyers, and a lease
// the ticks call for has a row saying its wording is pending.
const AGR_COLS = '110px minmax(0, 1fr) 140px 130px 170px';
export function PFAgreements({ onOpen }: { onOpen: (id: string) => void }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const d: any = await api.pfAgreements(page, limit); setRows(d.rows || []); setTotal(Number(d.total || 0)); }
    catch { /* the empty list says it */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, [page, limit]); // eslint-disable-line
  const typeLabel = (v: string): string => propTypeLabel(t, v);
  const ellipsis: React.CSSProperties = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
  return (
    <div className="property-form" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: 'var(--bg-app)', fontFamily: RC }}>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 16 }}>
        <div style={{ width: '100%', maxWidth: PROP_LIST_WIDTH, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: AGR_COLS, gap: 14, padding: '0 17px 8px', fontSize: 12, fontWeight: 700, color: 'var(--text-tertiary)' }}>
            <span>{t('pf_spa_list_form')}</span><span>{t('pf_spa_list_client')}</span><span>{t('pf_spa_list_type')}</span>
            <span>{t('pf_spa_list_version')}</span><span>{t('pf_spa_list_edited')}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {loading ? <div style={{ ...propCard, padding: 36, textAlign: 'center', color: 'var(--text-tertiary)' }}>{t('pf_loading')}</div>
              : rows.length === 0 ? <div style={{ ...propCard, padding: 36, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t('pf_spa_list_none')}</div>
                : rows.map(r => (
                  <div key={`${r.id}:${r.agreement || ''}`} onClick={() => onOpen(r.id)} style={{ ...propCard, display: 'grid', gridTemplateColumns: AGR_COLS, gap: 14, alignItems: 'center', padding: '12px 16px', cursor: 'pointer' }}
                    onMouseEnter={e => (e.currentTarget.style.background = PROP_CARD_HOVER)} onMouseLeave={e => (e.currentTarget.style.background = PROP_CARD_BG)}>
                    <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{r.report_no}</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ ...ellipsis, fontWeight: 700, color: 'var(--text-primary)' }}>{[r.buyer_name, r.seller_name].filter(Boolean).join(' ← ') || t('pf_untitled')}</div>
                      {r.property_name && <div style={{ ...ellipsis, fontSize: 12, color: 'var(--text-tertiary)' }}>{r.property_name}</div>}
                    </div>
                    {r.kind === 'lease' ? (
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{typeLabel(String(r.variant || ''))}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('pf_lease')}</div>
                      </div>
                    ) : <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{typeLabel(String(r.variant || ''))}</span>}
                    <span style={{ fontSize: 13, fontWeight: 700, color: r.version ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>{r.version || t('pf_spa_original')}</span>
                    <div style={{ minWidth: 0, fontSize: 12, color: 'var(--text-tertiary)' }}>
                      <div style={ellipsis}>{r.edited_by || ''}</div>
                      <div>{fmtDateTime(r.edited_at)}</div>
                    </div>
                  </div>
                ))}
          </div>
        </div>
      </div>
      <Pagination page={page} total={total} limit={limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />
    </div>
  );
}

export default function PropertyForm({ embedded, openAgreement }: { embedded?: boolean; openAgreement?: string }) {
  useTranslation();
  // SLF Property → Agreements hands over the form whose agreement was clicked: it opens at the agreement.
  const [openId, setOpenId] = useState(openAgreement || '');
  const [toSpa, setToSpa] = useState(!!openAgreement);
  // A deep link opens one form straight away: /m/slf_property?v=property_form&open=<id>. Only on this
  // tab — the other tabs' `open` ids are theirs.
  useEffect(() => {
    if (openAgreement) return;
    try {
      const p = new URLSearchParams(window.location.search);
      const v = p.get('open');
      if (v && p.get('v') === 'property_form') setOpenId(v);
    } catch { /* no query */ }
  }, []); // eslint-disable-line
  const create = async () => { try { const r: any = await api.pfCreate(); setOpenId(r.id); } catch { /* */ } };
  return (
    <div className="property-form" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: 'var(--bg-app)', height: embedded ? undefined : '100vh', fontFamily: RC }}>
      {openId
        ? <PFForm id={openId} toSpa={toSpa} onBack={() => { setToSpa(false); setOpenId(''); }} />
        : <PFList onOpen={setOpenId} onNew={create} />}
    </div>
  );
}

// ── The client's links (/property-form/view/<token>, /property-form/edit/<token>) — no login ─────
// The server decides from the token which link it is; the word in the address is only for people.
export function PropertyFormPublicPage() {
  const { t } = useTranslation();
  const { token = '' } = useParams();
  const [state, setState] = useState<'load' | 'ok' | 'err'>('load');
  const [info, setInfo] = useState<any>(null);
  const [files, setFiles] = useState<any[]>([]);
  const st = useAutosave((d, baseRev) => api.pfPublicUpdate(token, { data: d, baseRev }), info?.status === 'new');
  useEffect(() => {
    api.pfPublicGet(token).then((r: any) => {
      // The country's land units the Land block asks for (report f7b09e0d) — no login, so no /api/me; the DD
      // share page's way. Set before the form is read in, which gives it a box for each unit.
      setCountryFeaturesOverride(r?.countryFeatures);
      setInfo(r); setFiles(r.files || []);
      st.init(r.data, Number(r.rev || 0), r.defaultDay);
      setState('ok');
    }).catch(() => setState('err'));
  }, [token]); // eslint-disable-line
  useEffect(() => { document.title = t('pf_public_title'); }, [t]);
  const ro = info?.mode !== 'edit';
  const ctl: Ctl | null = st.data ? {
    data: st.data, set: st.set, files,
    upload: async (file, path, kind) => { const r: any = await api.pfPublicUpload(token, file, path, kind); if (r?.file) setFiles(l => l.concat([r.file])); return r; },
    remove: async fileId => { await api.pfPublicFileDelete(token, fileId); setFiles(l => l.filter(f => f.id !== fileId)); },
    open: f => { window.open(api.pfPublicFileUrl(token, f.id), '_blank'); },
  } : null;
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-app)', fontFamily: RC, padding: 'clamp(8px, 3vw, 24px)', boxSizing: 'border-box' }}>
      <div style={{ width: '100%', maxWidth: 892, margin: '0 auto', display: 'grid', gap: 14 }}>
        <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <img className="app-logo" src="/logo.png" alt={brandOr('Logo')} style={{ width: 28, height: 28, objectFit: 'contain' }} />
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{t('pf_public_title')}</div>
          {info?.report_no && <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{info.report_no}</span>}
          <div style={{ flex: 1 }} />
          {state === 'ok' && (ro
            ? <span style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>{t('pf_view_only')}</span>
            : <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{st.save === 'saving' ? t('pf_saving') : st.save === 'saved' ? t('pf_saved') : ''}</span>)}
        </div>
        {state === 'load' && <div style={{ textAlign: 'center', color: 'var(--text-tertiary)', padding: '60px 0' }}><i className="ti ti-loader" style={{ fontSize: 26 }} /></div>}
        {state === 'err' && <div style={{ textAlign: 'center', color: 'var(--text-tertiary)', padding: '60px 0' }}><i className="ti ti-link-off" style={{ fontSize: 28, display: 'block', marginBottom: 10 }} />{t('pf_link_invalid')}</div>}
        {st.err && <div style={errBox}>{st.err}</div>}
        {state === 'ok' && ctl && <RO.Provider value={ro}><FormBody ctl={ctl} cur={String(info?.currency || '')} /></RO.Provider>}
      </div>
    </div>
  );
}
